// src/pages/SensorDashboard.js
import React, {useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Chart from "chart.js/auto";
import zoomPlugin from "chartjs-plugin-zoom";
import "hammerjs";
import "chartjs-adapter-date-fns";
import { es } from "date-fns/locale";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import "./SensorDashboard.css";
import AuthContext from "../context/AuthContext";
import ObservationsModal from "./ObservationsModal";

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const API_BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/sensores`;
const API_ROOT = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const TZ = "America/Guayaquil";
const ALL_DEVICES_VALUE = "__ALL__";
const OVERVIEW_RANGE_MS = 24 * 3600 * 1000;

const TYPE_DISPLAY_NAMES = {
  General: "Todas las aplicaciones",
  CalidadAgua: "Calidad de agua",
  HumedadTemperatura: "Humedad y temperatura",
  HumedadTemperaturaGPS: "Cadena de frío GPS",
  CondicionesAmbientales: "Condiciones Ambientales",
  Temperatura: "Temperatura",
  Nivel: "Nivel",
  Flujo: "Flujo",
  Ruido: "Ruido",
  MapeoTermico: "Mapeo térmico",
  GPS: "GPS",
  Presion: "Presión",
  EnergiaElectrica: "Energía eléctrica",
  Meteorologica: "Estación meteorológica",
};

function getTypeDisplayName(type) {
  const resolved = resolveType(type);
  return TYPE_DISPLAY_NAMES[resolved] || String(type || "Sensor");
}

/* ===== Config por tipo ===== */
const TYPE_CONFIG = {
  CalidadAgua: {
    variables: ["PH", "turbidity", "EC_K1", "CLORO_LIBRE", "O2", "TEMP"],
    kpi: ["PH", "turbidity", "EC_K1", "CLORO_LIBRE", "O2", "TEMP"],
    units: { PH: "pH", turbidity: "NTU", EC_K1: "µS/cm", CLORO_LIBRE: "mg/L" },
  },

HumedadTemperatura: {
  variables: [
    "temperature",
    "humidity",
    "TEMP",
    "HUM",
    "TEMPERATURA",
    "HUMEDAD",
    "TEMPERATURA_EXTERNA",
    "HUMEDAD_EXTERNA",
  ],

  kpi: [
    "temperature",
    "humidity",
    "TEMP",
    "HUM",
    "TEMPERATURA",
    "HUMEDAD",
    "TEMPERATURA_EXTERNA",
    "HUMEDAD_EXTERNA",
  ],

  units: {
    temperature: "°C",
    humidity: "%HR",
    TEMP: "°C",
    HUM: "%HR",
    TEMPERATURA: "°C",
    HUMEDAD: "%HR",
    TEMPERATURA_EXTERNA: "°C",
    HUMEDAD_EXTERNA: "%HR",
    BAT: "%",
  },
},

HumedadTemperaturaGPS: {
  variables: [
    "TEMP",
    "HUM",
  ],

  kpi: [
    "TEMP",
    "HUM",
  ],

  units: {
    TEMP: "°C",
    HUM: "%HR",
  },
},
  
Nivel: {
    variables: ["NIVELHIDROSTATICO", "LEVEL_CM", "NIVEL", "level"],
    kpi: ["NIVELHIDROSTATICO", "LEVEL_CM", "NIVEL"],
    units: { NIVELHIDROSTATICO: "m", LEVEL_CM: "cm", NIVEL: "m", level: "m" },
  },
  Temperatura: {
    variables: ["TEMP", "temperature"],
    kpi: ["TEMP"],
    units: { TEMP: "°C", temperature: "°C" },
  },
  Flujo: { variables: ["FLUJO", "flow"], kpi: ["FLUJO"], units: { FLUJO: "L/min", flow: "L/min" } },
  Ruido: { variables: ["LAeq"], kpi: ["LAeq"], units: { LAeq: "dB(A)" } },
  GPS: {
    variables: ["LAT", "LON", "LATITUDE", "LONGITUDE", "SPEED", "ALT", "ALTITUDE"],
    kpi: ["SPEED"],
    units: { LAT: "°", LON: "°", LATITUDE: "°", LONGITUDE: "°", SPEED: "km/h", ALT: "m", ALTITUDE: "m" },
  },
  Presion: {
    variables: ["PRESSURE", "TEMP", "BAT"],
    kpi: ["PRESSURE", "TEMP"],
    units: { PRESSURE: "", TEMP: "°C", BAT: "%" },
  },

CondicionesAmbientales: {
  variables: ["TEMP", "HUM", "PRESSURE_DIFF"],
  kpi: ["TEMP", "HUM", "PRESSURE_DIFF"],
  units: {
    TEMP: "°C",
    HUM: "%HR",
    PRESSURE_DIFF: ""
  },
 },
  Meteorologica: {
    variables: ["TEMP", "HUM", "PRESSURE", "WIND_SPEED", "WIND_DIRECTION", "RAINFALL", "BAT"],
    kpi: ["TEMP", "HUM", "PRESSURE", "WIND_SPEED", "WIND_DIRECTION", "RAINFALL", "BAT"],
    units: {
      TEMP: "°C",
      HUM: "%HR",
      PRESSURE: "hPa",
      WIND_SPEED: "m/s",
      WIND_DIRECTION: "°",
      RAINFALL: "mm",
      BAT: "%",
    },
  },
EnergiaElectrica: {
  variables: [
    "TEMP",
    "VOLTAGE_L1",
    "VOLTAGE_L2",
    "VOLTAGE_L3",
    "THDV_L1",
    "THDV_L2",
    "THDV_L3",
    ...Array.from({ length: 12 }, (_, i) => i + 1).flatMap((ch) => [
      `CURRENT_CH${ch}`,
      `POWER_FACTOR_CH${ch}`,
      `ACTIVE_POWER_CH${ch}`,
      `REACTIVE_POWER_CH${ch}`,
      `APPARENT_POWER_CH${ch}`,
      `ACTIVE_ENERGY_CH${ch}`,
      `REACTIVE_ENERGY_CH${ch}`,
      `APPARENT_ENERGY_CH${ch}`,
      `THDI_CH${ch}`,
    ]),
  ],

  kpi: [
    "TEMP",
    "VOLTAGE_L1",
    "VOLTAGE_L2",
    "VOLTAGE_L3",
    ...Array.from({ length: 12 }, (_, i) => i + 1).flatMap((ch) => [
      `CURRENT_CH${ch}`,
      `POWER_FACTOR_CH${ch}`,
      `ACTIVE_POWER_CH${ch}`,
      `REACTIVE_POWER_CH${ch}`,
      `APPARENT_POWER_CH${ch}`,
      `ACTIVE_ENERGY_CH${ch}`,
      `REACTIVE_ENERGY_CH${ch}`,
      `APPARENT_ENERGY_CH${ch}`,
      `THDI_CH${ch}`,
    ]),
    "THDV_L1",
    "THDV_L2",
    "THDV_L3",
  ],

  units: {
    TEMP: "°C",
    VOLTAGE_L1: "V",
    VOLTAGE_L2: "V",
    VOLTAGE_L3: "V",
    THDV_L1: "%",
    THDV_L2: "%",
    THDV_L3: "%",
    ...Object.fromEntries(
      Array.from({ length: 12 }, (_, i) => i + 1).flatMap((ch) => [
        [`CURRENT_CH${ch}`, "A"],
        [`POWER_FACTOR_CH${ch}`, ""],
        [`ACTIVE_POWER_CH${ch}`, "kW"],
        [`REACTIVE_POWER_CH${ch}`, "kVAr"],
        [`APPARENT_POWER_CH${ch}`, "kVA"],
        [`ACTIVE_ENERGY_CH${ch}`, "kWh"],
        [`REACTIVE_ENERGY_CH${ch}`, "kVArh"],
        [`APPARENT_ENERGY_CH${ch}`, "kVAh"],
        [`THDI_CH${ch}`, "%"],
      ])
    ),
  },
},
  MapeoTermico: { variables: ["TEMP", "TEMPERATURE"], kpi: ["TEMP"], units: { TEMP: "°C", TEMPERATURE: "°C" } },
};

/* ===== Alias de tipos ===== */
const TYPE_ALIASES = {
  "temperatura y humedad": "HumedadTemperatura",
  temphum: "HumedadTemperatura",
  agua: "CalidadAgua",
  waterquality: "CalidadAgua",
  temp: "Temperatura",
  nivel: "Nivel",
  flow: "Flujo",
  caudal: "Flujo",
  ruido: "Ruido",
  sonometro: "Ruido",
  noise: "Ruido",
  sound: "Ruido",
  gps: "GPS",
  tracker: "GPS",
  posicion: "GPS",
  ubicación: "GPS",
  "ubicacion": "GPS",
  "mapeo termico": "MapeoTermico",
  "mapeo térmico": "MapeoTermico",
  termico: "MapeoTermico",
  térmico: "MapeoTermico",
  meteorologica: "Meteorologica",
  "meteorológica": "Meteorologica",
  meteorologico: "Meteorologica",
  "meteorológico": "Meteorologica",
  "estacion meteorologica": "Meteorologica",
  "estación meteorológica": "Meteorologica",
  weather: "Meteorologica",
  "cadena de frio gps": "HumedadTemperaturaGPS",
  "cadena de frío gps": "HumedadTemperaturaGPS",
  "energia electrica": "EnergiaElectrica",
  "energía eléctrica": "EnergiaElectrica",
  energiaelectrica: "EnergiaElectrica",
  humedadtemperaturagps: "HumedadTemperaturaGPS",
  battery: "BAT",
  bat: "BAT",
  battery_level: "BAT",
};
function resolveType(raw) {
  if (!raw) return "CalidadAgua";
  if (TYPE_CONFIG[raw]) return raw;
  const low = String(raw).trim().toLowerCase();
  if (low === "general" || low === "todos" || low === "todoslossensores") return "General";
  if (TYPE_ALIASES[low]) return TYPE_ALIASES[low];
  const key = low
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/\s+/g, "");
  const map2 = {
    calidadagua: "CalidadAgua",
    humedadtemperatura: "HumedadTemperatura",
    humedadtemperaturagps: "HumedadTemperaturaGPS",
    condicionesambientales: "CondicionesAmbientales",
    temperatura: "Temperatura",
    nivel: "Nivel",
    flujo: "Flujo",
    ruido: "Ruido",
    gps: "GPS",
    presion: "Presion",
    meteorologica: "Meteorologica",
    meteorologico: "Meteorologica",
    estacionmeteorologica: "Meteorologica",
    mapeotermico: "MapeoTermico",
    energiaelectrica: "EnergiaElectrica",
    general: "General",
    todos: "General",
    todoslossensores: "General",
  };
  return map2[key] || "CalidadAgua";
}

/* ===== Alias de variables ===== */
const VAR_ALIASES = {
  cl_free: "CLORO_LIBRE",
  cloro_libre: "CLORO_LIBRE",
  "cloro-libre": "CLORO_LIBRE",
  clorolibre: "CLORO_LIBRE",
  "cloro libre": "CLORO_LIBRE",
  ph: "PH",
  ec_k1: "EC_K1",
  "ec-k1": "EC_K1",
  turbidity: "turbidity",
  TURBIDITY: "turbidity",
  temperature: "temperature",
  TEMPERATURE: "temperature",
  temperatura: "TEMPERATURA",
  humedad: "HUMEDAD",
  temperatura_externa: "TEMPERATURA_EXTERNA",
  humedad_externa: "HUMEDAD_EXTERNA", 
  temp: "TEMP",
  hum: "HUM",
  humidity: "HUM",
  HUMIDITY: "HUM",
  nivel: "NIVEL",
  level: "level",
  nivelhidrostatico: "NIVELHIDROSTATICO",
  "nivel-hidrostatico": "NIVELHIDROSTATICO",
  nivel_hidrostatico: "NIVELHIDROSTATICO",
  level_cm: "LEVEL_CM",
  pressure: "PRESSURE",
  wind_speed: "WIND_SPEED",
  wind_direction: "WIND_DIRECTION",
  rainfall: "RAINFALL",
  laeq: "LAeq",
};
function canonVar(v) {
  if (!v) return v;
  const key = String(v)
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[\s\-]+/g, "_");
  return VAR_ALIASES[key] || v;
}
// ===== Ajustes visuales específicos por sensor =====
// Estos offsets solo afectan la presentación del dashboard.
// Los datos originales almacenados en PostgreSQL no se modifican.
const SENSOR_VALUE_OFFSETS = {
  SMTR2: {
    TEMPERATURA_COLOR: -3,
    TEMPERATURA_DQO: -3,
  },
};

function getSensorValueOffset(deviceId, variable) {
  const deviceKey = String(deviceId || "").trim().toUpperCase();

  // En PostgreSQL estas variables pueden llegar como "Temperatura COLOR"
  // y "Temperatura DQO". Se normalizan solo para buscar el offset,
  // sin cambiar el nombre usado por las consultas del sensor.
  const variableKey = String(canonVar(variable) || variable || "")
    .trim()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toUpperCase();

  return Number(SENSOR_VALUE_OFFSETS?.[deviceKey]?.[variableKey] || 0);
}

function applySensorValueOffset(deviceId, variable, value) {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) return numericValue;

  return numericValue + getSensorValueOffset(deviceId, variable);
}

// ===== Validación de lecturas solo para presentación =====
// PostgreSQL conserva siempre el dato original. Estos límites únicamente
// determinan si una lectura puede mostrarse y participar en estadísticas/gráficas.
const SENSOR_DISPLAY_MAX_BY_VARIABLE = {
  EC_K10: 20000,
};

function normalizeDisplayVariableKey(variable) {
  return String(canonVar(variable) || variable || "")
    .trim()
    .toUpperCase();
}

function isSensorDisplayValueValid(variable, value) {
  if (value === null || value === undefined || value === "") return false;

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return false;

  const maxAllowed =
    SENSOR_DISPLAY_MAX_BY_VARIABLE[normalizeDisplayVariableKey(variable)];

  return !Number.isFinite(maxAllowed) || numericValue <= maxAllowed;
}

/* ===== Overrides por dispositivo (no afecta a otros) =====
   Ej: Oxigeno2 => solo mostrar ADC1 y ADC2 (KPIs + gráficas) */
const DEVICE_VAR_OVERRIDES = {
  // Oxigeno2 (CalidadAgua)
  "24e124445e420472": { vars: ["ADC1", "ADC2"], kpis: ["ADC1", "ADC2"] },
};

const normId = (v) => String(v ?? "").trim();

function applyDeviceVarOverride(deviceId, sortedVars) {
  const cfg = DEVICE_VAR_OVERRIDES[normId(deviceId)];

  if (!cfg) {
    return {
      vars: sortedVars,
      kpis: sortedVars,
    };
  }

  const wantedVars = (cfg.vars || []).map(canonVar);

  const filteredVars = wantedVars.filter((v) =>
    sortedVars.includes(v)
  );

  const finalVars = filteredVars.length
    ? filteredVars
    : sortedVars;

  const wantedKpis = (cfg.kpis || cfg.vars || []).map(canonVar);

  const filteredKpis = wantedKpis.filter((v) =>
    finalVars.includes(v)
  );

  const finalKpis = filteredKpis.length
    ? filteredKpis
    : finalVars;

  return {
    vars: finalVars,
    kpis: finalKpis,
  };
}

/* ===== Unidades / decimales ===== */
const UNITS_MAP = {
  PH: "pH",
  turbidity: "NTU",
  EC_K1: "µS/cm",
  temperature: "°C",
  TEMPERATURE: "°C",
  TEMP: "°C",
  HUM: "%HR",
  TEMPERATURA_COLOR: "°C",
  TEMPERATURA_DQO: "°C",
  "TEMPERATURA COLOR": "°C",
  "TEMPERATURA DQO": "°C",
  TSS: "mg/L",
  NIVEL: "m",
  NIVELHIDROSTATICO: "m",
  LEVEL_CM: "cm",
  level: "m",
  FLUJO: "L/min",
  flow: "L/min",
  LAeq: "dB(A)",
  CLORO_LIBRE: "mg/L",
  O2: "mg/L",
  ADC1: "PPM",
  ADC2: "PPM",
  CL2 : "PH",
  TEMPERATURE: "°C",
  DQO:"mg/l",
  TOCEQUIVALENTE : "mg/l",
  POWER_FACTOR_CH1: "",
  ACTIVE_POWER_CH1: "kW",
  REACTIVE_POWER_CH1: "kVAr",
  APPARENT_POWER_CH1: "kVA",
  ACTIVE_ENERGY_CH1: "kWh",
  REACTIVE_ENERGY_CH1: "kVArh",
  APPARENT_ENERGY_CH1: "kVAh",
  REVERSE_ACTIVE_ENERGY_CH1: "kWh",
  REVERSE_ACTIVE_ENERGY_CH2: "kWh",
  REVERSE_REACTIVE_ENERGY_CH1: "kVArh",
  REVERSE_REACTIVE_ENERGY_CH2: "kVArh",  
  VOLTAGE_L1: "V",
  VOLTAGE_L2: "V",
  VOLTAGE_L3: "V",
  CURRENT_CH1: "A",
  CURRENT_CH2: "A",
  POWER_FACTOR_CH2: "",
  ACTIVE_POWER_CH2: "kW",
  REACTIVE_POWER_CH2: "kVAr",
  APPARENT_POWER_CH2: "kVA",
  ACTIVE_ENERGY_CH2: "kWh",
  REACTIVE_ENERGY_CH2: "kVArh",
  APPARENT_ENERGY_CH2: "kVAh",
  THDI_CH1: "%",
  THDI_CH2: "%",
  THDV_L1: "%",
  THDV_L2: "%",
  THDV_L3: "%",
  TEMPERATURA: "°C",
  HUMEDAD: "%HR",
  TEMPERATURA_EXTERNA: "°C",
  HUMEDAD_EXTERNA: "%HR",
  EXT_HUM: "%HR",
  EXT_TEMP: "°C",
  BAT: "%",
  PH_1: "pH",
  PH_2: "pH",
  TEMP_1: "°C",
  TEMP_2: "°C",
  COLOR_REAL: "UCV de Pt/Co",
  TURBIDEZ: "NTU",
  EXT_HUM: "%HR",
  EXT_TEMP: "°C",
  BAT: "%",
  EC_K10:"µS/cm",
  EC10_TEMP:"°C",
  TOC: "ppb",
  CONDUCTIVITY_COMP: "µS/cm",
  CONDUCTIVITY_UNCOMP: "µS/cm",
  CONDUCTIVIDAD_COMPENSADA:"µS/cm C",
  BATV: "V",
  BAT_V: "V",
  BatV: "V",
  DISSOLVED_OXYGEN: "mg/L",
  dissolved_oxygen: "mg/L",
  EC1_TEMP: "°C",
  EC1_Temp: "°C",
  LATITUDE: "°",
  LONGITUDE: "°",
  WIND_SPEED: "m/s",
  WIND_DIRECTION: "°",
  RAINFALL: "mm",

};
const DECIMALS_MAP = { PH: 2, TSS: 2, CLORO_LIBRE: 2, PH_1: 2,PH_2: 2, BAT: 0,  TEMPERATURA: 2,
  HUMEDAD: 2,
  TEMPERATURA_EXTERNA: 2,
  HUMEDAD_EXTERNA: 2,  TEMPERATURA_COLOR: 1,
  TEMPERATURA_DQO: 1,
  PRESSURE_DIFF: 2,
  "TEMPERATURA COLOR": 1, 
 "TEMPERATURA DQO": 1, 
// PAT700
  TOC: 1,
  TEMP: 1,
  CONDUCTIVITY_COMP: 2,
  CONDUCTIVITY_UNCOMP: 2,
  CONDUCTIVIDAD_COMPENSADA:2,
  WIND_SPEED: 1,
  WIND_DIRECTION: 1,
  RAINFALL: 2,
  PRESSURE: 1,
  POWER_FACTOR_CH1: 2,
  ACTIVE_POWER_CH1: 3,
  REACTIVE_POWER_CH1: 3,
  APPARENT_POWER_CH1: 3,
  ACTIVE_ENERGY_CH1: 3,
  REACTIVE_ENERGY_CH1: 3,
  APPARENT_ENERGY_CH1: 3,
  REVERSE_ACTIVE_ENERGY_CH1: 3,
  REVERSE_ACTIVE_ENERGY_CH2: 3,
  REVERSE_REACTIVE_ENERGY_CH1: 3,
  REVERSE_REACTIVE_ENERGY_CH2: 3,
  VOLTAGE_L1: 2,
  VOLTAGE_L2: 2,
  VOLTAGE_L3: 2,
  CURRENT_CH1: 2,
  CURRENT_CH2: 2,
  POWER_FACTOR_CH2: 2,
  ACTIVE_POWER_CH2: 3,
  REACTIVE_POWER_CH2: 3,
  APPARENT_POWER_CH2: 3,
  ACTIVE_ENERGY_CH2: 3,
  REACTIVE_ENERGY_CH2: 3,
  APPARENT_ENERGY_CH2: 3,
  THDI_CH1: 2,
  THDI_CH2: 2,
  THDV_L1: 2,
  THDV_L2: 2,
  THDV_L3: 2,
};
const decimalsFor = (v) => {
  const key = normalizeVariableDisplayKey(v);

  if (/^CURRENT_CH(?:[1-9]|1[0-2])$/.test(key)) return 2;
  if (/^POWER_FACTOR_CH(?:[1-9]|1[0-2])$/.test(key)) return 2;

  if (/^(?:ACTIVE|REACTIVE|APPARENT)_POWER_CH(?:[1-9]|1[0-2])$/.test(key)) {
    return 3;
  }

  if (/^(?:ACTIVE|REACTIVE|APPARENT)_ENERGY_CH(?:[1-9]|1[0-2])$/.test(key)) {
    return 3;
  }

  if (/^REVERSE_(?:ACTIVE|REACTIVE)_ENERGY_CH(?:[1-9]|1[0-2])$/.test(key)) {
    return 3;
  }

  if (/^THDI_CH(?:[1-9]|1[0-2])$/.test(key)) return 2;

  return DECIMALS_MAP[key] ?? 1;
};

// El PAT700 cambia la resolución visual del TOC según el valor:
// - menor de 100 ppb: 1 decimal
// - desde 100 ppb: sin decimales
function decimalsForValue(variable, value) {
  const variableKey = String(canonVar(variable) || variable || "")
    .trim()
    .toUpperCase();

  const numericValue = Number(value);

  if (
    variableKey === "TOC" &&
    Number.isFinite(numericValue) &&
    numericValue >= 1000
  ) {
    return 0;
  }

  return decimalsFor(variable);
}

function formatSensorNumber(value, variable) {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) return "—";

  return numericValue.toFixed(
    decimalsForValue(variable, numericValue)
  );
}

const VARIABLE_DISPLAY_NAMES = {
  BAT: "Batería",
  BATV: "Voltaje de batería",
  BAT_V: "Voltaje de batería",
  CLORO_LIBRE: "Cloro libre",
  CL2: "Cloro",
  COLOR_REAL: "Color real",
  COLOR_APARENTE: "Color aparente",
  CONDUCTIVIDAD_COMPENSADA: "Conductividad compensada",
  CONDUCTIVITY_COMP: "Conductividad compensada",
  CONDUCTIVITY_UNCOMP: "Conductividad sin compensación",
  DISSOLVED_OXYGEN: "Oxígeno disuelto",
  O2: "Oxígeno disuelto",
  DO: "Oxígeno disuelto",
  DQO: "Demanda química de oxígeno (DQO)",
  PRESSURE_DIFF: "Presión diferencial",
  TOCEQUIVALENTE: "Carbono orgánico total equivalente",
  EC1_TEMP: "Temperatura del sensor de conductividad K1",
  EC10_TEMP: "Temperatura del sensor de conductividad K10",
  EC_K1: "Conductividad eléctrica K1",
  EC_K10: "Conductividad eléctrica K10",
  HUM: "Humedad relativa",
  HUMEDAD: "Humedad relativa",
  HUMIDITY: "Humedad relativa",
  EXT_HUM: "Humedad relativa externa",
  HUMEDAD_EXTERNA: "Humedad relativa externa",
  LAEQ: "Nivel sonoro continuo equivalente (LAeq)",
  SPL: "Nivel de presión sonora (SPL)",
  LAT: "Latitud",
  LATITUDE: "Latitud",
  LON: "Longitud",
  LONGITUDE: "Longitud",
  LEVEL: "Nivel",
  LEVEL_CM: "Nivel",
  NIVEL: "Nivel",
  NIVELHIDROSTATICO: "Nivel hidrostático",
  PH: "Potencial de hidrógeno (pH)",
  PH_1: "Potencial de hidrógeno (pH)",
  PH_2: "Potencial de hidrógeno (pH)",
  TEMP: "Temperatura",
  TEMP_1: "Temperatura",
  TEMP_2: "Temperatura",
  TEMPERATURE: "Temperatura",
  TEMPERATURA: "Temperatura",
  EXT_TEMP: "Temperatura externa",
  TEMPERATURA_EXTERNA: "Temperatura externa",
  TEMPERATURA_COLOR: "Temperatura de medición de color",
  TEMPERATURA_DQO: "Temperatura de medición de DQO",
  TOC: "Carbono orgánico total (TOC)",
  TSS: "Sólidos suspendidos totales (TSS)",
  TURBIDITY: "Turbidez",
  TURBIDEZ: "Turbidez",
  PRESSURE: "Presión",
  WIND_SPEED: "Velocidad del viento",
  WIND_DIRECTION: "Dirección del viento",
  RAINFALL: "Precipitación acumulada",
  FLUJO: "Caudal",
  FLOW: "Caudal",
  ADC1: "Canal analógico 1 (ADC1)",
  ADC2: "Canal analógico 2 (ADC2)",
  ORP: "Potencial de óxido-reducción (ORP)",
  SPEED: "Velocidad",
  ALT: "Altitud",
  ALTITUDE: "Altitud",
};

const TYPE_VARIABLE_DISPLAY_NAMES = {
  Meteorologica: {
    PRESSURE: "Presión atmosférica",
    WIND_SPEED: "Velocidad del viento",
    WIND_DIRECTION: "Dirección del viento",
    RAINFALL: "Precipitación acumulada",
  },
EnergiaElectrica: {
    TEMP: "Temperatura",

    VOLTAGE_L1: "Voltaje L1",
    VOLTAGE_L2: "Voltaje L2",
    VOLTAGE_L3: "Voltaje L3",

    CURRENT_CH1: "Corriente CH1",
    CURRENT_CH2: "Corriente CH2",

    POWER_FACTOR_CH2: "Factor de potencia CH2",

    ACTIVE_POWER_CH2: "Potencia activa CH2",
    REACTIVE_POWER_CH2: "Potencia reactiva CH2",
    APPARENT_POWER_CH2: "Potencia aparente CH2",

    ACTIVE_ENERGY_CH2: "Energía activa CH2",
    REACTIVE_ENERGY_CH2: "Energía reactiva CH2",
    APPARENT_ENERGY_CH2: "Energía aparente CH2",

    THDI_CH1: "Distorsión armónica de corriente CH1",
    THDI_CH2: "Distorsión armónica de corriente CH2",

    THDV_L1: "Distorsión armónica de voltaje L1",
    THDV_L2: "Distorsión armónica de voltaje L2",
    THDV_L3: "Distorsión armónica de voltaje L3"
  }
};

const VARIABLE_DISPLAY_ACRONYMS = new Set([
  "ADC",
  "BAT",
  "DO",
  "DQO",
  "EC",
  "GPS",
  "LAEQ",
  "O2",
  "ORP",
  "PH",
  "SPL",
  "TOC",
  "TSS",
]);

function normalizeVariableDisplayKey(variable) {
  const canonical = canonVar(variable) || variable;

  return String(canonical || "")
    .trim()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toUpperCase();
}

function humanizeVariableFallback(variable) {
  const raw = String(variable || "").trim();
  if (!raw) return "Variable";

  const words = raw
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ");

  return words
    .map((word, index) => {
      const upper = word.toUpperCase();

      if (VARIABLE_DISPLAY_ACRONYMS.has(upper) || /^\d+$/.test(word)) {
        return upper === "PH" ? "pH" : upper === "LAEQ" ? "LAeq" : upper;
      }

      const lower = word.toLocaleLowerCase("es-EC");

      return index === 0
        ? lower.charAt(0).toLocaleUpperCase("es-EC") + lower.slice(1)
        : lower;
    })
    .join(" ");
}

function getVariableDisplayName(variable, type = null) {
  const key = normalizeVariableDisplayKey(variable);
  const resolvedType = type ? resolveType(type) : null;

  if (resolvedType === "EnergiaElectrica") {
    const channelPatterns = [
      [/^CURRENT_CH(\d+)$/, "Corriente"],
      [/^POWER_FACTOR_CH(\d+)$/, "Factor de potencia"],
      [/^ACTIVE_POWER_CH(\d+)$/, "Potencia activa"],
      [/^REACTIVE_POWER_CH(\d+)$/, "Potencia reactiva"],
      [/^APPARENT_POWER_CH(\d+)$/, "Potencia aparente"],
      [/^ACTIVE_ENERGY_CH(\d+)$/, "Energía activa"],
      [/^REACTIVE_ENERGY_CH(\d+)$/, "Energía reactiva"],
      [/^APPARENT_ENERGY_CH(\d+)$/, "Energía aparente"],
      [/^REVERSE_ACTIVE_ENERGY_CH(\d+)$/, "Energía activa reversa"],
      [/^REVERSE_REACTIVE_ENERGY_CH(\d+)$/, "Energía reactiva reversa"],
      [/^THDI_CH(\d+)$/, "Distorsión armónica de corriente"],
    ];

    for (const [regex, label] of channelPatterns) {
      const match = key.match(regex);
      if (match) return `${label} CH${match[1]}`;
    }
  }

  const typeName = resolvedType
    ? TYPE_VARIABLE_DISPLAY_NAMES?.[resolvedType]?.[key]
    : null;

  return typeName || VARIABLE_DISPLAY_NAMES[key] || humanizeVariableFallback(variable);
}

function getVariableUnit(variable, type = null) {
  const canonical = canonVar(variable);
  const normalizedKey = normalizeVariableDisplayKey(variable);
  const resolvedType = type ? resolveType(type) : null;
  const typeUnits = resolvedType ? TYPE_CONFIG?.[resolvedType]?.units || {} : {};

  const electricalUnit = (() => {
    const key = normalizedKey;

    if (/^CURRENT_CH(?:[1-9]|1[0-2])$/.test(key)) return "A";
    if (/^POWER_FACTOR_CH(?:[1-9]|1[0-2])$/.test(key)) return "";
    if (/^ACTIVE_POWER_CH(?:[1-9]|1[0-2])$/.test(key)) return "kW";
    if (/^REACTIVE_POWER_CH(?:[1-9]|1[0-2])$/.test(key)) return "kVAr";
    if (/^APPARENT_POWER_CH(?:[1-9]|1[0-2])$/.test(key)) return "kVA";
    if (/^ACTIVE_ENERGY_CH(?:[1-9]|1[0-2])$/.test(key)) return "kWh";
    if (/^REVERSE_ACTIVE_ENERGY_CH(?:[1-9]|1[0-2])$/.test(key)) return "kWh";
    if (/^REACTIVE_ENERGY_CH(?:[1-9]|1[0-2])$/.test(key)) return "kVArh";
    if (/^REVERSE_REACTIVE_ENERGY_CH(?:[1-9]|1[0-2])$/.test(key)) return "kVArh";
    if (/^APPARENT_ENERGY_CH(?:[1-9]|1[0-2])$/.test(key)) return "kVAh";
    if (/^THDI_CH(?:[1-9]|1[0-2])$/.test(key)) return "%";

    return "";
  })();

  return (
    typeUnits[canonical] ||
    typeUnits[normalizedKey] ||
    UNITS_MAP[canonical] ||
    UNITS_MAP[normalizedKey] ||
    UNITS_MAP[variable] ||
    electricalUnit ||
    ""
  );
}

function getVariableDisplayLabel(
  variable,
  includeUnit = false,
  unitOverride = "",
  type = null
) {
  const name = getVariableDisplayName(variable, type);

  if (!includeUnit) return name;

  const unit = String(unitOverride || getVariableUnit(variable, type) || "").trim();
  if (!unit) return name;

  const unitMarker = `(${unit})`.toLocaleLowerCase("es-EC");

  if (name.toLocaleLowerCase("es-EC").includes(unitMarker)) {
    return name;
  }

  return `${name} · ${unit}`;
}

Chart.register(zoomPlugin);

/* ===== Chart defaults ===== */
Chart.defaults.font.family =
  'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Inter, "Helvetica Neue", Arial';
Chart.defaults.font.size = 12;
Chart.defaults.color = "#64748b";
Chart.defaults.borderColor = "#e5e7eb";

/* ===== helpers fecha ===== */
function parseToMs(v) {
  if (v == null) return NaN;
  if (typeof v === "number") return v < 1e11 ? v * 1000 : v;
  const s = String(v).trim();
  if (/^\d+$/.test(s)) {
    const n = Number(s);
    return n < 1e11 ? n * 1000 : n;
  }
  if (/[zZ]|[+\-]\d{2}:?\d{2}$/.test(s)) {
    const t = Date.parse(s.replace(" ", "T"));
    return Number.isNaN(t) ? NaN : t;
  }
  {
    const m = s
      .replace("T", " ")
      .match(/^(\d{4})-(\d{2})-(\d{2})[ ](\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?$/);
    if (m) {
      const [, y, mo, d, hh, mm, ss] = m.map(Number);
      const ECT_ADD_H = 5;
      return Date.UTC(y, mo - 1, d, hh + ECT_ADD_H, mm, ss || 0);
    }
  }
  {
    const m = s.match(
      /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
    );
    if (m) {
      let [, d, mo, yRaw, hh = "0", mm = "0", ss = "0"] = m;
      const y = String(yRaw).length === 2 ? Number("20" + yRaw) : Number(yRaw);
      const ECT_ADD_H = 5;
      return Date.UTC(
        y,
        Number(mo) - 1,
        Number(d),
        Number(hh) + ECT_ADD_H,
        Number(mm),
        Number(ss)
      );
    }
  }
  const t = Date.parse(s);
  return Number.isNaN(t) ? NaN : t;
}
function parseToMsUTC(v) {
  if (v == null) return NaN;
  if (typeof v === "number") return v < 1e11 ? v * 1000 : v;
  const s = String(v).trim();
  if (/^\d+$/.test(s)) {
    const n = Number(s);
    return n < 1e11 ? n * 1000 : n;
  }
  if (/[zZ]|[+\-]\d{2}:?\d{2}$/.test(s)) {
    const t = Date.parse(s.replace(" ", "T"));
    return Number.isNaN(t) ? NaN : t;
  }
  {
    const m = s
      .replace("T", " ")
      .match(/^(\d{4})-(\d{2})-(\d{2})[ ](\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?$/);
    if (m) {
      const [, y, mo, d, hh, mm, ss] = m.map(Number);
      return Date.UTC(y, mo - 1, d, hh, mm, ss || 0);
    }
  }
  {
    const m = s.match(
      /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
    );
    if (m) {
      let [, d, mo, yRaw, hh = "0", mm = "0", ss = "0"] = m;
      const y = String(yRaw).length === 2 ? Number("20" + yRaw) : Number(yRaw);
      return Date.UTC(
        y,
        Number(mo) - 1,
        Number(d),
        Number(hh),
        Number(mm),
        Number(ss)
      );
    }
  }
  const t = Date.parse(s);
  return Number.isNaN(t) ? NaN : t;
}
// Reglas de conexión por usuario.
// Salesiana transmite aproximadamente cada 60 minutos.
// Amagua transmite aproximadamente cada 30 minutos.
const DEFAULT_STATUS_LIMITS = { staleMin: 15, offlineMin: 60 };
const SALESIANA_STATUS_LIMITS = { staleMin: 75, offlineMin: 150 };
const AMAGUA_STATUS_LIMITS = { staleMin: 40, offlineMin: 90 };
const EXT_OPTIONAL_VARS = new Set(["EXT_TEMP", "EXT_HUM",
  "TEMPERATURA_EXTERNA",
  "HUMEDAD_EXTERNA"]);
const EXT_OPTIONAL_WINDOW_MS = 20 * 60 * 1000; // 20 min

function shouldShowExtVarOnly(varName, row) {
  const v = String(canonVar(varName || "")).toUpperCase();

  // Todas las demás variables SIEMPRE se muestran
  if (!EXT_OPTIONAL_VARS.has(v)) return true;

  // Solo EXT_TEMP y EXT_HUM se ocultan si están viejas
  const ts = row?.ts ?? row?.fecha ?? row?.fecha_ms ?? row?.time ?? null;
  const ms = toMsSafe(ts);
  if (!Number.isFinite(ms)) return false;

  return Date.now() - ms <= EXT_OPTIONAL_WINDOW_MS;
}

// ==========================================================
// ENERGÍA ELÉCTRICA - VISIBILIDAD DINÁMICA DE CANALES
//
// CH1 y CH2 siempre se muestran.
//
// CH3 a CH12:
// - Se mantienen en PostgreSQL.
// - Se siguen consultando.
// - Solo aparecen en el dashboard cuando el canal
//   realmente empieza a medir corriente.
//
// L3:
// - Solo aparece cuando exista voltaje real.
// ==========================================================

function getElectricalKpiNumber(kpis, variable) {
  const key = canonVar(variable);
  const row = kpis?.[key];

  const raw =
    row?.value ??
    row?.raw_value ??
    row?.alert_value;

  const n = Number(raw);

  return Number.isFinite(n)
    ? n
    : null;
}


function shouldShowElectricalVariable(
  variable,
  kpis,
  activeType,
  visibleChannels = null
) {
  // Solo aplicar esta lógica a Energía eléctrica.
  if (activeType !== "EnergiaElectrica") {
    return true;
  }

  const key = normalizeVariableDisplayKey(variable);

  // Las energías reversas se conservan en PostgreSQL, pero no se muestran
  // en el dashboard ni en las gráficas.
  if (
    /^REVERSE_ACTIVE_ENERGY_CH\d+$/.test(key) ||
    /^REVERSE_REACTIVE_ENERGY_CH\d+$/.test(key)
  ) {
    return false;
  }

  // Variables asociadas a CH1 ... CH12.
  const channelMatch = key.match(/_CH(\d+)$/);

  if (channelMatch) {
    const channel = Number(channelMatch[1]);

    if (channel === 1 || channel === 2) {
      return true;
    }

    if (channel >= 3 && channel <= 12) {
      if (Array.isArray(visibleChannels)) {
        return visibleChannels.includes(channel);
      }

      // Fallback para usos donde aún no se pase la lista aprendida.
      const current = getElectricalKpiNumber(
        kpis,
        `CURRENT_CH${channel}`
      );

      return current !== null && Math.abs(current) > 0.01;
    }
  }

  // Fase L3: solo aparece cuando exista voltaje real.
  if (key === "VOLTAGE_L3" || key === "THDV_L3") {
    const voltageL3 = getElectricalKpiNumber(kpis, "VOLTAGE_L3");
    return voltageL3 !== null && Math.abs(voltageL3) > 1;
  }

  // El desequilibrio de voltaje solo tiene sentido con L3 activa.
  if (key === "VOLTAGE_UNBALANCE") {
    const voltageL3 = getElectricalKpiNumber(kpis, "VOLTAGE_L3");
    return voltageL3 !== null && Math.abs(voltageL3) > 1;
  }

  return true;
}

function toMsSafe(ts) {
  if (ts == null) return NaN;
  if (typeof ts === "number") return ts < 1e11 ? ts * 1000 : ts;
  const n1 = parseToMsUTC(ts);
  if (Number.isFinite(n1)) return n1;
  const n2 = parseToMs(ts);
  if (Number.isFinite(n2)) return n2;
  const n3 = Date.parse(String(ts).replace(" ", "T"));
  return Number.isNaN(n3) ? NaN : n3;
}

const fmtDT = (v) => {
  const ms = parseToMs(v);
  return Number.isNaN(ms)
    ? "—"
    : new Date(ms).toLocaleString("es-EC", { timeZone: TZ });
};
const idForVar = (v) => `sensorChart-${String(v).replace(/[^\w\-]/g, "_")}`;

/* ===== Estado (dot) ===== */
function computeStatus(ts, now = new Date(), staleMin = 15, offlineMin = 60) {
  const t = parseToMsUTC(ts);
  if (!Number.isFinite(t)) return "offline";
  const diffMin = (now.getTime() - t) / 60000;
  if (diffMin < 0) return "online";
  if (diffMin <= staleMin) return "online";
  if (diffMin <= offlineMin) return "stale";
  return "offline";
}
function StatusDot({ status = "offline", title, showLabel = false }) {
  const colors = {
    online: "#22c55e",
    stale: "#f59e0b",
    offline: "#ef4444",
    lowflow: "#dc2626",
  };
  const label =
    status === "lowflow"
      ? "FLUJO BAJO"
      : status === "online"
      ? "EN LÍNEA"
      : status === "offline"
      ? "FUERA DE LÍNEA"
      : "INTERMITENTE";
  if (!showLabel)
    return (
      <span
        className={`status-dot ${status}`}
        title={title || label}
        aria-label={label}
        style={{ background: colors[status] }}
      />
    );
  return (
    <span
      className="status-wrap"
      title={title || label}
      aria-label={label}
      style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
    >
      <span
        className={`status-dot ${status}`}
        style={{ background: colors[status] }}
      />
      <span className={`status-label ${status}`}>{label}</span>
    </span>
  );
}



/* ===== Iconografía uniforme del menú lateral ===== */
function SidebarIcon({ name }) {
  const commonProps = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.9,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    focusable: "false",
    "aria-hidden": "true",
  };

  switch (name) {
    case "home":
      return (
        <svg {...commonProps}>
          <path d="M3 10.5 12 3l9 7.5" />
          <path d="M5.5 9.5V21h13V9.5" />
          <path d="M9.5 21v-6h5v6" />
        </svg>
      );

    case "sensors":
      return (
        <svg {...commonProps}>
          <rect x="4" y="4" width="16" height="16" rx="3" />
          <path d="M8 9h8M8 12h8M8 15h5" />
        </svg>
      );

    case "map":
      return (
        <svg {...commonProps}>
          <path d="m3 6 5-2 8 2 5-2v14l-5 2-8-2-5 2Z" />
          <path d="M8 4v14M16 6v14" />
          <circle cx="12" cy="10" r="1.7" />
        </svg>
      );

    case "alerts":
      return (
        <svg {...commonProps}>
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
          <path d="M10 21h4" />
        </svg>
      );

    case "add":
      return (
        <svg {...commonProps}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8v8M8 12h8" />
        </svg>
      );

    case "reports":
      return (
        <svg {...commonProps}>
          <path d="M5 3h10l4 4v14H5Z" />
          <path d="M15 3v5h5M8 13h8M8 17h6" />
        </svg>
      );

    case "tracking":
      return (
        <svg {...commonProps}>
          <circle cx="12" cy="12" r="3" />
          <circle cx="12" cy="12" r="8" />
          <path d="M12 2v2M12 20v2M2 12h2M20 12h2" />
        </svg>
      );

    case "observations":
      return (
        <svg {...commonProps}>
          <path d="M4 20h4l11-11a2.8 2.8 0 0 0-4-4L4 16Z" />
          <path d="m13.5 6.5 4 4" />
        </svg>
      );

    case "installations":
      return (
        <svg {...commonProps}>
          <path d="M4 21V8l8-5 8 5v13Z" />
          <path d="M8 21v-6h8v6M8 10h.01M12 10h.01M16 10h.01" />
        </svg>
      );

    case "users":
      return (
        <svg {...commonProps}>
          <circle cx="9" cy="8" r="3" />
          <path d="M3.5 20a5.5 5.5 0 0 1 11 0" />
          <path d="M16 5.5a3 3 0 0 1 0 5.8M17 14a5 5 0 0 1 3.5 6" />
        </svg>
      );

    case "logo":
      return (
        <svg {...commonProps}>
          <rect x="3" y="4" width="18" height="16" rx="2.5" />
          <circle cx="9" cy="10" r="2" />
          <path d="m5 17 4-4 3 3 2-2 5 3" />
        </svg>
      );

    case "audit":
      return (
        <svg {...commonProps}>
          <path d="M6 3h12v18H6Z" />
          <path d="M9 8h6M9 12h6M9 16h4" />
        </svg>
      );

    case "part11":
      return (
        <svg {...commonProps}>
          <path d="M12 3 20 6v6c0 5-3.4 8-8 9-4.6-1-8-4-8-9V6Z" />
          <path d="m8.5 12 2.2 2.2 4.8-5" />
        </svg>
      );

    case "calibration":
      return (
        <svg {...commonProps}>
          <path d="M4 7h10M18 7h2M4 17h2M10 17h10" />
          <circle cx="16" cy="7" r="2" />
          <circle cx="8" cy="17" r="2" />
          <path d="M4 12h4M12 12h8" />
          <circle cx="10" cy="12" r="2" />
        </svg>
      );

    case "account":
      return (
        <svg {...commonProps}>
          <circle cx="12" cy="8" r="3.2" />
          <path d="M5 20a7 7 0 0 1 14 0" />
        </svg>
      );

    case "logout":
      return (
        <svg {...commonProps}>
          <path d="M10 5H5v14h5" />
          <path d="M13 8l4 4-4 4M8 12h9" />
        </svg>
      );

    default:
      return null;
  }
}


/* ===== Dirección del viento / brújula ===== */
const WIND_DIRECTIONS_16 = [
  "N", "NNE", "NE", "ENE",
  "E", "ESE", "SE", "SSE",
  "S", "SSO", "SO", "OSO",
  "O", "ONO", "NO", "NNO",
];

function normalizeWindDegrees(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return ((n % 360) + 360) % 360;
}

function getWindDirectionLabel(value) {
  const deg = normalizeWindDegrees(value);
  if (deg === null) return "—";
  return WIND_DIRECTIONS_16[Math.round(deg / 22.5) % 16];
}

function WindCompass({ degrees, size = 52 }) {
  const deg = normalizeWindDegrees(degrees);
  if (deg === null) return null;

  const label = getWindDirectionLabel(deg);

  return (
    <div
      title={`Dirección: ${formatSensorNumber(deg, "WIND_DIRECTION")}° · ${label}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        flexShrink: 0,
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 100 100"
        aria-label={`Brújula: ${label}`}
        role="img"
        style={{ display: "block" }}
      >
        <circle
          cx="50"
          cy="50"
          r="43"
          fill="rgba(239,246,255,.9)"
          stroke="rgba(37,99,235,.28)"
          strokeWidth="4"
        />
        <circle cx="50" cy="50" r="3.5" fill="#0f172a" />

        <text x="50" y="15" textAnchor="middle" fontSize="11" fontWeight="900" fill="#475569">N</text>
        <text x="86" y="54" textAnchor="middle" fontSize="10" fontWeight="800" fill="#64748b">E</text>
        <text x="50" y="92" textAnchor="middle" fontSize="10" fontWeight="800" fill="#64748b">S</text>
        <text x="14" y="54" textAnchor="middle" fontSize="10" fontWeight="800" fill="#64748b">O</text>

        <g transform={`rotate(${deg} 50 50)`}>
          <line
            x1="50"
            y1="55"
            x2="50"
            y2="25"
            stroke="#2563eb"
            strokeWidth="6"
            strokeLinecap="round"
          />
          <path d="M50 15 L42 30 L58 30 Z" fill="#2563eb" />
        </g>
      </svg>

      <span
        style={{
          minWidth: 30,
          fontSize: 14,
          lineHeight: 1,
          fontWeight: 900,
          color: "#0b67b2",
          letterSpacing: ".3px",
        }}
      >
        {label}
      </span>
    </div>
  );
}

/* ===== Imagen del sensor ===== */
function getSensorImage(info) {
  const candidates = [info?.image_url, info?.photo_url];
  for (const u of candidates) {
    if (!u) continue;
    if (/^https?:\/\//i.test(u) || u.startsWith("/")) return u;
    return `/${u.replace(/^\/?/, "")}`;
  }
  return "/images/default-sensor.png";
}

function normalizeTeamLogoInfo(payload) {
  if (!payload || typeof payload !== "object") return null;

  // Algunos endpoints devuelven el equipo directamente y otros dentro de
  // propiedades como team, data o result.
  const nested =
    payload.team && typeof payload.team === "object"
      ? payload.team
      : payload.data && typeof payload.data === "object"
      ? payload.data
      : payload.result && typeof payload.result === "object"
      ? payload.result
      : payload;

  return {
    ...payload,
    ...nested,
  };
}

function getTeamLogoValue(info) {
  return (
    info?.logo_url ??
    info?.logoUrl ??
    info?.logo ??
    info?.url ??
    info?.path ??
    ""
  );
}

// Convierte la ruta guardada en PostgreSQL en una URL visible para el navegador.
// El PDF puede usar una ruta física como:
// /home/rolando/backend/uploads/team-logos/logo.png
// pero el navegador necesita una URL pública como:
// https://dominio.com/uploads/team-logos/logo.png
function resolveTeamLogoUrl(value) {
  let raw = String(value || "").trim();
  if (!raw) return "";

  raw = raw.replace(/\\/g, "/");

  if (/^(https?:)?\/\//i.test(raw) || raw.startsWith("data:")) {
    return raw;
  }

  const backendBase = String(
    process.env.REACT_APP_BACKEND_URL || ""
  ).replace(/\/$/, "");

  // Si la base configurada termina en /api, se retira para acceder a archivos estáticos.
  const publicBase = backendBase.replace(/\/api$/i, "");

  const uploadsIndex = raw.toLowerCase().indexOf("/uploads/");
  if (uploadsIndex >= 0) {
    return `${publicBase}${raw.slice(uploadsIndex)}`;
  }

  const publicIndex = raw.toLowerCase().indexOf("/public/");
  if (publicIndex >= 0) {
    return `${publicBase}${raw.slice(publicIndex + "/public".length)}`;
  }

  if (/^(uploads|public)\//i.test(raw)) {
    const cleanRelative = raw.replace(/^public\//i, "");
    return `${publicBase}/${cleanRelative}`;
  }

  // Para rutas web normales como /images/logo.png.
  if (
    raw.startsWith("/") &&
    !/^\/(home|var|opt|srv|usr|mnt|root|tmp)\//i.test(raw)
  ) {
    return `${publicBase}${raw}`;
  }

  return "";
}

/* ====== Rango de tiempo ====== */
const RANGE_OPTIONS = ["24h", "7d", "30d"];
const RANGE_TO_MS = {
  "24h": 24 * 3600 * 1000,
  "7d": 7 * 24 * 3600 * 1000,
  "30d": 30 * 24 * 3600 * 1000,
};
const RANGE_TO_UNIT = { "24h": "minute", "7d": "hour", "30d": "day" };
const MAX_CUSTOM_RANGE_MS = 30 * 24 * 3600 * 1000;
const ECUADOR_UTC_OFFSET_MS = 5 * 60 * 60 * 1000;

function msToEcuadorDateTimeInput(ms) {
  if (!Number.isFinite(ms)) return "";
  return new Date(ms - ECUADOR_UTC_OFFSET_MS).toISOString().slice(0, 16);
}
function ecuadorDateTimeInputToMs(value) {
  const match = String(value || "").trim().match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/
  );
  if (!match) return NaN;

  const [, year, month, day, hour, minute] = match.map(Number);
  return Date.UTC(year, month - 1, day, hour + 5, minute, 0, 0);
}

function rangePresetForDuration(durationMs) {
  if (durationMs <= RANGE_TO_MS["24h"]) return "24h";
  if (durationMs <= RANGE_TO_MS["7d"]) return "7d";
  return "30d";
}

function timeUnitForDuration(durationMs) {
  if (durationMs <= RANGE_TO_MS["24h"]) return "minute";
  if (durationMs <= RANGE_TO_MS["7d"]) return "hour";
  return "day";
}

const CHART_GAP_LIMIT_MS = {
  "24h": 20 * 60 * 1000,        // 20 minutos
  "7d": 60 * 60 * 1000,         // 1 hora
  "30d": 6 * 60 * 60 * 1000,    // 6 horas
};

// Salesiana envía una lectura aproximadamente cada hora.
// El margen de 90 minutos evita cortes por pequeños retrasos de transmisión.
const SALESIANA_CHART_GAP_LIMIT_MS = {
  "24h": 90 * 60 * 1000,        // 1 hora y 30 minutos
  "7d": 90 * 60 * 1000,         // 1 hora y 30 minutos
  "30d": 6 * 60 * 60 * 1000,    // conserva la vista general mensual
};

// Amagua envía una lectura aproximadamente cada 30 minutos.
// En 24 h se permiten hasta 45 minutos entre lecturas antes de dibujar un corte.
const AMAGUA_CHART_GAP_LIMIT_MS = {
  "24h": 45 * 60 * 1000,        // 45 minutos
  "7d": 60 * 60 * 1000,         // 1 hora
  "30d": 6 * 60 * 60 * 1000,    // conserva la vista general mensual
};

function insertOfflineGaps(
  points,
  currentRange,
  isSalesianaUser = false,
  isAmaguaUser = false
) {
  const gapLimits = isAmaguaUser
    ? AMAGUA_CHART_GAP_LIMIT_MS
    : isSalesianaUser
    ? SALESIANA_CHART_GAP_LIMIT_MS
    : CHART_GAP_LIMIT_MS;

  const maxGapMs = gapLimits[currentRange] || gapLimits["24h"];

  // Conserva puntos explícitos con y=null. En Chart.js, con spanGaps=false,
  // esos puntos generan un hueco visual sin alterar la escala del eje Y.
  const sorted = (points || [])
    .filter(
      (p) =>
        Number.isFinite(p?.x) &&
        (p?.y === null || Number.isFinite(p?.y))
    )
    .sort((a, b) => a.x - b.x);

  if (!sorted.length) return [];

  const result = [];
  let previousValidPoint = null;

  for (const current of sorted) {
    if (!Number.isFinite(current?.y)) {
      result.push({
        ...current,
        y: null,
        isGap: true,
      });
      continue;
    }

    if (
      previousValidPoint &&
      current.x - previousValidPoint.x > maxGapMs
    ) {
      result.push({
        x: previousValidPoint.x + 1,
        y: null,
        isGap: true,
      });
    }

    result.push(current);
    previousValidPoint = current;
  }

  return result;
}

// Cuando un equipo dispone de TOC, se considera la variable principal.
// En ese caso, la presentación debe quedar:
// TOC -> conductividad compensada -> temperatura -> demás variables.
const TOC_PRIMARY_VARIABLE_ORDER = [
  "TOC",
  "CONDUCTIVIDAD_COMPENSADA",
  "CONDUCTIVITY_COMP",
  "TEMP",
  "TEMP_1",
  "TEMP_2",
  "TEMPERATURA",
  "TEMPERATURE",
  "temperature",
];

function normalizeVariableOrderKey(variable) {
  return String(canonVar(variable) || variable || "")
    .trim()
    .toUpperCase();
}

function getContextualVariableOrder(variables, fallbackOrder) {
  const values = Array.isArray(variables) ? variables : [];
  const hasToc = values.some(
    (variable) => normalizeVariableOrderKey(variable) === "TOC"
  );

  if (!hasToc) return fallbackOrder;

  const priorityKeys = new Set(
    TOC_PRIMARY_VARIABLE_ORDER.map(normalizeVariableOrderKey)
  );

  return [
    ...TOC_PRIMARY_VARIABLE_ORDER,
    ...fallbackOrder.filter(
      (variable) => !priorityKeys.has(normalizeVariableOrderKey(variable))
    ),
  ];
}

function sortVariablesByContext(variables, fallbackOrder) {
  const values = Array.isArray(variables) ? [...variables] : [];
  const activeOrder = getContextualVariableOrder(values, fallbackOrder);

  const orderIndex = new Map(
    activeOrder.map((variable, index) => [
      normalizeVariableOrderKey(variable),
      index,
    ])
  );

  return values
    .map((variable, originalIndex) => ({ variable, originalIndex }))
    .sort((left, right) => {
      const leftIndex =
        orderIndex.get(normalizeVariableOrderKey(left.variable)) ??
        Number.MAX_SAFE_INTEGER;
      const rightIndex =
        orderIndex.get(normalizeVariableOrderKey(right.variable)) ??
        Number.MAX_SAFE_INTEGER;

      if (leftIndex !== rightIndex) return leftIndex - rightIndex;

      if (
        leftIndex === Number.MAX_SAFE_INTEGER &&
        rightIndex === Number.MAX_SAFE_INTEGER
      ) {
        const alphabetical = String(left.variable).localeCompare(
          String(right.variable),
          "es"
        );

        if (alphabetical !== 0) return alphabetical;
      }

      return left.originalIndex - right.originalIndex;
    })
    .map(({ variable }) => variable);
}

const OVERVIEW_VARIABLE_ORDER = [
  "TOC",
  "PH",
  "PH_1",
  "PH_2",
  "turbidity",
  "TURBIDEZ",
  "EC_K1",
  "EC_K10",
  "CLORO_LIBRE",
  "O2",
  "TEMP",
  "TEMP_1",
  "TEMP_2",
  "TEMPERATURA",
  "TEMPERATURA_EXTERNA",
  "temperature",
  "humidity",
  "HUM",
  "HUMEDAD",
  "HUMEDAD_EXTERNA",
  "ADC1",
  "ADC2",
  "TSS",
  "NIVELHIDROSTATICO",
  "LEVEL_CM",
  "NIVEL",
  "level",
  "PRESSURE",
  "WIND_SPEED",
  "WIND_DIRECTION",
  "RAINFALL",
  "FLUJO",
  "flow",
  "LAeq",
  "BAT",
];

const parseNumOrNull = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

function calcChartStats(points) {
  const clean = (points || [])
    .filter((p) => Number.isFinite(p.y))
    .sort((a, b) => a.x - b.x);

  if (!clean.length) return null;

  const values = clean.map((p) => p.y);
  const count = values.length;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const mean = values.reduce((a, b) => a + b, 0) / count;

  const meanSquaredDeviation =
    values.reduce((acc, v) => acc + Math.pow(v - mean, 2), 0) / count;

  const std = Math.sqrt(meanSquaredDeviation);
  const last = clean[clean.length - 1]?.y;

  return {
    count,
    min,
    max,
    mean,
    std,
    last,
    from: clean[0]?.x,
    to: clean[clean.length - 1]?.x,
  };
}

function fmtStatValue(value, variable, type = null) {
  if (!Number.isFinite(value)) return "—";
  const u = getVariableUnit(variable, type);
  return `${formatSensorNumber(value, variable)}${u ? ` ${u}` : ""}`;
}


function ecuadorDayKey(ms) {
  if (!Number.isFinite(ms)) return "";

  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(ms));

  const values = Object.fromEntries(
    parts
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value])
  );

  return `${values.year}-${values.month}-${values.day}`;
}

// Convierte el contador acumulado de precipitación en incrementos agrupados por día.
// Si el contador se reinicia, el valor posterior al reinicio se toma como lluvia nueva.
function buildDailyRainfallPoints(points) {
  const clean = (points || [])
    .filter((p) => Number.isFinite(p?.x) && Number.isFinite(p?.y))
    .sort((a, b) => a.x - b.x);

  if (!clean.length) return [];

  const buckets = new Map();
  let previousPoint = null;

  for (const point of clean) {
    const dayKey = ecuadorDayKey(point.x);
    if (!dayKey) continue;

    if (!buckets.has(dayKey)) {
      buckets.set(dayKey, {
        x: point.x,
        y: 0,
        rainfall_daily: true,
        day_key: dayKey,
      });
    }

    const bucket = buckets.get(dayKey);
    bucket.x = point.x;

    if (previousPoint) {
      const currentValue = Number(point.y);
      const previousValue = Number(previousPoint.y);

      let increment =
        currentValue >= previousValue
          ? currentValue - previousValue
          : currentValue;

      if (!Number.isFinite(increment) || increment < 0) increment = 0;

      bucket.y += increment;
    }

    previousPoint = point;
  }

  return Array.from(buckets.values()).map((point) => ({
    ...point,
    y: Number(point.y.toFixed(4)),
  }));
}

function getChartVariableDisplayName(variable, type = null) {
  const key = normalizeVariableDisplayKey(variable);
  const resolvedType = type ? resolveType(type) : null;

  if (resolvedType === "Meteorologica" && key === "RAINFALL") {
    return "Lluvia registrada por día";
  }

  return getVariableDisplayName(variable, type);
}

const visualLimitLabelPlugin = {
  id: "visualLimitLabel",
  afterDatasetsDraw(chart) {
    const datasets = (chart.data?.datasets || []).filter(
      (item) => item?.isVisualLimit === true
    );

    if (!datasets.length) return;

    const yScale = chart.scales?.y;
    const chartArea = chart.chartArea;
    if (!yScale || !chartArea) return;

    const ctx = chart.ctx;

    datasets.forEach((dataset) => {
      const value = Number(dataset.visualLimitValue);
      if (!Number.isFinite(value)) return;

      const y = yScale.getPixelForValue(value);
      if (y < chartArea.top || y > chartArea.bottom) return;

      const isMin = dataset.visualLimitKind === "min";
      const label = String(
        dataset.label || (isMin ? "Mínimo visual" : "Máximo visual")
      );

      ctx.save();
      ctx.font =
        '700 11px ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial';

      const textWidth = ctx.measureText(label).width;
      const horizontalPadding = 7;
      const boxHeight = 22;
      const boxWidth = textWidth + horizontalPadding * 2;
      const x = Math.max(chartArea.left + 4, chartArea.right - boxWidth - 4);

      const preferredY = isMin ? y + 3 : y - boxHeight - 3;
      const boxY = Math.max(
        chartArea.top + 2,
        Math.min(preferredY, chartArea.bottom - boxHeight - 2)
      );

      const lineColor = String(
        dataset.borderColor || (isMin ? "#0f766e" : "#dc2626")
      );

      ctx.fillStyle = "rgba(255,255,255,.94)";
      ctx.strokeStyle = lineColor;
      ctx.globalAlpha = 0.9;
      ctx.lineWidth = 1;
      ctx.beginPath();

      if (typeof ctx.roundRect === "function") {
        ctx.roundRect(x, boxY, boxWidth, boxHeight, 6);
      } else {
        ctx.rect(x, boxY, boxWidth, boxHeight);
      }

      ctx.fill();
      ctx.stroke();

      ctx.globalAlpha = 1;
      ctx.fillStyle = lineColor;
      ctx.textBaseline = "middle";
      ctx.fillText(label, x + horizontalPadding, boxY + boxHeight / 2);
      ctx.restore();
    });
  },
};

function buildVisualLimitDataset(
  variable,
  value,
  currentRange,
  rawPoints = [],
  kind = "max",
  rangeWindow = null
) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return null;

  const validX = (rawPoints || [])
    .map((point) => Number(point?.x))
    .filter(Number.isFinite);

  const now = Date.now();
  const isCustomWindow =
    currentRange === "custom" &&
    Number.isFinite(rangeWindow?.fromMs) &&
    Number.isFinite(rangeWindow?.toMs);

  let startX = isCustomWindow
    ? rangeWindow.fromMs
    : validX.length
    ? Math.min(...validX)
    : now - (RANGE_TO_MS[currentRange] || RANGE_TO_MS["24h"]);

  let endX = isCustomWindow
    ? rangeWindow.toMs
    : validX.length
    ? Math.max(...validX)
    : now;

  if (startX === endX) {
    endX = startX + 60 * 1000;
  }

  const unit = getVariableUnit(variable);
  const formatted = formatSensorNumber(numericValue, variable);
  const isMin = kind === "min";
  const lineColor = isMin ? "#0f766e" : "#dc2626";

  return {
    label: `${isMin ? "Mínimo" : "Máximo"} visual: ${formatted}${
      unit ? ` ${unit}` : ""
    }`,
    data: [
      { x: startX, y: numericValue },
      { x: endX, y: numericValue },
    ],
    borderColor: lineColor,
    backgroundColor: "transparent",
    borderWidth: 2,
    borderDash: [8, 6],
    pointRadius: 0,
    pointHoverRadius: 0,
    pointHitRadius: 0,
    tension: 0,
    fill: false,
    parsing: false,
    spanGaps: true,
    order: 0,
    isVisualLimit: true,
    visualLimitKind: isMin ? "min" : "max",
    visualLimitValue: numericValue,
  };
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function coordToNumber(value) {
  // Evita convertir null / "" en 0. Sin este control, un sensor sin
  // coordenadas termina apareciendo en Lat 0 / Lon 0 (Golfo de Guinea).
  if (value === null || value === undefined) return null;
  if (typeof value === "string" && !value.trim()) return null;

  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function getDeviceLat(device) {
  return coordToNumber(device?.lat ?? device?.latitude);
}

function getDeviceLng(device) {
  return coordToNumber(device?.lng ?? device?.lon ?? device?.longitude);
}

function hasUsableCoordinates(device) {
  const lat = getDeviceLat(device);
  const lng = getDeviceLng(device);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return false;

  // 0,0 se usa aquí como coordenada inválida de respaldo. Para una
  // plataforma de sensores en campo no representa una ubicación válida.
  if (Math.abs(lat) < 1e-9 && Math.abs(lng) < 1e-9) return false;

  return true;
}

function statusText(status) {
  return status === "online"
    ? "EN LÍNEA"
    : status === "stale"
    ? "INTERMITENTE"
    : "FUERA DE LÍNEA";
}

function fmtOverviewValue(value, variable, type = null) {
  return Number.isFinite(value) ? fmtStatValue(value, variable, type) : "—";
}

export default function SensorDashboard() {
  const { tipo } = useParams();
  const activeType = resolveType(tipo);
  const isGeneralDashboard = activeType === "General";
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);

  // Permite abrir un sensor concreto desde el Panel General y conservar "Ver como".
  const routeQuery = useMemo(() => new URLSearchParams(window.location.search), [tipo]);
  const requestedSensorId = routeQuery.get("sensorId") || "";
  const requestedViewAs = routeQuery.get("as") || "";

  const canViewData = !!user?.permissions?.includes("view_data");
  const canManageAlarms = !!user?.permissions?.includes("manage_alarms");
  const alarmsEnabledByPlan = !!user?.alarms_enabled;
  const canUseAlarms = canManageAlarms && alarmsEnabledByPlan;
  const canGenerateReports = !!user?.permissions?.includes("generate_reports");
  const canManageUsers = !!user?.permissions?.includes("manage_users");
  const canManageCalibrations = Number(user?.id) === 1 && !!user?.permissions?.includes("manage_calibrations");
  const canManageInstallations = !!user?.permissions?.includes("manage_installations");
  const canClaimSensors = !!user;
  const canAccessPart11 = canManageUsers && !!user?.part11_enabled;

  const token = localStorage.getItem("token");
  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const [viewAs, setViewAs] = useState(requestedViewAs);
  const withAs = (url) =>
    viewAs ? `${url}${url.includes("?") ? "&" : "?"}as=${encodeURIComponent(viewAs)}` : url;

  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState("");
  // En /dashboard/General la vista siempre representa todos los sensores,
  // independientemente de su aplicación/tipo.
  const isAllDevices = isGeneralDashboard || selectedDevice === ALL_DEVICES_VALUE;
  const [allSensorsOverview, setAllSensorsOverview] = useState([]);
  const [allSensorsLoading, setAllSensorsLoading] = useState(false);
  const [allSensorsMsg, setAllSensorsMsg] = useState("");

  // Filtros exclusivos del Panel General.
  const [generalSearch, setGeneralSearch] = useState("");
  const [generalTypeFilter, setGeneralTypeFilter] = useState("all");
  const [generalStatusFilter, setGeneralStatusFilter] = useState("all");
  const [generalVisibleCount, setGeneralVisibleCount] = useState(12);

  const [availableVars, setAvailableVars] = useState([]);
  const [kpiVars, setKpiVars] = useState([]);
  const [sensorInfo, setSensorInfo] = useState(null);

  // El módulo de instalación solo aplica a las maletas S.M.T.R.
  // Se mantiene además la validación por permiso manage_installations.
  const isSMTRSensor = String(sensorInfo?.name || "")
    .trim()
    .toUpperCase()
    .startsWith("S.M.T.R");

  const canShowInstallations =
    canManageInstallations &&
    isSMTRSensor &&
    !!selectedDevice &&
    !isAllDevices;
  const [sensorData, setSensorData] = useState([]);
  const [kpis, setKpis] = useState({});
  const [calibrationMode, setCalibrationMode] = useState("raw");
  const [usersList, setUsersList] = useState([]);
  const [sensorStatus, setSensorStatus] = useState(null);
  const [statusTs, setStatusTs] = useState(null);

  // Estado del accesorio EM300 asociado al sensor (tapa de alcantarilla).
  // El EM300 no se muestra como sensor independiente: solo aporta OPEN/CLOSED.
  const [coverState, setCoverState] = useState(null);


  // Observaciones operativas: inicialmente habilitadas para MOLINOS CHAMPION.
  // También funciona cuando un administrador usa "Ver como: molinos".
  const isMolinosUser = useMemo(() => {
    const viewedUser = viewAs
      ? usersList.find(
          (item) =>
            String(item?.username || "").trim().toLowerCase() ===
            String(viewAs).trim().toLowerCase()
        )
      : null;

    const effectiveUserId = viewedUser?.id ?? (!viewAs ? user?.id : null);
    const effectiveTeamId =
      viewedUser?.team_id ??
      viewedUser?.teamId ??
      (!viewAs ? user?.team_id ?? user?.teamId : null);
    const effectiveUsername = String(
      viewAs || viewedUser?.username || user?.username || user?.name || ""
    )
      .trim()
      .toLowerCase();

    return (
      Number(effectiveUserId) === 39 ||
      Number(effectiveTeamId) === 21 ||
      effectiveUsername === "molinos" ||
      effectiveUsername.includes("molinos champion")
    );
  }, [
    viewAs,
    usersList,
    user?.id,
    user?.team_id,
    user?.teamId,
    user?.username,
    user?.name,
  ]);

  // La excepción se aplica al usuario Salesiana, no al dispositivo.
  // También funciona cuando un administrador usa la opción "Ver como".
  const isSalesianaUser = useMemo(() => {
    const viewedUser = viewAs
      ? usersList.find(
          (item) =>
            String(item?.username || "").trim().toLowerCase() ===
            String(viewAs).trim().toLowerCase()
        )
      : null;

    const effectiveUserId = viewedUser?.id ?? (!viewAs ? user?.id : null);
    const effectiveUsername = String(
      viewAs || viewedUser?.username || user?.username || user?.name || ""
    )
      .trim()
      .toLowerCase();

    return (
      Number(effectiveUserId) === 36 ||
      effectiveUsername === "salesiana" ||
      effectiveUsername.includes("universidad salesiana")
    );
  }, [viewAs, usersList, user?.id, user?.username, user?.name]);

  // Amagua transmite aproximadamente cada 30 minutos.
  // La excepción también se respeta cuando un administrador usa "Ver como".
  const isAmaguaUser = useMemo(() => {
    const viewedUser = viewAs
      ? usersList.find(
          (item) =>
            String(item?.username || "").trim().toLowerCase() ===
            String(viewAs).trim().toLowerCase()
        )
      : null;

    const effectiveUserId = viewedUser?.id ?? (!viewAs ? user?.id : null);
    const effectiveUsername = String(
      viewAs || viewedUser?.username || user?.username || user?.name || ""
    )
      .trim()
      .toLowerCase();

    return (
      Number(effectiveUserId) === 29 ||
      effectiveUsername === "amagua"
    );
  }, [viewAs, usersList, user?.id, user?.username, user?.name]);

  const statusLimits = isAmaguaUser
    ? AMAGUA_STATUS_LIMITS
    : isSalesianaUser
    ? SALESIANA_STATUS_LIMITS
    : DEFAULT_STATUS_LIMITS;

  const computeContextStatus = (ts, now = new Date()) =>
    computeStatus(ts, now, statusLimits.staleMin, statusLimits.offlineMin);
  const [claimOpen, setClaimOpen] = useState(false);
  const [claimDeviceId, setClaimDeviceId] = useState("");
  const [claimPairingCode, setClaimPairingCode] = useState("");
  const [claimLoading, setClaimLoading] = useState(false);
  const [claimMsg, setClaimMsg] = useState("");

 // ===== Editor del perfil del sensor =====
  const [sensorEditorOpen, setSensorEditorOpen] = useState(false);
  const [sensorEditorName, setSensorEditorName] = useState("");
  const [sensorEditorLocationSource, setSensorEditorLocationSource] =
  useState("none");
  const [sensorEditorLat, setSensorEditorLat] = useState("");
  const [sensorEditorLon, setSensorEditorLon] = useState("");
  const [sensorEditorSaving, setSensorEditorSaving] = useState(false);
  const [sensorEditorMsg, setSensorEditorMsg] = useState("");
 
  // ===== Foto del lugar de instalación =====
  const sensorEditorPhotoInputRef = useRef(null);

  const [sensorEditorPhotoUrl, setSensorEditorPhotoUrl] = useState("");
  const [sensorEditorPhotoFile, setSensorEditorPhotoFile] = useState(null);
  const [sensorEditorPhotoPreviewUrl, setSensorEditorPhotoPreviewUrl] =
  useState("");

  const [sensorEditorPhotoLoading, setSensorEditorPhotoLoading] =
  useState(false);

  const [sensorEditorPhotoDeleting, setSensorEditorPhotoDeleting] =
  useState(false);

// Rango independiente para cada gráfica
  const [chartRanges, setChartRanges] = useState({});
  const [chartCustomRanges, setChartCustomRanges] = useState({});
  const [chartCustomRangeOpen, setChartCustomRangeOpen] = useState({});
  const [chartCustomRangeMessages, setChartCustomRangeMessages] = useState({});
  const [chartStats, setChartStats] = useState({});

 // ===== Comparación de laboratorio =====
  const [labComparisonOpen, setLabComparisonOpen] = useState(false);
  const [labComparisonVariable, setLabComparisonVariable] = useState("");
  const [labSamples, setLabSamples] = useState(["", "", "", "", ""]);
  const [labComparisonLoading, setLabComparisonLoading] = useState(false);
  const [labComparisonMsg, setLabComparisonMsg] = useState("");
  const [labComparisonResult, setLabComparisonResult] = useState(null);
  
  const openLabComparison = (variable) => {
  const vKey = canonVar(variable);

  // Por ahora, comparación de laboratorio únicamente
  // para Cloro libre del sensor Módulo Guaranda.
  if (
    selectedDevice !== "24e124445f287697" ||
    vKey !== "CLORO_LIBRE"
  ) {
    return;
  }

  setLabComparisonVariable(vKey);
  setLabSamples(["", "", "", "", ""]);
  setLabComparisonMsg("");
  setLabComparisonResult(null);
  setLabComparisonOpen(true);
};

const closeLabComparison = () => {
  if (labComparisonLoading) return;

  setLabComparisonOpen(false);
  setLabComparisonMsg("");
  setLabComparisonResult(null);
};

const updateLabSample = (index, value) => {
  setLabSamples((current) =>
    current.map((item, i) => (i === index ? value : item))
  );
};

const addLabSample = () => {
  setLabSamples((current) => {
    if (current.length >= 10) return current;
    return [...current, ""];
  });
};

const removeLabSample = (index) => {
  setLabSamples((current) => {
    if (current.length <= 5) return current;
    return current.filter((_, i) => i !== index);
  });
};

const submitLabComparison = async () => {
  try {
    setLabComparisonMsg("");
    setLabComparisonResult(null);

    if (labSamples.length < 5 || labSamples.length > 10) {
      setLabComparisonMsg("Debe ingresar entre 5 y 10 muestras.");
      return;
    }

    const hasEmpty = labSamples.some(
      (value) => String(value ?? "").trim() === ""
    );

    if (hasEmpty) {
      setLabComparisonMsg("Debe completar todas las muestras.");
      return;
    }

    const numericSamples = labSamples.map((value) => Number(value));

    if (numericSamples.some((value) => !Number.isFinite(value))) {
      setLabComparisonMsg("Todas las muestras deben ser valores numéricos.");
      return;
    }

    setLabComparisonLoading(true);

    const { data } = await axios.post(
      `${API_ROOT}/lab-comparisons`,
      {
        sensor_id: selectedDevice,
        variable: labComparisonVariable,
        unit: getVariableUnit(labComparisonVariable, activeType),
        samples: numericSamples,
      },
      authHeader
    );

    setLabComparisonResult(data?.comparison || null);
    setLabComparisonMsg("Comparación realizada correctamente.");
  } catch (error) {
    console.error("Error comparación laboratorio:", error);

    setLabComparisonMsg(
      error?.response?.data?.message ||
        "No se pudo realizar la comparación de laboratorio."
    );
  } finally {
    setLabComparisonLoading(false);
  }
};

  // Límites visuales independientes de los umbrales de alarma.
  // chartLimits = máximos; chartMinLimits = mínimos.
  const [chartLimits, setChartLimits] = useState({});
  const [chartMinLimits, setChartMinLimits] = useState({});
  const [chartLimitInputs, setChartLimitInputs] = useState({});
  const [chartMinLimitInputs, setChartMinLimitInputs] = useState({});
  const [chartLimitSaving, setChartLimitSaving] = useState({});
  const [chartLimitMessages, setChartLimitMessages] = useState({});
  const chartLimitsRef = useRef({});
  const chartMinLimitsRef = useRef({});

  const getChartRange = (variable) => {
    const vKey = canonVar(variable);
    return chartRanges[vKey] || "24h";
  };

  const getDefaultCustomRange = (variable) => {
    const vKey = canonVar(variable);
    const currentRange = getChartRange(vKey);
    const fallbackDuration =
      RANGE_TO_MS[currentRange] || RANGE_TO_MS["24h"];
    const toMs = Date.now();
    const fromMs = toMs - fallbackDuration;

    return {
      from: msToEcuadorDateTimeInput(fromMs),
      to: msToEcuadorDateTimeInput(toMs),
    };
  };

  const resolveChartWindow = (
    variable,
    selectedRange,
    explicitCustomRange = null
  ) => {
    const vKey = canonVar(variable);
    const currentRange = selectedRange || getChartRange(vKey);

    if (currentRange === "custom") {
      const customRange =
        explicitCustomRange || chartCustomRanges[vKey] || null;
      const fromMs = ecuadorDateTimeInputToMs(customRange?.from);
      const toMs = ecuadorDateTimeInputToMs(customRange?.to);

      if (
        Number.isFinite(fromMs) &&
        Number.isFinite(toMs) &&
        fromMs < toMs
      ) {
        return {
          fromMs,
          toMs,
          durationMs: toMs - fromMs,
          isCustom: true,
        };
      }
    }

    const toMs = Date.now();
    const durationMs =
      RANGE_TO_MS[currentRange] || RANGE_TO_MS["24h"];

    return {
      fromMs: toMs - durationMs,
      toMs,
      durationMs,
      isCustom: false,
    };
  };

  const toggleCustomChartRange = (variable) => {
    const vKey = canonVar(variable);

    setChartCustomRanges((current) => {
      if (current[vKey]) return current;
      return {
        ...current,
        [vKey]: getDefaultCustomRange(vKey),
      };
    });

    setChartCustomRangeMessages((current) => ({
      ...current,
      [vKey]: "",
    }));

    setChartCustomRangeOpen((current) => ({
      ...current,
      [vKey]: !current[vKey],
    }));
  };

  const updateCustomChartRange = (variable, field, value) => {
    const vKey = canonVar(variable);

    setChartCustomRanges((current) => ({
      ...current,
      [vKey]: {
        ...(current[vKey] || getDefaultCustomRange(vKey)),
        [field]: value,
      },
    }));
  };

  const applyCustomChartRange = (variable) => {
    const vKey = canonVar(variable);
    const customRange =
      chartCustomRanges[vKey] || getDefaultCustomRange(vKey);
    const fromMs = ecuadorDateTimeInputToMs(customRange.from);
    const toMs = ecuadorDateTimeInputToMs(customRange.to);

    if (!Number.isFinite(fromMs) || !Number.isFinite(toMs)) {
      setChartCustomRangeMessages((current) => ({
        ...current,
        [vKey]: "Selecciona una fecha y hora válidas.",
      }));
      return;
    }

    if (fromMs >= toMs) {
      setChartCustomRangeMessages((current) => ({
        ...current,
        [vKey]: "La fecha Desde debe ser anterior a Hasta.",
      }));
      return;
    }

    if (toMs - fromMs > MAX_CUSTOM_RANGE_MS) {
      setChartCustomRangeMessages((current) => ({
        ...current,
        [vKey]: "El rango personalizado puede ser de hasta 30 días.",
      }));
      return;
    }

    setChartCustomRangeMessages((current) => ({
      ...current,
      [vKey]: "",
    }));
    setChartRanges((current) => ({
      ...current,
      [vKey]: "custom",
    }));
    setChartCustomRangeOpen((current) => ({
      ...current,
      [vKey]: false,
    }));

    if (selectedDevice && selectedDevice !== ALL_DEVICES_VALUE) {
      loadSingleSeries(
        selectedDevice,
        vKey,
        "custom",
        customRange
      );
    }
  };

  const changeChartRange = (variable, newRange) => {
    const vKey = canonVar(variable);

    setChartRanges((prev) => ({
      ...prev,
      [vKey]: newRange,
    }));
    setChartCustomRangeOpen((current) => ({
      ...current,
      [vKey]: false,
    }));
    setChartCustomRangeMessages((current) => ({
      ...current,
      [vKey]: "",
    }));

    if (selectedDevice && selectedDevice !== ALL_DEVICES_VALUE) {
      loadSingleSeries(
        selectedDevice,
        vKey,
        newRange
      );
    }
  };

  const chartsRef = useRef({});

  useEffect(() => {
    chartLimitsRef.current = chartLimits;
  }, [chartLimits]);

  useEffect(() => {
    chartMinLimitsRef.current = chartMinLimits;
  }, [chartMinLimits]);

  const syncVisualLimitOnChart = (variable) => {
    const vKey = canonVar(variable);
    const chart = chartsRef.current[vKey];

    if (!chart) return;

    const rawPoints = Array.isArray(chart.$rawSensorPoints)
      ? chart.$rawSensorPoints
      : [];

    const currentRange = chart.$currentRange || getChartRange(vKey);
    const rangeWindow = chart.$rangeWindow || null;

    const datasets = (chart.data?.datasets || []).filter(
      (dataset) => dataset?.isVisualLimit !== true
    );

    const maxDataset = buildVisualLimitDataset(
      vKey,
      chartLimitsRef.current[vKey],
      currentRange,
      rawPoints,
      "max",
      rangeWindow
    );

    const minDataset = buildVisualLimitDataset(
      vKey,
      chartMinLimitsRef.current[vKey],
      currentRange,
      rawPoints,
      "min",
      rangeWindow
    );

    chart.data.datasets = [
      ...datasets,
      ...(maxDataset ? [maxDataset] : []),
      ...(minDataset ? [minDataset] : []),
    ];

    chart.update("none");
  };

  useEffect(() => {
    Object.keys(chartsRef.current).forEach((variable) => {
      syncVisualLimitOnChart(variable);
    });
  }, [chartLimits, chartMinLimits]); // eslint-disable-line

  useEffect(() => {
    if (!selectedDevice || isAllDevices) {
      setChartLimits({});
      setChartMinLimits({});
      setChartLimitInputs({});
      setChartMinLimitInputs({});
      setChartLimitMessages({});
      return;
    }

    let alive = true;

    (async () => {
      try {
        const url = withAs(
          `${API_ROOT}/chart-limits/${encodeURIComponent(selectedDevice)}`
        );
        const { data } = await axios.get(url, authHeader);
        if (!alive) return;

        const rows = Array.isArray(data?.limits)
          ? data.limits
          : Array.isArray(data)
          ? data
          : [];

        const nextMaxLimits = {};
        const nextMinLimits = {};
        const nextMaxInputs = {};
        const nextMinInputs = {};

        rows.forEach((row) => {
          const variable = canonVar(row?.variable);
          if (!variable) return;

          const maxValue =
            row?.max_value === null || row?.max_value === undefined
              ? NaN
              : Number(row.max_value);
          const minValue =
            row?.min_value === null || row?.min_value === undefined
              ? NaN
              : Number(row.min_value);

          if (Number.isFinite(maxValue)) {
            nextMaxLimits[variable] = maxValue;
            nextMaxInputs[variable] = String(maxValue);
          }

          if (Number.isFinite(minValue)) {
            nextMinLimits[variable] = minValue;
            nextMinInputs[variable] = String(minValue);
          }
        });

        setChartLimits(nextMaxLimits);
        setChartMinLimits(nextMinLimits);
        setChartLimitInputs(nextMaxInputs);
        setChartMinLimitInputs(nextMinInputs);
        setChartLimitMessages({});
      } catch (error) {
        if (!alive) return;

        setChartLimits({});
        setChartMinLimits({});
        setChartLimitInputs({});
        setChartMinLimitInputs({});
        setChartLimitMessages({
          __global:
            error?.response?.data?.error ||
            "No se pudieron cargar los límites visuales.",
        });
      }
    })();

    return () => {
      alive = false;
    };
  }, [selectedDevice, viewAs, isAllDevices, authHeader]); // eslint-disable-line

  const saveChartLimit = async (variable) => {
    if (!selectedDevice || isAllDevices) return;

    const vKey = canonVar(variable);
    const rawMin = String(chartMinLimitInputs[vKey] ?? "").trim();
    const rawMax = String(chartLimitInputs[vKey] ?? "").trim();

    if (!rawMin && !rawMax) {
      setChartLimitMessages((current) => ({
        ...current,
        [vKey]: "Ingresa un mínimo, un máximo o ambos.",
      }));
      return;
    }

    const minValue = rawMin ? Number(rawMin) : null;
    const maxValue = rawMax ? Number(rawMax) : null;

    if (rawMin && !Number.isFinite(minValue)) {
      setChartLimitMessages((current) => ({
        ...current,
        [vKey]: "Ingresa un mínimo visual válido.",
      }));
      return;
    }

    if (rawMax && !Number.isFinite(maxValue)) {
      setChartLimitMessages((current) => ({
        ...current,
        [vKey]: "Ingresa un máximo visual válido.",
      }));
      return;
    }

    if (
      Number.isFinite(minValue) &&
      Number.isFinite(maxValue) &&
      minValue >= maxValue
    ) {
      setChartLimitMessages((current) => ({
        ...current,
        [vKey]: "El mínimo visual debe ser menor que el máximo visual.",
      }));
      return;
    }

    setChartLimitSaving((current) => ({ ...current, [vKey]: true }));
    setChartLimitMessages((current) => ({ ...current, [vKey]: "" }));

    try {
      const url = withAs(
        `${API_ROOT}/chart-limits/${encodeURIComponent(
          selectedDevice
        )}/${encodeURIComponent(vKey)}`
      );

      const { data } = await axios.put(
        url,
        {
          min_value: minValue,
          max_value: maxValue,
        },
        authHeader
      );

      const savedMin =
        data?.limit?.min_value === null ||
        data?.limit?.min_value === undefined
          ? null
          : Number(data.limit.min_value);
      const savedMax =
        data?.limit?.max_value === null ||
        data?.limit?.max_value === undefined
          ? null
          : Number(data.limit.max_value);

      setChartMinLimits((current) => {
        const next = { ...current };
        if (Number.isFinite(savedMin)) next[vKey] = savedMin;
        else delete next[vKey];
        return next;
      });

      setChartLimits((current) => {
        const next = { ...current };
        if (Number.isFinite(savedMax)) next[vKey] = savedMax;
        else delete next[vKey];
        return next;
      });

      setChartMinLimitInputs((current) => ({
        ...current,
        [vKey]: Number.isFinite(savedMin) ? String(savedMin) : "",
      }));

      setChartLimitInputs((current) => ({
        ...current,
        [vKey]: Number.isFinite(savedMax) ? String(savedMax) : "",
      }));

      setChartLimitMessages((current) => ({
        ...current,
        [vKey]: "Límites visuales guardados.",
      }));

      setTimeout(() => {
        setChartLimitMessages((current) => ({
          ...current,
          [vKey]: "",
        }));
      }, 2200);
    } catch (error) {
      setChartLimitMessages((current) => ({
        ...current,
        [vKey]:
          error?.response?.data?.error ||
          "No se pudieron guardar los límites visuales.",
      }));
    } finally {
      setChartLimitSaving((current) => ({
        ...current,
        [vKey]: false,
      }));
    }
  };

  const removeChartLimit = async (variable) => {
    if (!selectedDevice || isAllDevices) return;

    const vKey = canonVar(variable);
    setChartLimitSaving((current) => ({ ...current, [vKey]: true }));
    setChartLimitMessages((current) => ({ ...current, [vKey]: "" }));

    try {
      const url = withAs(
        `${API_ROOT}/chart-limits/${encodeURIComponent(
          selectedDevice
        )}/${encodeURIComponent(vKey)}`
      );

      await axios.delete(url, authHeader);

      setChartLimits((current) => {
        const next = { ...current };
        delete next[vKey];
        return next;
      });
      setChartMinLimits((current) => {
        const next = { ...current };
        delete next[vKey];
        return next;
      });
      setChartLimitInputs((current) => ({
        ...current,
        [vKey]: "",
      }));
      setChartMinLimitInputs((current) => ({
        ...current,
        [vKey]: "",
      }));
      setChartLimitMessages((current) => ({
        ...current,
        [vKey]: "Límites visuales eliminados.",
      }));

      setTimeout(() => {
        setChartLimitMessages((current) => ({
          ...current,
          [vKey]: "",
        }));
      }, 2200);
    } catch (error) {
      setChartLimitMessages((current) => ({
        ...current,
        [vKey]:
          error?.response?.data?.error ||
          "No se pudieron eliminar los límites visuales.",
      }));
    } finally {
      setChartLimitSaving((current) => ({
        ...current,
        [vKey]: false,
      }));
    }
  };

const updateVisibleChartStats = (variable, chartOverride = null) => {
  const vKey = canonVar(variable);
  const chart = chartOverride || chartsRef.current[vKey];

  if (!chart) return;

  const rawPoints = Array.isArray(chart.$rawSensorPoints)
    ? chart.$rawSensorPoints
    : [];

  const xScale = chart.scales?.x;
  const yScale = chart.scales?.y;

  const minX = Number(xScale?.min);
  const maxX = Number(xScale?.max);
  const minY = Number(yScale?.min);
  const maxY = Number(yScale?.max);

  const visiblePoints = rawPoints.filter((point) => {
    if (!Number.isFinite(point?.x) || !Number.isFinite(point?.y)) {
      return false;
    }

    const insideX =
      (!Number.isFinite(minX) || point.x >= minX) &&
      (!Number.isFinite(maxX) || point.x <= maxX);

    const insideY =
      (!Number.isFinite(minY) || point.y >= minY) &&
      (!Number.isFinite(maxY) || point.y <= maxY);

    return insideX && insideY;
  });

  setChartStats((prev) => ({
    ...prev,
    [vKey]: calcChartStats(visiblePoints),
  }));
};

const scheduleVisibleChartStats = (variable, chartOverride = null) => {
  window.requestAnimationFrame(() => {
    updateVisibleChartStats(variable, chartOverride);
  });
};

   const zoomInChart = (variable) => {
  const vKey = canonVar(variable);
  const chart = chartsRef.current[vKey];

  if (chart && typeof chart.zoom === "function") {
    chart.zoom(1.25);
    scheduleVisibleChartStats(vKey, chart);
  }
};

const zoomOutChart = (variable) => {
  const vKey = canonVar(variable);
  const chart = chartsRef.current[vKey];

  if (chart && typeof chart.zoom === "function") {
    chart.zoom(0.8);
    scheduleVisibleChartStats(vKey, chart);
  }
};

const resetChartZoom = (variable) => {
  const vKey = canonVar(variable);
  const chart = chartsRef.current[vKey];

  if (chart && typeof chart.resetZoom === "function") {
    chart.resetZoom();
    scheduleVisibleChartStats(vKey, chart);
  }
};

  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const allMapRef = useRef(null);
  const allMarkersRef = useRef([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [leftNavOpen, setLeftNavOpen] = useState(false);
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const menuRef = useRef(null);
  const username = user?.name || user?.username || "Usuario";
  const accountRoleLabel =
    user?.role === "admin"
      ? "Administrador"
      : user?.role === "supervisor"
      ? "Supervisor"
      : user?.role === "operador"
      ? "Operador"
      : "Usuario";

  // ===== CAMBIAR CONTRASEÑA =====
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState("");

     // ===== LOGO DE REPORTES PDF =====
  const [logoModalOpen, setLogoModalOpen] = useState(false);
  const [teamLogoInfo, setTeamLogoInfo] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [logoLoading, setLogoLoading] = useState(false);
  const [logoMsg, setLogoMsg] = useState("");
  const [teamLogoVersion, setTeamLogoVersion] = useState(0);

  const teamLogoValue = getTeamLogoValue(teamLogoInfo);

  const teamLogoSrc = useMemo(() => {
    const resolved = resolveTeamLogoUrl(teamLogoValue);
    if (!resolved) return "";

    // Evita que el navegador muestre una versión antigua después de reemplazar el logo.
    if (resolved.startsWith("data:")) return resolved;

    const separator = resolved.includes("?") ? "&" : "?";
    return `${resolved}${separator}v=${teamLogoVersion}`;
  }, [teamLogoValue, teamLogoVersion]);

  // Identidad del cliente (equipo) visible en el lateral. Nunca fija a ELICROM.
  // El logo se obtiene de /api/team/logo; ante ausencia se muestran iniciales.
  const clientDisplayName =
    teamLogoInfo?.team_name ||
    teamLogoInfo?.teamName ||
    user?.team_name ||
    user?.teamName ||
    user?.team?.name ||
    user?.username ||
    "Mi organización";
  const clientInitial = String(clientDisplayName).trim().charAt(0).toUpperCase() || "C";

  // cache KPIs
  const kpiCacheRef = useRef(new Map());
  const kpiSeq = useRef(0);
  const statusSeq = useRef(0);

  // ===== OBSERVACIONES OPERATIVAS =====
  const [observationsOpen, setObservationsOpen] = useState(false);

  // ===== ALERTAS / UMBRALES (NUEVO) =====
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [thrLoading, setThrLoading] = useState(false);
  const [thrSaving, setThrSaving] = useState({});
  const [thrMap, setThrMap] = useState({}); // { VAR: {min:'', max:''} }

  const [tgStatus, setTgStatus] = useState("not_linked"); // 'not_linked' | 'linked'
  const [tgLoading, setTgLoading] = useState(false);
  const [tgCode, setTgCode] = useState("");
  const [tgMsg, setTgMsg] = useState("");
  const [telegramAccounts, setTelegramAccounts] = useState([]);
  const [maxNotificationRecipients, setMaxNotificationRecipients] = useState(3); // Telegram
  const [maxEmailRecipients, setMaxEmailRecipients] = useState(3);

  const [prefLoading, setPrefLoading] = useState(false);
  const [prefSaving, setPrefSaving] = useState(false);
  const [telegramEnabled, setTelegramEnabled] = useState(true);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [alertEmails, setAlertEmails] = useState([]);
  const [newAlertEmail, setNewAlertEmail] = useState("");
  const [prefMsg, setPrefMsg] = useState("");

  const [testLoading, setTestLoading] = useState(false);
  const [testMsg, setTestMsg] = useState("");
  const lastTsOverall = useMemo(() => {
    const times = Object.values(kpis || {})
      .map((o) => o && (o.ts ?? o.fecha_ms ?? o.fecha ?? o.time))
      .map((v) => parseToMs(v))
      .filter(Number.isFinite);
    return times.length ? Math.max(...times) : null;
  }, [kpis]);

// ==========================================================
// ENERGÍA ELÉCTRICA - CANALES APRENDIDOS / VISIBLES
//
// CH1 y CH2 siempre se muestran.
// CH3 ... CH12 se aprenden cuando alguna vez registran corriente > 0.01 A.
// La lista queda guardada por sensor en localStorage, por lo que un canal
// no desaparece cuando temporalmente su corriente baja a 0 A.
// ==========================================================

const visibleElectricalChannels = useMemo(() => {
  if (activeType !== "EnergiaElectrica") {
    return [];
  }

  const detected = new Set([1, 2]);
  const storageKey = `cth01_detected_channels_${selectedDevice || "default"}`;

  try {
    const saved = JSON.parse(localStorage.getItem(storageKey) || "[]");

    if (Array.isArray(saved)) {
      saved.forEach((ch) => {
        const n = Number(ch);
        if (Number.isInteger(n) && n >= 1 && n <= 12) {
          detected.add(n);
        }
      });
    }
  } catch (_) {
    // Si localStorage no está disponible, el dashboard continúa normalmente.
  }

  for (let ch = 3; ch <= 12; ch++) {
    const current = getElectricalKpiNumber(kpis, `CURRENT_CH${ch}`);
    const activePower = getElectricalKpiNumber(kpis, `ACTIVE_POWER_CH${ch}`);
    const activeEnergy = getElectricalKpiNumber(kpis, `ACTIVE_ENERGY_CH${ch}`);

    const hasCurrent = current !== null && Math.abs(current) > 0.01;
    const hasPower = activePower !== null && Math.abs(activePower) > 0.001;

    // El acumulado de energía permite reconocer un canal que ya fue usado
    // aunque justo en este instante su corriente haya bajado a 0 A.
    const hasMeaningfulEnergy =
      activeEnergy !== null && Math.abs(activeEnergy) > 0.1;

    if (hasCurrent || hasPower || hasMeaningfulEnergy) {
      detected.add(ch);
    }
  }

  const channels = Array.from(detected).sort((a, b) => a - b);

  try {
    localStorage.setItem(storageKey, JSON.stringify(channels));
  } catch (_) {
    // No bloquear la interfaz si el navegador impide escribir localStorage.
  }

  return channels;
}, [kpis, activeType, selectedDevice]);


const visibleAvailableVars = useMemo(() => {
  return (availableVars || []).filter((v) => {
    const vv = canonVar(v);

    return (
      shouldShowExtVarOnly(v, kpis?.[vv]) &&
      shouldShowElectricalVariable(
        v,
        kpis,
        activeType,
        visibleElectricalChannels
      )
    );
  });
}, [
  availableVars,
  kpis,
  activeType,
  visibleElectricalChannels,
]);


const visibleKpiVars = useMemo(() => {
  return (kpiVars || []).filter((v) => {
    const vv = canonVar(v);

    if (vv === "BAT") {
      return false;
    }

    return (
      shouldShowExtVarOnly(vv, kpis?.[vv]) &&
      shouldShowElectricalVariable(
        vv,
        kpis,
        activeType,
        visibleElectricalChannels
      )
    );
  });
}, [
  kpiVars,
  kpis,
  activeType,
  visibleElectricalChannels,
]);


// ==========================================================
// KPI AGRUPADOS - ENERGÍA ELÉCTRICA
// ==========================================================

const ELECTRICAL_MAIN_KPI_VARS = new Set([
  "TEMP",
  "VOLTAGE_L1",
  "VOLTAGE_L2",
  "VOLTAGE_L3",
  ...Array.from({ length: 12 }, (_, i) => `CURRENT_CH${i + 1}`),
]);

const ELECTRICAL_GROUPED_KPI_VARS = new Set([
  "TEMP",
  "VOLTAGE_L1",
  "VOLTAGE_L2",
  "VOLTAGE_L3",
  "THDV_L1",
  "THDV_L2",
  "THDV_L3",

  ...Array.from({ length: 12 }, (_, i) => i + 1).flatMap((ch) => [
    `CURRENT_CH${ch}`,
    `POWER_FACTOR_CH${ch}`,
    `ACTIVE_POWER_CH${ch}`,
    `REACTIVE_POWER_CH${ch}`,
    `APPARENT_POWER_CH${ch}`,
    `ACTIVE_ENERGY_CH${ch}`,
    `REACTIVE_ENERGY_CH${ch}`,
    `APPARENT_ENERGY_CH${ch}`,
    `REVERSE_ACTIVE_ENERGY_CH${ch}`,
    `REVERSE_REACTIVE_ENERGY_CH${ch}`,
    `THDI_CH${ch}`,
  ]),
]);

function getElectricalMainValue(variable) {

  const vv = canonVar(variable);
  const row = kpis?.[vv] || null;

  const ts =
    row?.ts ??
    row?.fecha_ms ??
    row?.fecha ??
    row?.time ??
    null;

  const status = computeContextStatus(ts);

  const displayValue =
    row?.calibrated === true
      ? calibrationMode === "corrected"
        ? row.corrected_value ?? row.value
        : row.raw_value ?? row.value
      : row?.value;

  const unit =
    getVariableUnit(vv, activeType);

  const formatted =
    status === "offline" ||
    displayValue === null ||
    displayValue === undefined
      ? `--.-- ${unit}`.trim()
      : `${formatSensorNumber(
          displayValue,
          vv
        )} ${unit}`.trim();

  return {
    value: formatted,
    status,
    ts,
  };
}

const hasCalibratedKpis = useMemo(() => {
  return Object.values(kpis || {}).some((row) => row?.calibrated === true);
}, [kpis]);

// Cargar automáticamente el mismo logo del equipo que se usa en los reportes.
useEffect(() => {
  let alive = true;

  if (!token) {
    setTeamLogoInfo(null);
    return () => {
      alive = false;
    };
  }

  (async () => {
    try {
      const { data } = await axios.get(
        `${API_ROOT}/team/logo`,
        authHeader
      );

      if (!alive) return;

      setTeamLogoInfo(normalizeTeamLogoInfo(data));
      setTeamLogoVersion(Date.now());
    } catch (_) {
      if (!alive) return;

      // El logo es opcional: si falla la consulta, el dashboard sigue funcionando.
      setTeamLogoInfo(null);
    }
  })();

  return () => {
    alive = false;
  };
}, [token, authHeader]);

  useEffect(() => {
    function handleDocClick(e) {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target)) setMenuOpen(false);
    }
    if (menuOpen) document.addEventListener("mousedown", handleDocClick);
    return () => document.removeEventListener("mousedown", handleDocClick);
  }, [menuOpen]);

  /* === usuarios (admin) === */
useEffect(() => {
  if (!canManageUsers) {
    setUsersList([]);
    setViewAs("");
    return;
  }

  const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/sensores/admin/users`;
  fetch(url, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
    .then(setUsersList)
    .catch(() => setUsersList([]));
}, [token, canManageUsers]);
  /* === dispositivos por tipo ===
     En el Panel General NO se hace una consulta adicional a /my-sensors.
     /overview ya devuelve la lista completa y se convierte en la única fuente
     de datos para evitar una segunda carga y una segunda ejecución del resumen. */
  useEffect(() => {
    if (isGeneralDashboard) return;

    (async () => {
      try {
        const base = `${API_BASE}/devices?type=${encodeURIComponent(activeType)}`;
        const url = withAs(base);
        const { data } = await axios.get(url, authHeader);
        const arr = Array.isArray(data) ? data : [];
        setDevices(arr.filter((d) => resolveType(d.sensor_type) === activeType));
      } catch {
        setDevices([]);
      }
    })();
  }, [authHeader, activeType, viewAs, isGeneralDashboard]); // eslint-disable-line

  useEffect(() => {
    if (isGeneralDashboard) {
      setSelectedDevice(ALL_DEVICES_VALUE);
      return;
    }

    if (!devices.length) {
      setSelectedDevice("");
      setSensorInfo(null);
      setSensorData([]);
      setKpiVars([]);
      setAvailableVars([]);
      setKpis({});
      Object.values(chartsRef.current).forEach((ch) => ch?.destroy());
      chartsRef.current = {};
      return;
    }

    // Si venimos desde el Panel General, abrir directamente el sensor elegido.
    if (
      requestedSensorId &&
      devices.some((d) => String(d.id) === String(requestedSensorId))
    ) {
      if (String(selectedDevice) !== String(requestedSensorId)) {
        setSelectedDevice(String(requestedSensorId));
      }
      return;
    }

    if (
      !selectedDevice ||
      selectedDevice === ALL_DEVICES_VALUE ||
      !devices.some((d) => String(d.id) === String(selectedDevice))
    ) {
      setSelectedDevice(String(devices[0].id));
    }
  }, [devices, isGeneralDashboard, requestedSensorId]); // eslint-disable-line

  useEffect(() => {
    setSensorData([]);
    setKpis({});
    setAvailableVars([]);
    setKpiVars([]);
    setCalibrationMode("raw");
    Object.values(chartsRef.current).forEach((ch) => ch?.destroy());
    chartsRef.current = {};
  }, [activeType]);

    useEffect(() => {
    setCalibrationMode("raw");
    }, [selectedDevice]);
  /* === variables del dispositivo === */
  useEffect(() => {
    if (!selectedDevice || isAllDevices) {
      setAvailableVars([]);
      setKpiVars([]);
      Object.values(chartsRef.current).forEach((ch) => ch?.destroy());
      chartsRef.current = {};
      return;
    }
    (async () => {
      try {
        const url = withAs(
          `${API_BASE}/variables/${encodeURIComponent(selectedDevice)}`
        );
        const { data } = await axios.get(url, authHeader);
const ORDER = [
  "TOC",
  "PH",
  "turbidity",
  "EC_K1",
  "CLORO_LIBRE",

  "temperature",
  "TEMP",
  "TEMPERATURA",
  "TEMPERATURA_EXTERNA",

  "humidity",
  "HUM",
  "HUMEDAD",
  "HUMEDAD_EXTERNA",

  "ADC1",
  "ADC2",
  "TSS",
  "NIVELHIDROSTATICO",
  "LEVEL_CM",
  "NIVEL",
  "level",
  "PRESSURE",
  "WIND_SPEED",
  "WIND_DIRECTION",
  "RAINFALL",
  "FLUJO",
  "flow",
  "LAeq",
];
        const seen = new Map();
        for (const raw of data || []) {
          if (!raw) continue;
          const canon = canonVar(raw);
          if (!seen.has(canon)) seen.set(canon, canon);
        }
        const uniq = Array.from(seen.values());
        const sortedVariables = sortVariablesByContext(uniq, ORDER);

        const { vars: finalVars, kpis: finalKpis } = applyDeviceVarOverride(
          selectedDevice,
          sortedVariables
        );

        setAvailableVars(finalVars);
        setKpiVars(finalKpis);
      } catch {
        setAvailableVars([]);
        setKpiVars([]);
      }
    })();
  }, [selectedDevice, viewAs, isAllDevices]); // eslint-disable-line

  /* === Estado online === */
  useEffect(() => {
    setSensorStatus(null);
    setStatusTs(null);
    if (!selectedDevice || isAllDevices) return;

    const statusVar = (() => {
      const cfg = TYPE_CONFIG[activeType] || {};
      const prefer = cfg.kpi || cfg.variables || [];
      const avail = (availableVars || []).map((v) => String(v).toUpperCase());
      const pick = prefer.find((p) => avail.includes(p)) || avail[0] || null;
      return pick;
    })();
    if (!statusVar) return;

    const mySeq = ++statusSeq.current;
    const base = `${API_BASE}/ultimos?sensorId=${encodeURIComponent(
      selectedDevice
    )}&variable=${encodeURIComponent(statusVar)}&limit=1`;
    const url = withAs(base);

    (async () => {
      try {
        const { data } = await axios.get(url, authHeader);
        if (mySeq !== statusSeq.current) return;
        const row = Array.isArray(data) ? data[0] : null;
        const tsMs = toMsSafe(
          row?.fecha_ms ?? row?.ts ?? row?.time ?? row?.fecha ?? null
        );
        setSensorStatus(computeContextStatus(tsMs));
        setStatusTs(Number.isFinite(tsMs) ? tsMs : null);
      } catch {
        if (mySeq !== statusSeq.current) return;
        setSensorStatus("offline");
      }
    })();
  }, [
    selectedDevice,
    availableVars,
    activeType,
    viewAs,
    authHeader,
    isAllDevices,
    isSalesianaUser,
    isAmaguaUser,
  ]);

  // Cargar info + KPIs + series
 // Cargar info + KPIs + series
useEffect(() => {
  if (!selectedDevice || isAllDevices) return;

  const info =
    devices.find((d) => String(d.id) === String(selectedDevice)) || null;
  setSensorInfo(info);
  if (hasUsableCoordinates(info)) ensureMap(getDeviceLat(info), getDeviceLng(info));

  let alive = true;

const refreshKpis = async () => {
  if (!alive) return;
  if (!Array.isArray(kpiVars) || kpiVars.length === 0) return;

  const kpiLoadVars =
    activeType === "HumedadTemperatura"
      ? Array.from(new Set([...kpiVars, "BAT"]))
      : kpiVars;

  const cache = kpiCacheRef.current.get(selectedDevice);
  setKpis(cache || {});

  const mySeq = ++kpiSeq.current;

  try {
    const next = await loadKpisDynamic(selectedDevice, kpiLoadVars);

    if (!alive || mySeq !== kpiSeq.current) return;

    setKpis(next);
    kpiCacheRef.current.set(selectedDevice, next);
  } catch {
    if (!alive || mySeq !== kpiSeq.current) return;

    setKpis(
      cache || Object.fromEntries(kpiLoadVars.map((v) => [canonVar(v), null]))
    );
  }
};

  // 1) KPIs: carga inmediata
  refreshKpis();

  // 2) KPIs: auto-refresh cada 60s (o 40000 si quisieras 40s)
  const kpiIntervalId = setInterval(() => {
    if (!alive) return;
    if (document.visibilityState !== "visible") return;
    refreshKpis();
  }, 60000);

  // Series (charts): se cargan cuando cambias sensor/vars (NO cada minuto)
  if (visibleAvailableVars.length) {
  loadAllSeries(
    selectedDevice,
    visibleAvailableVars,
    visibleAvailableVars[0]
  );
} else {
  Object.values(chartsRef.current).forEach((ch) => ch?.destroy());
  chartsRef.current = {};
  setSensorData([]);
}

  // IMPORTANTISIMO: limpiar el intervalo al cambiar sensor o salir del componente
  return () => {
    alive = false;
    clearInterval(kpiIntervalId);
  };
}, [
  selectedDevice,
  availableVars,
  kpiVars,
  devices,
  calibrationMode,
  isAllDevices,
  isSalesianaUser,
  isAmaguaUser,
]); // eslint-disable-line

//---------------------------------------------

// Auto-refresh general de gráficas
useEffect(() => {
  if (!selectedDevice || isAllDevices || !visibleAvailableVars.length) return;

  let alive = true;

  const refreshCharts = () => {
    if (!alive) return;
    if (document.visibilityState !== "visible") return;

    loadAllSeries(
      selectedDevice,
      visibleAvailableVars,
      visibleAvailableVars[0]
    );
  };

  // Se revisan todas cada 3 minutos.
  // Cada gráfica mantiene su propio rango.
  const id = setInterval(refreshCharts, 180000);

  return () => {
    alive = false;
    clearInterval(id);
  };
}, [
  selectedDevice,
  visibleAvailableVars,
  chartRanges,
  chartCustomRanges,
  calibrationMode,
  isAllDevices,
  isSalesianaUser,
  isAmaguaUser,
]); // eslint-disable-line

// Estado de tapa (Milesight EM300) asociado al sensor seleccionado.
// Consulta inmediatamente y luego cada 10 s. Si la API falla temporalmente,
// conserva el último estado conocido para no ocultar una apertura activa.
useEffect(() => {
  if (!selectedDevice || isAllDevices) {
    setCoverState(null);
    return;
  }

  let alive = true;

  // Al cambiar de un sensor individual a otro, no arrastrar el estado anterior.
  setCoverState(null);

  const loadCoverState = async () => {
    try {
      const url = withAs(
        `${API_BASE}/accessory-state/${encodeURIComponent(selectedDevice)}`
      );

      const { data } = await axios.get(url, {
        ...authHeader,
        timeout: 10000,
      });

      if (!alive) return;

      setCoverState(data?.has_accessory ? data : null);
    } catch (error) {
      if (!alive) return;

      console.warn(
        "No se pudo consultar el estado de la tapa:",
        error?.response?.data?.error || error?.message || error
      );
      // No limpiar coverState aquí: un fallo de red no debe ocultar
      // una condición OPEN que ya estaba visible.
    }
  };

  loadCoverState();

  const coverIntervalId = setInterval(() => {
    if (!alive) return;
    if (document.visibilityState !== "visible") return;
    loadCoverState();
  }, 10000);

  return () => {
    alive = false;
    clearInterval(coverIntervalId);
  };
}, [selectedDevice, isAllDevices, viewAs, authHeader]); // eslint-disable-line

// En el Panel General, los cambios internos de `devices` NO deben volver a
// disparar /overview. Para vistas por aplicación sí necesitamos reaccionar
// cuando cambia la lista de sensores del tipo actual.
const overviewDevicesDependency = isGeneralDashboard ? null : devices;

// Vista general: resumen de todos los sensores del tipo actual
useEffect(() => {
  if (!isAllDevices) return;

  setSensorInfo(null);
  setSensorStatus(null);
  setStatusTs(null);
  setAvailableVars([]);
  setKpiVars([]);
  setKpis({});
  setSensorData([]);
  setChartStats({});

  Object.values(chartsRef.current).forEach((ch) => ch?.destroy());
  chartsRef.current = {};

  if (mapRef.current) {
    mapRef.current.remove();
    mapRef.current = null;
    markerRef.current = null;
  }

  let alive = true;

  const refreshOverview = async () => {
    if (!alive) return;

    setAllSensorsLoading(true);
    setAllSensorsMsg("");

    try {
      const overview = await loadAllSensorsOverview();
      if (!alive) return;
      setAllSensorsOverview(overview);

      // En /dashboard/General la respuesta de /overview también alimenta
      // `devices`. Así evitamos consultar /my-sensors solo para llenar la lista.
      if (isGeneralDashboard) {
        setDevices(
          (Array.isArray(overview) ? overview : []).map((sensor) => ({
            id: sensor.id,
            name: sensor.name,
            sensor_type: sensor.sensor_type,
            lat: sensor.lat,
            lng: sensor.lng,
            image_url: sensor.image_url,
            photo_url: sensor.photo_url,
            active: sensor.active,
          }))
        );
      }
    } catch (error) {
      if (!alive) return;
      setAllSensorsOverview([]);
      setAllSensorsMsg("No se pudo cargar el resumen general de sensores.");
    } finally {
      if (alive) setAllSensorsLoading(false);
    }
  };

  refreshOverview();

  const id = setInterval(() => {
    if (!alive) return;
    if (document.visibilityState !== "visible") return;
    refreshOverview();
  }, 60000);

  return () => {
    alive = false;
    clearInterval(id);
  };
}, [
  isAllDevices,
  overviewDevicesDependency,
  activeType,
  viewAs,
  authHeader,
  calibrationMode,
  isSalesianaUser,
  isAmaguaUser,
  isGeneralDashboard,
]); // eslint-disable-line


const generalTypeOptions = useMemo(() => {
  if (!isGeneralDashboard) return [];

  const types = Array.from(
    new Set(
      (allSensorsOverview || [])
        .map((sensor) => resolveType(sensor?.sensor_type))
        .filter((type) => type && type !== "General")
    )
  );

  return types.sort((a, b) =>
    getTypeDisplayName(a).localeCompare(getTypeDisplayName(b), "es")
  );
}, [allSensorsOverview, isGeneralDashboard]);

const filteredGeneralSensors = useMemo(() => {
  if (!isGeneralDashboard) return allSensorsOverview || [];

  const term = String(generalSearch || "").trim().toLowerCase();

  return (allSensorsOverview || []).filter((sensor) => {
    const sensorType = resolveType(sensor?.sensor_type);
    const matchesType =
      generalTypeFilter === "all" || sensorType === generalTypeFilter;

    const matchesStatus =
      generalStatusFilter === "all" || sensor?.status === generalStatusFilter;

    const searchable = [
      sensor?.name,
      sensor?.id,
      getTypeDisplayName(sensorType),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    const matchesSearch = !term || searchable.includes(term);

    return matchesType && matchesStatus && matchesSearch;
  });
}, [
  allSensorsOverview,
  isGeneralDashboard,
  generalSearch,
  generalTypeFilter,
  generalStatusFilter,
]);

const visibleGeneralSensors = useMemo(
  () =>
    isGeneralDashboard
      ? filteredGeneralSensors.slice(0, generalVisibleCount)
      : filteredGeneralSensors,
  [filteredGeneralSensors, generalVisibleCount, isGeneralDashboard]
);

const generalMissingCoordsCount = useMemo(
  () =>
    isGeneralDashboard
      ? filteredGeneralSensors.filter((sensor) => !hasUsableCoordinates(sensor)).length
      : 0,
  [filteredGeneralSensors, isGeneralDashboard]
);

useEffect(() => {
  if (!isGeneralDashboard) return;
  setGeneralVisibleCount(12);
}, [generalSearch, generalTypeFilter, generalStatusFilter, isGeneralDashboard]);

useEffect(() => {
  if (!isAllDevices) {
    if (allMapRef.current) {
      allMapRef.current.remove();
      allMapRef.current = null;
      allMarkersRef.current = [];
    }
    return;
  }

  const mapItems = isGeneralDashboard
    ? filteredGeneralSensors
    : allSensorsOverview;

  const raf = window.requestAnimationFrame(() => {
    ensureAllSensorsMap(mapItems);
  });

  return () => window.cancelAnimationFrame(raf);
}, [
  isAllDevices,
  allSensorsOverview,
  isGeneralDashboard,
  filteredGeneralSensors,
]); // eslint-disable-line

  async function loadKpisDynamic(deviceId, vars) {
    const calls = vars.map((v) => {
      const base = `${API_BASE}/ultimos?sensorId=${encodeURIComponent(
        deviceId
      )}&variable=${encodeURIComponent(canonVar(v))}&limit=1`;
      const url = withAs(base);
      return axios.get(url, authHeader);
    });
    const results = await Promise.allSettled(calls);
    const next = Object.fromEntries(vars.map((v) => [canonVar(v), null]));
    results.forEach((r, i) => {
      const varName = canonVar(vars[i]);
      if (r.status === "fulfilled") {
        const arr = Array.isArray(r.value.data) ? r.value.data : [];
        const row = arr[0];
        const tsRaw = row?.fecha_ms ?? row?.fecha ?? row?.time ?? null;
        const tsMs = parseToMs(tsRaw);
   
        if (row) {
          const sourceValue = Number(row.value ?? 0);
          const sourceRawValue =
            row.raw_value !== undefined && row.raw_value !== null
              ? Number(row.raw_value)
              : sourceValue;
          const sourceCorrectedValue =
            row.corrected_value !== undefined && row.corrected_value !== null
              ? Number(row.corrected_value)
              : sourceValue;

          const displayValueIsValid = isSensorDisplayValueValid(
            varName,
            sourceRawValue
          );

          next[varName] = {
            value: displayValueIsValid
              ? applySensorValueOffset(deviceId, varName, sourceValue)
              : null,

            raw_value: displayValueIsValid
              ? applySensorValueOffset(deviceId, varName, sourceRawValue)
              : null,

            corrected_value: displayValueIsValid
              ? applySensorValueOffset(deviceId, varName, sourceCorrectedValue)
              : null,

            // Se conserva el valor original para que el botón de prueba
            // de alarmas no altere la lógica de umbrales.
            alert_value: sourceValue,
            display_invalid: !displayValueIsValid,
            sensor_display_offset: getSensorValueOffset(deviceId, varName),

            correction:
              row.correction !== undefined && row.correction !== null
                ? Number(row.correction)
                : 0,

            calibration_error:
              row.calibration_error !== undefined &&
              row.calibration_error !== null
                ? Number(row.calibration_error)
                : 0,

            calibrated: row.calibrated === true,

            calibration_certificate_id:
              row.calibration_certificate_id ?? null,
            calibration_certificate_number:
              row.calibration_certificate_number ?? null,
            calibration_due_date: row.calibration_due_date ?? null,

            ts: Number.isFinite(tsMs) ? tsMs : null,
            fecha: row.fecha_ms ?? row.fecha,
          };
        } else {
          next[varName] = null;
        }
       

     }
    });
    return next;
  }
async function loadOverviewVariables(deviceId) {
  const url = withAs(
    `${API_BASE}/variables/${encodeURIComponent(deviceId)}`
  );

  const { data } = await axios.get(url, authHeader);
  const seen = new Map();

  for (const raw of data || []) {
    if (!raw) continue;
    const canon = canonVar(raw);
    if (!seen.has(canon)) seen.set(canon, canon);
  }

  const uniq = Array.from(seen.values());
  const sortedVariables = sortVariablesByContext(
    uniq,
    OVERVIEW_VARIABLE_ORDER
  );

  const { vars: finalVars } = applyDeviceVarOverride(
    deviceId,
    sortedVariables
  );
  return finalVars;
}

async function loadOverviewVariableStats(deviceId, variable) {
  const vKey = canonVar(variable);
  const now = Date.now();
  const since = now - OVERVIEW_RANGE_MS;
  const fromISO = new Date(since).toISOString();
  const toISO = new Date(now).toISOString();

  const base =
    `${API_BASE}/valores/${encodeURIComponent(deviceId)}` +
    `?variable=${encodeURIComponent(vKey)}` +
    `&from=${encodeURIComponent(fromISO)}` +
    `&to=${encodeURIComponent(toISO)}` +
    `&limit=20000`;

  const url = withAs(base);
  const { data } = await axios.get(url, authHeader);
  const rows = Array.isArray(data) ? data : [];

  const points = rows
    .map((r) => {
      const yValue =
        r?.calibrated === true
          ? calibrationMode === "corrected"
            ? r.corrected_value ?? r.value
            : r.raw_value ?? r.value
          : r.value ?? r.valor;

      const sourceRawValue =
        r.raw_value !== undefined && r.raw_value !== null
          ? r.raw_value
          : r.value ?? r.valor;

      const xValue = parseToMs(
        r.fecha_ms ??
          r.fecha ??
          r.ts ??
          r.time
      );

      const displayValueIsValid = isSensorDisplayValueValid(
        vKey,
        sourceRawValue
      );

      return {
        x: xValue,
        y: displayValueIsValid
          ? applySensorValueOffset(deviceId, vKey, yValue)
          : null,
      };
    })
    .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y))
    .sort((a, b) => a.x - b.x);

  const stats = calcChartStats(points);
  const lastTs = rows
    .map((r) =>
      parseToMs(r.fecha_ms ?? r.fecha ?? r.ts ?? r.time)
    )
    .filter(Number.isFinite)
    .reduce((max, ts) => (max === null ? ts : Math.max(max, ts)), null);

  return {
    variable_name: vKey,
    unit: UNITS_MAP[vKey] || "",
    last_value: Number.isFinite(stats?.last) ? stats.last : null,
    avg_value: Number.isFinite(stats?.mean) ? stats.mean : null,
    min_value: Number.isFinite(stats?.min) ? stats.min : null,
    max_value: Number.isFinite(stats?.max) ? stats.max : null,
    std_value: Number.isFinite(stats?.std) ? stats.std : null,
    count_data: stats?.count || 0,
    last_ts: lastTs,
    status: computeContextStatus(lastTs),
  };
}

async function loadGeneralOverviewFallback() {
  // Respaldo para que el Panel General nunca quede cargando indefinidamente.
  // Obtiene la lista autorizada y, si es posible, la última fecha por sensor.
  const sensorsUrl = withAs(`${API_BASE}/my-sensors`);
  const { data: sensorsData } = await axios.get(sensorsUrl, {
    ...authHeader,
    timeout: 10000,
  });

  const sensors = Array.isArray(sensorsData) ? sensorsData : [];
  const ids = sensors
    .map((sensor) => String(sensor?.device_id || "").trim())
    .filter(Boolean);

  let onlineMap = {};

  if (ids.length) {
    try {
      const { data } = await axios.get(
        `${API_BASE}/online?ids=${encodeURIComponent(ids.join(","))}&windowMin=15`,
        {
          ...authHeader,
          timeout: 10000,
        }
      );
      onlineMap = data && typeof data === "object" ? data : {};
    } catch (error) {
      console.warn("Fallback /online no disponible:", error?.message || error);
      onlineMap = {};
    }
  }

  return sensors
    .map((sensor) => {
      const deviceId = String(sensor?.device_id || "").trim();
      const statusInfo = onlineMap?.[deviceId] || {};
      const lastTs = toMsSafe(statusInfo?.last_ts ?? statusInfo?.last_ms ?? null);

      return {
        id: deviceId,
        name: sensor?.name || deviceId,
        sensor_type: sensor?.type || "CalidadAgua",
        lat: sensor?.lat,
        lng: sensor?.lon,
        image_url: sensor?.image_url,
        photo_url: sensor?.photo_url,
        active: sensor?.active,
        status: computeContextStatus(lastTs),
        last_ts: Number.isFinite(lastTs) ? lastTs : null,
        battery: null,
        variables: [],
        overview_fallback: true,
      };
    })
    .filter((sensor) => sensor.id)
    .sort((a, b) => String(a.name).localeCompare(String(b.name)));
}

async function loadGeneralOverviewFast() {
  const url = withAs(`${API_BASE}/overview`);

  let data;
  try {
    const response = await axios.get(url, {
      ...authHeader,
      timeout: 15000,
    });
    data = response.data;
  } catch (error) {
    console.warn(
      "El endpoint /overview tardó demasiado o falló. Se usa el modo de respaldo:",
      error?.message || error
    );
    return loadGeneralOverviewFallback();
  }

  const rows = Array.isArray(data) ? data : [];

  return rows
    .map((sensor) => {
      const deviceId = String(sensor?.device_id || sensor?.id || "").trim();
      const readings = Array.isArray(sensor?.readings) ? sensor.readings : [];

      const variables = readings
        .map((row) => {
          const vKey = canonVar(row?.variable_name);
          const rawValue =
            row?.value === null || row?.value === undefined || row?.value === ""
              ? NaN
              : Number(row.value);
          const tsMs = toMsSafe(row?.ts ?? row?.fecha ?? null);
          const validValue = isSensorDisplayValueValid(vKey, rawValue);

          return {
            variable_name: vKey,
            unit: UNITS_MAP[vKey] || "",
            last_value: validValue
              ? applySensorValueOffset(deviceId, vKey, rawValue)
              : null,
            last_ts: Number.isFinite(tsMs) ? tsMs : null,
            status: computeContextStatus(tsMs),
            display_invalid: !validValue,
          };
        })
        .filter((row) => row.variable_name);

      const lastTs = toMsSafe(sensor?.last_ts);
      const batteryRow = variables.find(
        (row) => normalizeVariableDisplayKey(row.variable_name) === "BAT"
      );

      return {
        id: deviceId,
        name: sensor?.name || deviceId,
        sensor_type: sensor?.type || sensor?.sensor_type || "CalidadAgua",
        lat: sensor?.lat,
        lng: sensor?.lon ?? sensor?.lng,
        image_url: sensor?.image_url,
        photo_url: sensor?.photo_url,
        active: sensor?.active,
        status: computeContextStatus(lastTs),
        last_ts: Number.isFinite(lastTs) ? lastTs : null,
        battery: Number.isFinite(batteryRow?.last_value)
          ? batteryRow.last_value
          : null,
        variables,
      };
    })
    .filter((sensor) => sensor.id)
    .sort((a, b) => String(a.name).localeCompare(String(b.name)));
}

async function loadAllSensorsOverview() {
  // El Panel General usa un endpoint agregado: una sola llamada para todos
  // los sensores y sus últimas variables. Las vistas por aplicación
  // conservan el comportamiento anterior con estadísticas de 24 h.
  if (isGeneralDashboard) {
    return loadGeneralOverviewFast();
  }

  const list = Array.isArray(devices) ? devices : [];

  const settled = await Promise.allSettled(
    list.map(async (device) => {
      const deviceId = String(device?.id || "");
      const lat = getDeviceLat(device);
      const lng = getDeviceLng(device);

      let vars = [];

      try {
        vars = await loadOverviewVariables(deviceId);
      } catch {
        vars = [];
      }

      const deviceType = resolveType(device?.sensor_type);
      const varsToLoad =
        deviceType === "HumedadTemperatura"
          ? Array.from(new Set([...vars, "BAT"]))
          : vars;

      const varResults = await Promise.allSettled(
        varsToLoad.map((v) => loadOverviewVariableStats(deviceId, v))
      );

      const variables = varResults
        .map((r) => (r.status === "fulfilled" ? r.value : null))
        .filter(Boolean);

      const lastTs = variables
        .map((v) => v.last_ts)
        .filter(Number.isFinite)
        .reduce((max, ts) => Math.max(max, ts), null);

      const batteryRow = variables.find((v) => canonVar(v.variable_name) === "BAT");
      const visibleVariables = variables.filter((v) => canonVar(v.variable_name) !== "BAT");

      return {
        id: deviceId,
        name: device?.name || deviceId,
        sensor_type: device?.sensor_type || activeType,
        lat,
        lng,
        image_url: device?.image_url,
        photo_url: device?.photo_url,
        status: computeContextStatus(lastTs),
        last_ts: lastTs,
        battery: Number.isFinite(batteryRow?.last_value) ? batteryRow.last_value : null,
        variables: visibleVariables,
      };
    })
  );

  return settled
    .map((r) => (r.status === "fulfilled" ? r.value : null))
    .filter(Boolean)
    .sort((a, b) => String(a.name).localeCompare(String(b.name)));
}

async function loadSingleSeries(
  deviceId,
  variable,
  selectedRange,
  explicitCustomRange = null
) {
  const vKey = canonVar(variable);
  const currentRange = selectedRange || getChartRange(vKey);
  const rangeWindow = resolveChartWindow(
    vKey,
    currentRange,
    explicitCustomRange
  );

  const fromISO = new Date(rangeWindow.fromMs).toISOString();
  const toISO = new Date(rangeWindow.toMs).toISOString();

  const base =
    `${API_BASE}/valores/${encodeURIComponent(deviceId)}` +
    `?variable=${encodeURIComponent(vKey)}` +
    `&from=${encodeURIComponent(fromISO)}` +
    `&to=${encodeURIComponent(toISO)}` +
    `&limit=50000`;

  const url = withAs(base);

  try {
    const { data } = await axios.get(url, authHeader);

    const rows = Array.isArray(data)
      ? data
      : [];

    const filtered = rows.filter((r) => {
      const t = parseToMs(
        r.fecha_ms ??
        r.fecha ??
        r.ts ??
        r.time
      );

      return Number.isFinite(t)
        ? t >= rangeWindow.fromMs && t <= rangeWindow.toMs
        : false;
    });

    drawChart(
      filtered,
      deviceId,
      vKey,
      currentRange,
      rangeWindow
    );

    if (
      vKey === canonVar(visibleAvailableVars[0])
    ) {
      setSensorData(filtered);
    }
  } catch {
    drawChart(
      [],
      deviceId,
      vKey,
      currentRange,
      rangeWindow
    );

    if (
      vKey === canonVar(visibleAvailableVars[0])
    ) {
      setSensorData([]);
    }
  }
}

async function loadAllSeries(deviceId, vars, firstVarForTable) {
  // Destruir las gráficas que ya no existen
  for (const key of Object.keys(chartsRef.current)) {
    const stillExists = vars.some(
      (v) => canonVar(v) === canonVar(key)
    );

    if (!stillExists) {
      chartsRef.current[key]?.destroy();
      delete chartsRef.current[key];
    }
  }

  const calls = vars.map(async (v) => {
    const vKey = canonVar(v);

    // Cada gráfica conserva su propio rango: 24h, 7d, 30d o personalizado.
    const currentRange = getChartRange(vKey);
    const rangeWindow = resolveChartWindow(vKey, currentRange);

    const fromISO = new Date(rangeWindow.fromMs).toISOString();
    const toISO = new Date(rangeWindow.toMs).toISOString();

    const base =
      `${API_BASE}/valores/${encodeURIComponent(deviceId)}` +
      `?variable=${encodeURIComponent(vKey)}` +
      `&from=${encodeURIComponent(fromISO)}` +
      `&to=${encodeURIComponent(toISO)}` +
      `&limit=50000`;

    const url = withAs(base);

    try {
      const { data } = await axios.get(url, authHeader);

      const rows = Array.isArray(data)
        ? data
        : [];

      const filtered = rows.filter((r) => {
        const t = parseToMs(
          r.fecha_ms ??
          r.fecha ??
          r.ts ??
          r.time
        );

        return Number.isFinite(t)
          ? t >= rangeWindow.fromMs && t <= rangeWindow.toMs
          : false;
      });

      drawChart(
        filtered,
        deviceId,
        vKey,
        currentRange,
        rangeWindow
      );

      if (vKey === canonVar(firstVarForTable)) {
        setSensorData(filtered);
      }
    } catch {
      drawChart(
        [],
        deviceId,
        vKey,
        currentRange,
        rangeWindow
      );

      if (vKey === canonVar(firstVarForTable)) {
        setSensorData([]);
      }
    }
  });

  await Promise.allSettled(calls);
}

 function drawChart(rows, deviceId, variable, currentRange, rangeWindow = null) {
    const vKey = canonVar(variable);
    const canvasId = idForVar(vKey);
    const canvas = document.getElementById(canvasId);
    if (!canvas) return;



    chartsRef.current[vKey]?.destroy();

    const ctx = canvas.getContext("2d");
    const grad = ctx.createLinearGradient(0, 0, 0, canvas.height);
    grad.addColorStop(0, "rgba(37, 99, 235, .22)");
    grad.addColorStop(1, "rgba(37, 99, 235, .02)");

     const sourcePoints = (rows || [])
  .map((r) => {
    const yValue =
      r?.calibrated === true
        ? calibrationMode === "corrected"
          ? r.corrected_value ?? r.value
          : r.raw_value ?? r.value
        : r.value ?? r.valor ?? 0;

    const sourceRawValue =
      r.raw_value !== undefined && r.raw_value !== null
        ? r.raw_value
        : r.value ?? r.valor;
    const sourceCorrectedValue =
      r.corrected_value !== undefined && r.corrected_value !== null
        ? r.corrected_value
        : r.value ?? r.valor;

    const displayValueIsValid = isSensorDisplayValueValid(
      vKey,
      sourceRawValue
    );

    return {
      x: parseToMs(r.fecha_ms ?? r.fecha ?? r.ts ?? r.time),
      y: displayValueIsValid
        ? applySensorValueOffset(deviceId, vKey, yValue)
        : null,
      raw_value: displayValueIsValid
        ? applySensorValueOffset(deviceId, vKey, sourceRawValue)
        : null,
      corrected_value: displayValueIsValid
        ? applySensorValueOffset(deviceId, vKey, sourceCorrectedValue)
        : null,
      correction: r.correction,
      calibrated: r.calibrated === true,
      certificate: r.calibration_certificate_number || null,
      display_invalid: !displayValueIsValid,
      sensor_display_offset: getSensorValueOffset(deviceId, vKey),
    };
  })
  .filter((p) => Number.isFinite(p.x))
  .sort((a, b) => a.x - b.x);

// Solo los puntos válidos participan en promedio, mínimo, máximo,
// desviación estándar, último valor y conteo de datos.
const rawPoints = sourcePoints.filter((p) => Number.isFinite(p.y));

const isDailyRainfallChart =
  activeType === "Meteorologica" && vKey === "RAINFALL";

const chartRawPoints = isDailyRainfallChart
  ? buildDailyRainfallPoints(rawPoints)
  : rawPoints;

const stats = calcChartStats(chartRawPoints);

// Los puntos inválidos se conservan como y=null para producir un hueco real
// en la línea y evitar que alteren automáticamente la escala del eje Y.
const effectiveDurationMs =
  Number.isFinite(rangeWindow?.durationMs)
    ? rangeWindow.durationMs
    : RANGE_TO_MS[currentRange] || RANGE_TO_MS["24h"];

const gapRangeKey =
  currentRange === "custom"
    ? rangePresetForDuration(effectiveDurationMs)
    : currentRange;

const points = isDailyRainfallChart
  ? chartRawPoints
  : insertOfflineGaps(
      sourcePoints,
      gapRangeKey,
      isSalesianaUser,
      isAmaguaUser
    );

// Los límites visuales configurados para la variable acumulada no se mezclan
// con la gráfica derivada de lluvia diaria.
const visualMaxDataset = isDailyRainfallChart
  ? null
  : buildVisualLimitDataset(
      vKey,
      chartLimitsRef.current[vKey],
      currentRange,
      rawPoints,
      "max",
      rangeWindow
    );

const visualMinDataset = isDailyRainfallChart
  ? null
  : buildVisualLimitDataset(
      vKey,
      chartMinLimitsRef.current[vKey],
      currentRange,
      rawPoints,
      "min",
      rangeWindow
    );

    setChartStats((prev) => ({
    ...prev,
    [vKey]: stats,
    }));

    const timeUnit =
      currentRange === "custom"
        ? timeUnitForDuration(effectiveDurationMs)
        : RANGE_TO_UNIT[currentRange] || "minute";


chartsRef.current[vKey] = new Chart(ctx, {
  type: isDailyRainfallChart ? "bar" : "line",
  data: {
    datasets: [
      {
      label: `${getChartVariableDisplayName(vKey, activeType)} — ${deviceId}${
      hasCalibratedKpis && !isDailyRainfallChart
      ? calibrationMode === "corrected"
      ? " · Corregido"
      : " · Original"
      : ""
      }`,

        data: points,
        borderColor: "#2563eb",
        backgroundColor: isDailyRainfallChart
          ? "rgba(37, 99, 235, .68)"
          : grad,
        pointRadius: isDailyRainfallChart ? 0 : chartRawPoints.length <= 2 ? 3 : 0,
        pointHoverRadius: isDailyRainfallChart ? 0 : 4,
        pointHitRadius: isDailyRainfallChart ? 0 : 14,
        borderWidth: isDailyRainfallChart ? 1 : 3,
        borderRadius: isDailyRainfallChart ? 7 : 0,
        maxBarThickness: isDailyRainfallChart ? 58 : undefined,
        tension: isDailyRainfallChart ? 0 : 0.35,
        fill: isDailyRainfallChart ? false : true,
        parsing: false,
        spanGaps: false,
        order: 1,
      },
      ...(visualMaxDataset ? [visualMaxDataset] : []),
      ...(visualMinDataset ? [visualMinDataset] : []),
    ],
  },
  options: {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 700, easing: "easeOutQuart" },
    plugins: {
  legend: {
    position: "top",
    labels: {
      boxWidth: 18,
      boxHeight: 3,
      usePointStyle: true,
      pointStyle: "line",
      font: { size: 14, weight: 700 },
      color: "#344a60",
      padding: 16,
    },
  },

  tooltip: {
    filter: (item) => item?.dataset?.isVisualLimit !== true,
    callbacks: {
      title: (items) => fmtDT(items?.[0]?.parsed?.x),
      label: (item) => {
        const u = getVariableUnit(vKey, activeType);
        const point = item.raw || {};
        const mainValue = `${formatSensorNumber(
          item.parsed.y,
          vKey
        )}${u ? ` ${u}` : ""}`;

        if (isDailyRainfallChart) {
          return `Lluvia registrada: ${mainValue}`;
        }

        if (!point.calibrated) {
          return mainValue;
        }

        if (calibrationMode === "corrected") {
          const rawTxt =
            point.raw_value !== undefined && point.raw_value !== null
              ? `Original: ${formatSensorNumber(
                  point.raw_value,
                  vKey
                )}${u ? ` ${u}` : ""}`
              : "Original: —";

          const corrTxt =
            point.correction !== undefined && point.correction !== null
              ? `Corrección: ${Number(point.correction).toFixed(4)}`
              : "Corrección: —";

          return [
            `Corregido: ${mainValue}`,
            rawTxt,
            corrTxt,
            `Certificado: ${point.certificate || "—"}`,
          ];
        }

        return [
          `Original: ${mainValue}`,
          `Certificado disponible: ${point.certificate || "—"}`,
        ];
      },
    },
    backgroundColor: "rgba(15,23,42,.94)",
    titleColor: "#e5e7eb",
    bodyColor: "#e5e7eb",
    titleFont: { size: 13, weight: 700 },
    bodyFont: { size: 13 },
    padding: 12,
    cornerRadius: 10,
    displayColors: false,
  },
        zoom: {
  limits: {
    x: {
      min: "original",
      max: "original",
    },
    y: {
      min: "original",
      max: "original",
    },
  },

  pan: {
    enabled: true,
    mode: "xy",
    modifierKey: "ctrl",
    onPanComplete: ({ chart }) => {
      updateVisibleChartStats(vKey, chart);
    },
  },

  zoom: {
    wheel: {
      enabled: false,
    },
    pinch: {
      enabled: true,
    },
    drag: {
      enabled: true,
      borderWidth: 1,
    },
    mode: "xy",
    onZoomComplete: ({ chart }) => {
      updateVisibleChartStats(vKey, chart);
    },
  },
},

},

    interaction: { mode: "index", intersect: false },
    scales: {
      x: {
        type: "time",
        min:
          currentRange === "custom" && Number.isFinite(rangeWindow?.fromMs)
            ? rangeWindow.fromMs
            : undefined,
        max:
          currentRange === "custom" && Number.isFinite(rangeWindow?.toMs)
            ? rangeWindow.toMs
            : undefined,
        adapters: { date: { locale: es } },
        time: { unit: isDailyRainfallChart ? "day" : timeUnit },
        grid: {
          color: "rgba(148,163,184,.14)",
          drawBorder: false,
        },
        ticks: {
          color: "#435a72",
          font: { size: 13.5, weight: 600 },
          autoSkip: true,
          maxTicksLimit: 8,
          maxRotation: 0,
          minRotation: 0,
          padding: 8,
        },
        border: {
          display: false,
        },
      },
      y: {
        beginAtZero: isDailyRainfallChart,
        grid: {
          color: "rgba(148,163,184,.14)",
          drawBorder: false,
        },
        ticks: {
          color: "#435a72",
          font: { size: 13.5, weight: 600 },
          maxTicksLimit: 8,
          padding: 8,
          callback: (v) => {
            const u = getVariableUnit(vKey, activeType);
            return `${formatSensorNumber(v, vKey)}${u ? ` ${u}` : ""}`;
          },
        },
        border: {
          display: false,
        },
      },
    },
  },
  plugins: [visualLimitLabelPlugin],

     });

    chartsRef.current[vKey].$currentRange = currentRange;
    chartsRef.current[vKey].$rangeWindow = rangeWindow;

    // Los puntos artificiales con y=null solo sirven para dibujar huecos.
    // Las estadísticas usan exclusivamente las lecturas reales.
    chartsRef.current[vKey].$rawSensorPoints = chartRawPoints;
  }

  function ensureMap(lat, lng) {
    const target = document.getElementById("sensorMap");
    if (!target) return;
    if (!mapRef.current) {
      mapRef.current = L.map(target, { scrollWheelZoom: false }).setView(
        [lat, lng],
        16
      );
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
        attribution: "&copy; OpenStreetMap",
      }).addTo(mapRef.current);
    } else {
      mapRef.current.setView([lat, lng], 16);
    }
    if (!markerRef.current)
      markerRef.current = L.marker([lat, lng]).addTo(mapRef.current);
    else markerRef.current.setLatLng([lat, lng]);
  }

  function ensureAllSensorsMap(items) {
    const target = document.getElementById("allSensorsMap");
    if (!target) return;

    const valid = (items || [])
      .filter(hasUsableCoordinates)
      .map((sensor) => ({
        ...sensor,
        lat: getDeviceLat(sensor),
        lng: getDeviceLng(sensor),
      }));

    if (!allMapRef.current) {
      allMapRef.current = L.map(target, { scrollWheelZoom: false }).setView(
        [-1.8312, -78.1834],
        6
      );

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
        attribution: "&copy; OpenStreetMap",
      }).addTo(allMapRef.current);
    }

    allMarkersRef.current.forEach((marker) => marker.remove());
    allMarkersRef.current = [];

    valid.forEach((sensor) => {
      const lastText = sensor.last_ts ? fmtDT(sensor.last_ts) : "Sin datos";
      const popup = `
        <strong>${escapeHtml(sensor.name || sensor.id)}</strong><br/>
        ID: ${escapeHtml(sensor.id)}<br/>
        Aplicación: ${escapeHtml(getTypeDisplayName(sensor.sensor_type))}<br/>
        Estado: ${escapeHtml(statusText(sensor.status))}<br/>
        Última lectura: ${escapeHtml(lastText)}
      `;

      const marker = L.marker([sensor.lat, sensor.lng])
        .addTo(allMapRef.current)
        .bindPopup(popup);

      allMarkersRef.current.push(marker);
    });

    setTimeout(() => allMapRef.current?.invalidateSize(), 80);

    if (valid.length === 0) {
      // Si el filtro actual no tiene sensores ubicados, regresamos a Ecuador.
      allMapRef.current.setView([-1.8312, -78.1834], 6);
    } else if (valid.length === 1) {
      allMapRef.current.setView([valid[0].lat, valid[0].lng], 15);
    } else {
      const bounds = L.latLngBounds(valid.map((sensor) => [sensor.lat, sensor.lng]));
      allMapRef.current.fitBounds(bounds, {
        padding: [35, 35],
        maxZoom: 13,
      });
    }
  }

// ======== ALERTAS: cargar/guardar thresholds + telegram ========
  const openAlerts = () => {
    if (!canUseAlarms) return;
    if (!selectedDevice || isAllDevices) return;
    setAlertsOpen(true);
    setTgMsg("");
    setTestMsg("");
  };
  const closeAlerts = () => {
    setAlertsOpen(false);
    setTgMsg("");
    setTgCode("");
    setTestMsg("");
  };

  const refreshTelegramAccounts = async () => {
    const statusUrl = withAs(`${API_ROOT}/telegram/status`);
    const { data } = await axios.get(statusUrl, authHeader);
    const accounts = Array.isArray(data?.accounts) ? data.accounts : [];
    setTelegramAccounts(accounts);
    setMaxNotificationRecipients(Number(data?.max) || 3);
    setTgStatus(accounts.length > 0 ? "linked" : "not_linked");
    return accounts;
  };

useEffect(() => {
  if (!alertsOpen) return;
  if (!selectedDevice || isAllDevices) return;

  (async () => {
    setThrLoading(true);
    setTestMsg("");
    setPrefMsg("");

    try {
      setPrefLoading(true);
      const prefUrl = withAs(`${API_ROOT}/telegram/preferences?sensor_id=${encodeURIComponent(selectedDevice)}`);
      const { data: prefData } = await axios.get(prefUrl, authHeader);

      setTelegramEnabled(prefData?.telegram_enabled !== false);
      setEmailEnabled(prefData?.email_enabled === true);
      setAlertEmails(
        Array.isArray(prefData?.alert_emails)
          ? prefData.alert_emails
          : prefData?.alert_email
          ? [prefData.alert_email]
          : []
      );
      setMaxNotificationRecipients(Number(prefData?.max_recipients) || 3);
      setMaxEmailRecipients(Number(prefData?.max_email_recipients) || 3);
      setNewAlertEmail("");

      try {
        await refreshTelegramAccounts();
      } catch (statusError) {
        setTelegramAccounts([]);
        setTgStatus("not_linked");
        setTgMsg("No se pudo leer la lista de cuentas de Telegram.");
      }
    } catch (e) {
      setTelegramEnabled(true);
      setEmailEnabled(false);
      setAlertEmails([]);
      setMaxEmailRecipients(3);
      setNewAlertEmail("");
      setPrefMsg("No se pudieron cargar las preferencias de notificación.");
    } finally {
      setPrefLoading(false);
    }

    try {
      const url = withAs(`${API_ROOT}/thresholds/${encodeURIComponent(selectedDevice)}`);
        const { data } = await axios.get(url, authHeader);
        const arr = Array.isArray(data) ? data : [];
        const next = {};
        for (const v of availableVars) {
          next[v] = { min: "", max: "" };
        }
        for (const row of arr) {
          const vv = canonVar(row?.variable);
          if (!vv) continue;
          if (!next[vv]) next[vv] = { min: "", max: "" };
          next[vv] = {
            min: row?.min_value ?? row?.min_value === 0 ? String(row.min_value) : "",
            max: row?.max_value ?? row?.max_value === 0 ? String(row.max_value) : "",
          };
        }
        setThrMap(next);
      } catch (e) {
        // si falla, igual mostramos inputs vacíos para el demo
        const fallback = {};
        for (const v of availableVars) fallback[v] = { min: "", max: "" };
        setThrMap(fallback);
      } finally {
        setThrLoading(false);
      }
    })();
  }, [alertsOpen, selectedDevice, availableVars, isAllDevices]); // eslint-disable-line

  const saveThreshold = async (variable) => {
    if (!selectedDevice || isAllDevices) return;
    const cur = thrMap[variable] || { min: "", max: "" };

    const payload = {
      sensor_id: selectedDevice,
      variable: canonVar(variable),
      min_value: parseNumOrNull(cur.min),
      max_value: parseNumOrNull(cur.max),
    };

    setThrSaving((m) => ({ ...m, [variable]: true }));
    setTestMsg("");
    try {
      const url = withAs(`${API_ROOT}/thresholds`);
      await axios.put(url, payload, authHeader);
      setTestMsg(`✅ Umbral guardado: ${getVariableDisplayName(variable)}`);
      setTimeout(() => setTestMsg(""), 1800);
    } catch (e) {
      setTestMsg(`❌ No se pudo guardar ${getVariableDisplayName(variable)}`);
    } finally {
      setThrSaving((m) => ({ ...m, [variable]: false }));
    }
  };

const getSensorInstallationPhotoSrc = (value) => {
  const raw = String(value || "").trim();

  if (!raw) return "";

  if (
    /^https?:\/\//i.test(raw) ||
    raw.startsWith("blob:") ||
    raw.startsWith("data:")
  ) {
    return raw;
  }

  const backendBase = String(
    process.env.REACT_APP_BACKEND_URL || ""
  ).replace(/\/+$/, "");

  const publicBase = backendBase.replace(/\/api$/i, "");

  const normalized = raw.startsWith("/")
    ? raw
    : `/${raw}`;

  return `${publicBase}${normalized}`;
};


const clearSensorEditorPhotoSelection = () => {
  if (
    sensorEditorPhotoPreviewUrl &&
    sensorEditorPhotoPreviewUrl.startsWith("blob:")
  ) {
    URL.revokeObjectURL(
      sensorEditorPhotoPreviewUrl
    );
  }

  setSensorEditorPhotoFile(null);
  setSensorEditorPhotoPreviewUrl("");

  if (sensorEditorPhotoInputRef.current) {
    sensorEditorPhotoInputRef.current.value = "";
  }
};


const loadSensorInstallationPhoto = async (deviceId) => {
  if (!deviceId) return;

  setSensorEditorPhotoLoading(true);

  try {
    const url = withAs(
      `${API_BASE}/${encodeURIComponent(
        deviceId
      )}/installation-photo`
    );

    const { data } = await axios.get(
      url,
      authHeader
    );

    setSensorEditorPhotoUrl(
      data?.installation_photo_url || ""
    );
  } catch (error) {
    console.error(
      "Error cargando foto de instalación:",
      error
    );

    setSensorEditorPhotoUrl("");

    setSensorEditorMsg(
      error?.response?.data?.error ||
        "No se pudo consultar la foto de instalación."
    );
  } finally {
    setSensorEditorPhotoLoading(false);
  }
};


const handleSensorEditorPhotoFile = (file) => {
  if (!file) return;

  const allowedTypes = [
    "image/jpeg",
    "image/png",
    "image/webp",
  ];

  const extensionOk =
    /\.(jpg|jpeg|png|webp)$/i.test(
      file.name || ""
    );

  if (
    !allowedTypes.includes(file.type) ||
    !extensionOk
  ) {
    setSensorEditorMsg(
      "Solo se permiten imágenes JPG, JPEG, PNG o WEBP."
    );

    return;
  }

  if (file.size > 5 * 1024 * 1024) {
    setSensorEditorMsg(
      "La fotografía debe pesar máximo 5 MB."
    );

    return;
  }

  if (
    sensorEditorPhotoPreviewUrl &&
    sensorEditorPhotoPreviewUrl.startsWith("blob:")
  ) {
    URL.revokeObjectURL(
      sensorEditorPhotoPreviewUrl
    );
  }

  const previewUrl =
    URL.createObjectURL(file);

  setSensorEditorPhotoFile(file);
  setSensorEditorPhotoPreviewUrl(previewUrl);
  setSensorEditorMsg("");
};

const openSensorEditor = () => {
  if (!sensorInfo || !selectedDevice || isAllDevices) return;

  const lat =
    sensorInfo?.lat ??
    sensorInfo?.latitude ??
    "";

  const lon =
    sensorInfo?.lng ??
    sensorInfo?.lon ??
    sensorInfo?.longitude ??
    "";

  const hasLocation =
    lat !== null &&
    lat !== undefined &&
    lat !== "" &&
    lon !== null &&
    lon !== undefined &&
    lon !== "";

  setSensorEditorName(sensorInfo?.name || "");
  setSensorEditorLocationSource(
    sensorInfo?.location_source ||
      (hasLocation ? "manual" : "none")
  );
setSensorEditorLat(
  hasLocation ? String(lat) : ""
);

setSensorEditorLon(
  hasLocation ? String(lon) : ""
);

clearSensorEditorPhotoSelection();
setSensorEditorPhotoUrl("");

setSensorEditorMsg("");
setSensorEditorOpen(true);

loadSensorInstallationPhoto(
  selectedDevice
);
};

const closeSensorEditor = () => {
  if (
    sensorEditorSaving ||
    sensorEditorPhotoDeleting
  ) {
    return;
  }

  clearSensorEditorPhotoSelection();

  setSensorEditorPhotoUrl("");
  setSensorEditorOpen(false);
  setSensorEditorMsg("");
};

const saveSensorProfile = async () => {
  if (!selectedDevice || isAllDevices) return;

  const cleanName = String(
    sensorEditorName || ""
  ).trim();

  if (!cleanName) {
    setSensorEditorMsg(
      "El nombre del sensor no puede estar vacío."
    );
    return;
  }

  const payload = {
    name: cleanName,
    location_source:
      sensorEditorLocationSource,
  };

  if (
    sensorEditorLocationSource === "manual"
  ) {
    const lat = Number(sensorEditorLat);
    const lon = Number(sensorEditorLon);

    if (
      !Number.isFinite(lat) ||
      !Number.isFinite(lon)
    ) {
      setSensorEditorMsg(
        "Ingresa una latitud y longitud válidas."
      );
      return;
    }

    payload.latitude = lat;
    payload.longitude = lon;
  }

  const hasNewPhoto =
    !!sensorEditorPhotoFile;

  setSensorEditorSaving(true);
  setSensorEditorMsg("");

  try {
    /* =========================================
       1. GUARDAR NOMBRE / UBICACIÓN
       ========================================= */

    const profileUrl = withAs(
      `${API_BASE}/${encodeURIComponent(
        selectedDevice
      )}/profile`
    );

    const { data } = await axios.put(
      profileUrl,
      payload,
      authHeader
    );

    const updated = data?.sensor;

    if (updated) {
      setSensorInfo((current) => ({
        ...(current || {}),
        name: updated.name,
        lat: updated.lat,
        lng: updated.lon,
        lon: updated.lon,
        location_source:
          updated.location_source,
      }));

      setDevices((current) =>
        current.map((device) =>
          String(device.id) ===
          String(selectedDevice)
            ? {
                ...device,
                name: updated.name,
                lat: updated.lat,
                lng: updated.lon,
                lon: updated.lon,
                location_source:
                  updated.location_source,
              }
            : device
        )
      );

      if (
        updated.lat !== null &&
        updated.lat !== undefined &&
        updated.lon !== null &&
        updated.lon !== undefined
      ) {
        ensureMap(
          updated.lat,
          updated.lon
        );
      }
    }

    /* =========================================
       2. SUBIR FOTO NUEVA SI FUE SELECCIONADA
       ========================================= */

    if (hasNewPhoto) {
      const formData = new FormData();

      formData.append(
        "installation_photo",
        sensorEditorPhotoFile
      );

      const photoUrl = withAs(
        `${API_BASE}/${encodeURIComponent(
          selectedDevice
        )}/installation-photo`
      );

      try {
        const photoResponse =
          await axios.post(
            photoUrl,
            formData,
            authHeader
          );

        const uploadedUrl =
          photoResponse?.data
            ?.installation_photo_url || "";

        setSensorEditorPhotoUrl(
          uploadedUrl
        );

        clearSensorEditorPhotoSelection();
      } catch (photoError) {
        setSensorEditorMsg(
          photoError?.response?.data?.error ||
            photoError?.response?.data
              ?.message ||
            "Los datos del sensor se guardaron, pero no se pudo subir la fotografía."
        );

        return;
      }
    }

    setSensorEditorMsg(
      hasNewPhoto
        ? "Sensor y foto actualizados correctamente."
        : "Sensor actualizado correctamente."
    );

    setTimeout(() => {
      setSensorEditorOpen(false);
      setSensorEditorMsg("");
    }, 700);
  } catch (error) {
    setSensorEditorMsg(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "No se pudo actualizar el sensor."
    );
  } finally {
    setSensorEditorSaving(false);
  }
};



const handleClaimSensor = async () => {
  if (!claimDeviceId.trim() || !claimPairingCode.trim()) {
    setClaimMsg("Debes ingresar el ID del sensor y el código de vinculación.");
    return;
  }

  setClaimLoading(true);
  setClaimMsg("");

  try {
    const url = withAs(`${API_ROOT}/sensores/claim`);
    const { data } = await axios.post(
  url,
  {
    device_id: claimDeviceId.trim(),
    pairing_code: claimPairingCode.trim(),
    expected_type: activeType,
  },
  authHeader
);

    setClaimMsg(data?.message || "Sensor agregado correctamente.");
    setClaimDeviceId("");
    setClaimPairingCode("");

    const refreshUrl = withAs(`${API_BASE}/devices?type=${encodeURIComponent(activeType)}`);
    const refreshed = await axios.get(refreshUrl, authHeader);
    const arr = Array.isArray(refreshed.data) ? refreshed.data : [];
    setDevices(arr.filter((d) => resolveType(d.sensor_type) === activeType));

    setTimeout(() => {
      setClaimOpen(false);
      setClaimMsg("");
    }, 1200);
  } catch (error) {
    setClaimMsg(
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      "No se pudo vincular el sensor."
    );
  } finally {
    setClaimLoading(false);
  }
};

  const generateTelegramCode = async () => {
    setTgLoading(true);
    setTgMsg("");

    try {
      const url = withAs(`${API_ROOT}/telegram/link-code`);
      const { data } = await axios.post(url, {}, authHeader);
      const code = data?.code ? String(data.code) : "";
      setTgCode(code);
      setTgMsg(code ? "Listo: envía ese código al bot en Telegram." : "No llegó código.");
    } catch (e) {
      setTgMsg(
        e?.response?.data?.error ||
        "❌ No pude generar el código de Telegram."
      );
    } finally {
      setTgLoading(false);
    }
  };

  const addAlertEmail = () => {
    setPrefMsg("");
    const cleanEmail = String(newAlertEmail || "").trim().toLowerCase();

    if (!cleanEmail) {
      setPrefMsg("Ingresa un correo electrónico.");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setPrefMsg("El correo ingresado no tiene un formato válido.");
      return;
    }

    if (alertEmails.some((email) => String(email).toLowerCase() === cleanEmail)) {
      setPrefMsg("Ese correo ya está registrado.");
      return;
    }

    if (alertEmails.length >= maxEmailRecipients) {
      setPrefMsg(`Solo se permiten hasta ${maxEmailRecipients} correos.`);
      return;
    }

    setAlertEmails((current) => [...current, cleanEmail]);
    setNewAlertEmail("");
    setPrefMsg("Correo agregado a la lista. Presiona ‘Guardar preferencias’ para aplicarlo.");
  };

  const removeAlertEmail = (emailToRemove) => {
    setAlertEmails((current) => current.filter((email) => email !== emailToRemove));
    setPrefMsg("Correo retirado de la lista. Presiona ‘Guardar preferencias’ para aplicar el cambio.");
  };

  const saveNotificationPreferences = async () => {
    setPrefMsg("");

    const normalizedEmails = [...new Set(
      (alertEmails || [])
        .map((email) => String(email || "").trim().toLowerCase())
        .filter(Boolean)
    )];

    if (normalizedEmails.length > maxEmailRecipients) {
      setPrefMsg(`Solo se permiten hasta ${maxEmailRecipients} correos.`);
      return;
    }

    const invalidEmail = normalizedEmails.find(
      (email) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    );

    if (invalidEmail) {
      setPrefMsg(`El correo ${invalidEmail} no tiene un formato válido.`);
      return;
    }

    if (emailEnabled && normalizedEmails.length === 0) {
      setPrefMsg("Debes registrar al menos un correo para activar las alertas por email.");
      return;
    }

    setPrefSaving(true);

    try {
      const url = withAs(`${API_ROOT}/telegram/preferences?sensor_id=${encodeURIComponent(selectedDevice)}`);
      const { data } = await axios.put(
        url,
        {
          sensor_id: selectedDevice,
          telegram_enabled: telegramEnabled,
          email_enabled: emailEnabled,
          alert_emails: normalizedEmails,
        },
        authHeader
      );

      setTelegramEnabled(data?.preferences?.telegram_enabled !== false);
      setEmailEnabled(data?.preferences?.email_enabled === true);
      setAlertEmails(
        Array.isArray(data?.preferences?.alert_emails)
          ? data.preferences.alert_emails
          : []
      );
      setMaxNotificationRecipients(
        Number(data?.preferences?.max_recipients) || 3
      );
      setPrefMsg("✅ Preferencias guardadas correctamente.");
      setTimeout(() => setPrefMsg(""), 2200);
    } catch (e) {
      setPrefMsg(
        e?.response?.data?.error ||
        "No se pudieron guardar las preferencias de notificación."
      );
    } finally {
      setPrefSaving(false);
    }
  };

  const confirmTelegram = async () => {
    setTgLoading(true);
    setTgMsg("");

    try {
      const url = withAs(`${API_ROOT}/telegram/confirm`);
      const { data } = await axios.post(url, { code: tgCode }, authHeader);

      if (data?.ok) {
        await refreshTelegramAccounts();
        setTgCode("");
        setTgMsg(
          data?.message ||
          (data?.already_connected
            ? "✅ Esta cuenta de Telegram ya estaba conectada."
            : "✅ Cuenta de Telegram conectada correctamente.")
        );
      } else {
        setTgMsg("❌ No confirmé. Envía el código al bot y reintenta.");
      }
    } catch (e) {
      setTgMsg(
        e?.response?.data?.error ||
        "❌ No confirmé. Envía el código al bot y reintenta."
      );
    } finally {
      setTgLoading(false);
    }
  };

  const removeTelegramAccount = async (accountId) => {
    if (!accountId) return;

    setTgLoading(true);
    setTgMsg("");

    try {
      const url = withAs(`${API_ROOT}/telegram/accounts/${encodeURIComponent(accountId)}`);
      await axios.delete(url, authHeader);
      await refreshTelegramAccounts();
      setTgMsg("✅ Cuenta de Telegram eliminada.");
      setTimeout(() => setTgMsg(""), 2200);
    } catch (e) {
      setTgMsg(
        e?.response?.data?.error ||
        "No se pudo eliminar la cuenta de Telegram."
      );
    } finally {
      setTgLoading(false);
    }
  };

  const testAlertNow = async () => {
    if (!selectedDevice || isAllDevices) return;

    setTestLoading(true);
    setTestMsg("");

    try {
      // Se usa un KPI solo como referencia para el contenido del mensaje.
      // /alerts/test NO crea ni modifica alarmas reales.
      const sample = (kpiVars || [])
        .map((v) => {
          const vv = canonVar(v);
          const val = kpis?.[vv]?.alert_value ?? kpis?.[vv]?.value;

          if (val === null || val === undefined) return null;

          return {
            variable_name: vv,
            value: Number(val),
          };
        })
        .filter(Boolean)[0];

      const url = withAs(`${API_ROOT}/alerts/test`);

      const { data } = await axios.post(
        url,
        {
          sensor_id: selectedDevice,
          variable_name: sample?.variable_name || "PRUEBA",
          value: sample?.value ?? 0,
        },
        authHeader
      );

      const email = data?.email || {};
      const telegram = data?.telegram || {};

      const describeChannel = (label, channel) => {
        if (!channel?.enabled) return `${label} desactivado`;
        if (channel?.available === false) return `${label} no disponible`;
        return `${label} ${channel?.sent || 0}/${channel?.targets || 0}`;
      };

      const channelText = [
        describeChannel("Correo", email),
        describeChannel("Telegram", telegram),
      ].join(" · ");

      if (data?.reason === "NO_RECIPIENTS") {
        setTestMsg(`⚠️ No hay destinatarios activos. ${channelText}`);
        return;
      }

      if ((data?.failed || 0) > 0) {
        setTestMsg(
          `⚠️ Prueba parcialmente enviada: ${channelText}. Fallos: ${data.failed}.`
        );
        return;
      }

      if ((data?.sent || 0) === 0) {
        setTestMsg(`⚠️ No se envió ninguna notificación. ${channelText}`);
        return;
      }

      setTestMsg(`✅ Prueba enviada inmediatamente: ${channelText}.`);
    } catch (e) {
      setTestMsg(
        `❌ ${
          e?.response?.data?.error ||
          "No se pudo enviar la prueba de alerta."
        }`
      );
    } finally {
      setTestLoading(false);
    }
  };



  /* ===== estilos embebidos ===== */
  const styles = (
    <style>{`
      /* (tus estilos ya existentes) */
      .toolbar-row{display:flex;gap:12px;align-items:center;flex-wrap:wrap;background:#eaf2ff;border:1px solid #dbeafe;border-radius:14px;padding:12px 14px;box-shadow:0 4px 14px rgba(2,6,23,.08)}
      .btn-solid{display:inline-flex;align-items:center;gap:8px;border:none;border-radius:10px;padding:10px 14px;font-weight:800;color:#fff;cursor:pointer;transition:.2s transform,.2s opacity}
      .btn-solid:hover{transform:translateY(-1px);opacity:.95}
      .btn-download{background:#2563eb}
      .btn-alerts{background:#0ea5e9}
      .btn-installation{background:#7c3aed}
      .btn-tracking{background:#0f766e}
      .btn-logout{background:#ef4444}
      .btn-home{background:#0284c7}
      .btn-circle{border-radius:9999px;padding:10px 12px}
      .page-title-centered{display:flex;justify-content:center;margin:8px 0 12px}
      .page-title-centered h1{margin:0;font-weight:900;color:#0284c7;font-size:clamp(28px,3vw,44px)}
      .icon{width:18px;height:18px}
      .action-float{position:fixed;top:14px;z-index:1000}
      .action-float.left{left:16px}
      .action-float.right{right:16px}
      .user-menu-wrap{position:relative}
      .btn-menu{background:#ef4444}
      .menu-panel{position:absolute;top:44px;right:0;min-width:220px;background:#fff;color:#0f172a;border:1px solid #e5e7eb;border-radius:12px;box-shadow:0 12px 28px rgba(2,6,23,.14);padding:8px;z-index:1100}
      .menu-item{display:flex;align-items:center;gap:10px;padding:10px 12px;border-radius:10px;font-weight:700;cursor:default}
      .menu-item + .menu-item{margin-top:4px}
      .menu-item.clickable{cursor:pointer}
      .menu-item.clickable:hover{background:#f1f5f9}
      .menu-username{font-weight:800;color:#0f172a}
      .menu-divider{height:1px;background:#e5e7eb;margin:6px 0}

      /* ===== KPI: máximo 4 por fila ===== */
      .kpis{
        display:grid !important;
        grid-template-columns:repeat(4,minmax(0,1fr)) !important;
        gap:16px !important;
        align-items:stretch;
      }

      .kpis .kpi{
        width:auto !important;
        min-width:0;
        margin:0 !important;
      }

      @media (max-width:1100px){
        .kpis{
          grid-template-columns:repeat(2,minmax(0,1fr)) !important;
        }
      }

      @media (max-width:600px){
        .kpis{
          grid-template-columns:1fr !important;
        }
      } 

     /* ===== tabs de rango ===== */
      .range-tabs{display:inline-flex;gap:8px;align-items:center;background:#ffffffcc;border:1px solid #dbeafe;border-radius:999px;padding:6px 8px}
      .range-btn{border:none;border-radius:999px;padding:6px 10px;font-weight:800;color:#0f172a;background:transparent;cursor:pointer}
      .range-btn.active{background:#2563eb;color:#fff}
       
       .calibration-switch-wrap{
  display:flex;
  justify-content:flex-end;
  align-items:center;
  gap:10px;
  margin:0 0 12px;
  flex-wrap:wrap;
}

.calibration-switch-label{
  font-size:13px;
  font-weight:900;
  color:#334155;
}

.calibration-switch{
  display:inline-flex;
  gap:6px;
  align-items:center;
  background:#ffffffcc;
  border:1px solid #dbeafe;
  border-radius:999px;
  padding:5px 6px;
  box-shadow:0 4px 12px rgba(2,6,23,.06);
}

.calibration-switch button{
  border:none;
  border-radius:999px;
  padding:6px 11px;
  font-size:12px;
  font-weight:900;
  color:#0f172a;
  background:transparent;
  cursor:pointer;
}

.calibration-switch button.active{
  background:#2563eb;
  color:#fff;
}

.kpi-calibration-note{
  margin-top:6px;
  font-size:11px;
  font-weight:800;
  color:#2563eb;
}

.kpi-calibration-note.raw{
  color:#64748b;
}

      /* ===== Encabezado de cada gráfica ===== */
      .chart-header{
        display:flex;
        align-items:center;
        justify-content:space-between;
        gap:12px;
        flex-wrap:wrap;
        margin-bottom:8px;
      }

      .chart-header .chart-title{
        margin:0;
        min-width:0;
        max-width:560px;
        white-space:normal;
        overflow-wrap:anywhere;
      }

      .variable-display-name{
        white-space:normal;
        overflow-wrap:anywhere;
      }

      .chart-range-tabs{
        padding:4px 6px;
        gap:4px;
      }

      .chart-range-tabs .range-btn{

        padding:5px 9px;
        font-size:12px;
      }

      .chart-limit-bar{
        display:flex;
        align-items:center;
        gap:8px;
        flex-wrap:wrap;
        margin:6px 0 10px;
        padding:8px 10px;
        border:1px solid #fecaca;
        border-radius:12px;
        background:rgba(254,242,242,.78);
      }

      .chart-limit-label{
        font-size:12px;
        font-weight:900;
        color:#991b1b;
      }

      .chart-limit-input{
        width:130px;
        min-width:110px;
        border:1px solid #fca5a5;
        border-radius:9px;
        padding:7px 9px;
        font-weight:800;
        color:#0f172a;
        background:#fff;
        outline:none;
      }

      .chart-limit-input:focus{
        border-color:#dc2626;
        box-shadow:0 0 0 3px rgba(220,38,38,.12);
      }

      .chart-limit-unit{
        font-size:12px;
        font-weight:800;
        color:#7f1d1d;
      }

      .chart-limit-btn{
        border:1px solid #dc2626;
        border-radius:9px;
        padding:7px 10px;
        font-size:12px;
        font-weight:900;
        cursor:pointer;
      }

      .chart-limit-btn.save{
        background:#dc2626;
        color:#fff;
      }

      .chart-limit-btn.remove{
        background:#fff;
        color:#b91c1c;
      }

      .chart-limit-btn:disabled{
        opacity:.55;
        cursor:not-allowed;
      }

      .chart-limit-message{
        flex-basis:100%;
        font-size:12px;
        font-weight:800;
        color:#7f1d1d;
      }

         .chart-stats-panel{
        display:grid;
        grid-template-columns:repeat(6,minmax(0,1fr));
        gap:8px;
        margin:6px 0 12px;
      }

      .chart-stats-panel div{
        background:rgba(255,255,255,.78);
        border:1px solid #dbeafe;
        border-radius:12px;
        padding:8px 10px;
      }

      .chart-stats-panel span{
        display:block;
        font-size:11px;
        font-weight:800;
        color:#64748b;
        margin-bottom:3px;
      }

      .chart-stats-panel strong{
        display:block;
        font-size:13px;
        font-weight:900;
        color:#0f172a;
      }

      @media(max-width:900px){
        .chart-stats-panel{
          grid-template-columns:repeat(2,minmax(0,1fr));
        }
      }
      /* ===== Modales / Alertas ===== */
      .modal-overlay{
        position:fixed;
        inset:0;
        background:rgba(2,6,23,.55);
        display:flex;
        align-items:center;
        justify-content:center;
        z-index:2000;
        padding:16px;
        overflow:hidden;
      }

      .modal-card{
        width:min(900px,96vw);
        max-height:calc(100vh - 32px);
        max-height:calc(100dvh - 32px);
        background:#fff;
        border:1px solid #e5e7eb;
        border-radius:18px;
        box-shadow:0 24px 60px rgba(2,6,23,.22);
        overflow:hidden;
        display:flex;
        flex-direction:column;
      }

      .modal-head{
        display:flex;
        align-items:center;
        justify-content:space-between;
        padding:14px 16px;
        background:#f8fafc;
        border-bottom:1px solid #e5e7eb;
        flex:0 0 auto;
        position:relative;
        z-index:2;
      }

      .modal-title{font-weight:900;color:#0f172a;font-size:18px}

      .modal-body{
        padding:16px;
        display:grid;
        grid-template-columns:1fr;
        gap:14px;
        min-height:0;
        overflow-y:auto;
        overflow-x:hidden;
        overscroll-behavior:contain;
      }

      /* El modal de alertas ocupa una altura controlada y siempre muestra scroll interno. */
      .alerts-modal-card{
        height:calc(100vh - 32px);
        height:calc(100dvh - 32px);
        max-height:900px;
      }

      .alerts-modal-body{
        flex:1 1 auto;
        min-height:0;
        overflow-y:scroll;
        scrollbar-gutter:stable;
      }

      .alerts-modal-body::-webkit-scrollbar{width:10px}
      .alerts-modal-body::-webkit-scrollbar-track{background:#f1f5f9}
      .alerts-modal-body::-webkit-scrollbar-thumb{background:#94a3b8;border-radius:999px;border:2px solid #f1f5f9}
      .alerts-modal-body::-webkit-scrollbar-thumb:hover{background:#64748b}
      .modal-section{border:1px solid #e5e7eb;border-radius:14px;padding:12px;background:#fff}
      .section-title{font-weight:900;color:#0f172a;margin-bottom:10px;display:flex;align-items:center;gap:8px}
      .row{display:flex;gap:10px;flex-wrap:wrap;align-items:center}
      .input{border:1px solid #d1d5db;border-radius:10px;padding:10px 12px;font-weight:700;outline:none;min-width:120px}
      .input:focus{border-color:#60a5fa;box-shadow:0 0 0 3px rgba(59,130,246,.15)}
      .notify-row{display:flex;align-items:center;gap:10px;font-weight:900;color:#0f172a}
      .notify-row input{width:18px;height:18px;accent-color:#2563eb}
      .email-input{min-width:min(360px,100%);flex:1}
      .btn-ghost{border:1px solid #d1d5db;background:#fff;color:#0f172a}
      .btn-ghost:hover{background:#f1f5f9}
      .pill{display:inline-flex;align-items:center;gap:8px;padding:8px 10px;border-radius:999px;background:#eef2ff;border:1px solid #c7d2fe;font-weight:800;color:#1e40af}
      .msg{font-weight:800;color:#0f172a;opacity:.85}
      .small{font-size:13px;color:#475569;font-weight:700}
      .grid-thr{display:grid;grid-template-columns:minmax(240px,1.5fr) 1fr 1fr 120px;gap:10px;align-items:center}
      @media (max-width: 700px){.grid-thr{grid-template-columns:1fr 1fr;}.grid-thr .col-wide{grid-column:1 / -1;}}
       .hero-card{
  position:relative;
}

.battery-badge{
  position:absolute;
  top:14px;
  right:16px;
  display:inline-flex;
  align-items:center;
  gap:6px;
  padding:6px 10px;
  border-radius:999px;
  background:rgba(255,255,255,.86);
  border:1px solid #dbeafe;
  color:#0f172a;
  font-size:13px;
  font-weight:900;
  box-shadow:0 6px 16px rgba(2,6,23,.08);
  z-index:2;
}

.battery-icon{
  font-size:14px;
  line-height:1;
}

.logo-preview-box{
  width:100%;
  min-height:120px;
  border:1px dashed #cbd5e1;
  border-radius:14px;
  display:flex;
  align-items:center;
  justify-content:center;
  background:#f8fafc;
  padding:14px;
  text-align:center;
}

.logo-preview-box img{
  max-width:260px;
  max-height:120px;
  object-fit:contain;
}

.logo-actions{
  display:flex;
  gap:10px;
  flex-wrap:wrap;
  align-items:center;
}

.logo-file-input{
  border:1px solid #d1d5db;
  border-radius:10px;
  padding:10px;
  background:#fff;
  font-weight:700;
  width:100%;
}
   

.all-overview-wrap{
  display:grid;
  gap:16px;
  margin-top:16px;
}

.all-summary-grid{
  display:grid;
  grid-template-columns:repeat(5,minmax(0,1fr));
  gap:14px;
}

.all-summary-card{
  padding:16px;
}

.all-summary-card span{
  display:block;
  color:#64748b;
  font-size:12px;
  font-weight:900;
  text-transform:uppercase;
  letter-spacing:.04em;
}

.all-summary-card strong{
  display:block;
  margin-top:5px;
  color:#0f172a;
  font-size:26px;
  font-weight:950;
}

.all-summary-card strong.all-summary-date{
  font-size:14px;
  line-height:1.25;
  font-weight:900;
}

.general-filter-row{
  display:grid;
  grid-template-columns:minmax(260px,1.5fr) minmax(190px,.8fr) minmax(180px,.7fr);
  gap:12px;
  flex:1 1 760px;
  min-width:0;
}

.general-search-wrap{
  position:relative;
  min-width:0;
}

.general-search-icon{
  position:absolute;
  left:14px;
  top:50%;
  transform:translateY(-50%);
  width:18px;
  height:18px;
  color:#64748b;
  pointer-events:none;
}

.general-search-input{
  width:100%;
  height:42px;
  padding:0 14px 0 42px;
  border:1px solid #cbd5e1;
  border-radius:14px;
  background:linear-gradient(180deg,#fff 0%,#f8fbff 100%);
  color:#0f172a;
  font-size:14px;
  font-weight:800;
  outline:none;
  box-shadow:0 4px 12px rgba(2,6,23,.06);
  transition:border-color .18s ease,box-shadow .18s ease;
}

.general-search-input:focus{
  border-color:#60a5fa;
  box-shadow:0 0 0 4px rgba(59,130,246,.15);
}
.general-results-strip{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  flex-wrap:wrap;
  padding:0 2px;
  color:#64748b;
  font-size:13px;
  font-weight:800;
}

.general-results-strip strong{
  color:#0f172a;
}

.all-map-card{
  padding:0;
  overflow:hidden;
}

.general-map-head{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  padding:13px 16px;
  border-bottom:1px solid #e2e8f0;
  background:linear-gradient(180deg,#fff,#f8fbff);
}

.general-map-title{
  margin:0;
  color:#0f172a;
  font-size:15px;
  font-weight:950;
}

.general-map-note{
  color:#64748b;
  font-size:12px;
  font-weight:800;
}

#allSensorsMap{
  width:100%;
  min-height:300px;
}

.all-sensors-grid{
  display:grid;
  grid-template-columns:repeat(3,minmax(0,1fr));
  gap:16px;
}

.all-sensor-card{
  padding:16px;
  min-width:0;
  display:flex;
  flex-direction:column;
  gap:14px;
}

.all-sensor-card.general-compact-card{
  border:1px solid #e2e8f0;
  box-shadow:0 8px 24px rgba(15,23,42,.06);
  transition:transform .18s ease,box-shadow .18s ease,border-color .18s ease;
}

.all-sensor-card.general-compact-card:hover{
  transform:translateY(-2px);
  border-color:#bfdbfe;
  box-shadow:0 14px 30px rgba(37,99,235,.10);
}

.all-sensor-head{
  display:grid;
  grid-template-columns:64px minmax(0,1fr) auto;
  gap:12px;
  align-items:start;
  margin:0;
}

.all-sensor-photo{
  width:58px;
  height:58px;
  object-fit:contain;
  border-radius:14px;
  background:#f8fafc;
  border:1px solid #e2e8f0;
  padding:6px;
}

.all-sensor-title{
  margin:0;
  color:#0f172a;
  font-size:17px;
  font-weight:950;
  line-height:1.18;
  overflow-wrap:anywhere;
}
.all-sensor-app{
  display:inline-flex;
  margin-top:6px;
  width:max-content;
  max-width:100%;
  border-radius:999px;
  padding:4px 8px;
  background:#eff6ff;
  border:1px solid #bfdbfe;
  color:#1d4ed8;
  font-size:10px;
  font-weight:950;
  text-transform:uppercase;
  letter-spacing:.035em;
}

.general-card-head-actions{
  display:flex;
  flex-direction:column;
  align-items:flex-end;
  gap:7px;
}

.general-battery-chip{
  display:inline-flex;
  align-items:center;
  gap:5px;
  padding:5px 8px;
  border-radius:999px;
  background:#ffffff;
  border:1px solid #dbeafe;
  color:#334155;
  font-size:11px;
  font-weight:900;
  white-space:nowrap;
  box-shadow:0 2px 8px rgba(15,23,42,.04);
}

.general-battery-chip .battery-icon{
  font-size:12px;
}

.status-chip{
  display:inline-flex;
  align-items:center;
  gap:7px;
  border-radius:999px;
  padding:6px 9px;
  font-size:10px;
  font-weight:950;
  background:#f8fafc;
  border:1px solid #e2e8f0;
  color:#0f172a;
  white-space:nowrap;
}

.status-chip.online{
  background:#dcfce7;
  border-color:#86efac;
  color:#166534;
}

.status-chip.stale{
  background:#fef3c7;
  border-color:#fde68a;
  color:#92400e;
}

.status-chip.offline{
  background:#fee2e2;
  border-color:#fecaca;
  color:#991b1b;
}

/* Las variables son la información principal de la vista general. */
.general-vars-list{
  display:grid;
  grid-template-columns:repeat(auto-fit,minmax(108px,1fr));
  gap:9px;
  padding:0;
  border:0;
  background:transparent;
}

.general-var-row{
  min-width:0;
  min-height:86px;
  display:flex;
  flex-direction:column;
  justify-content:center;
  align-items:flex-start;
  gap:8px;
  padding:11px 12px;
  border:1px solid #dbeafe;
  border-radius:13px;
  background:linear-gradient(180deg,#f8fbff 0%,#f2f8ff 100%);
}

.general-var-name{
  width:100%;
  min-width:0;
  color:#475569;
  font-size:10px;
  line-height:1.18;
  font-weight:900;
  text-transform:uppercase;
  letter-spacing:.025em;
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap;
}

.general-var-value{
  color:#0f172a;
  font-size:clamp(19px,1.45vw,24px);
  line-height:1;
  font-weight:950;
  letter-spacing:-.025em;
  white-space:nowrap;
}

.general-compact-card .general-vars-list.is-offline .general-var-row{
  background:#f8fafc;
  border-color:#e2e8f0;
}

.general-compact-card .general-vars-list.is-offline .general-var-value{
  color:#94a3b8;
}

.general-card-empty{
  min-height:62px;
  display:grid;
  place-items:center;
  padding:10px;
  border:1px dashed #cbd5e1;
  border-radius:13px;
  color:#64748b;
  background:#f8fafc;
  font-size:12px;
  font-weight:800;
  text-align:center;
}

.general-card-footer{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
  margin-top:auto;
  padding-top:2px;
}

.general-last-reading{
  color:#64748b;
  font-size:11px;
  font-weight:800;
  line-height:1.25;
}

.general-view-btn{
  min-height:36px !important;
  height:36px !important;
  padding:0 13px !important;
  border-radius:11px !important;
  background:linear-gradient(135deg,#0284c7,#2563eb) !important;
  font-size:11px !important;
  white-space:nowrap;
}

.general-load-more{
  display:flex;
  justify-content:center;
  padding:4px 0 2px;
}

.general-load-more button{
  min-width:190px;
}

.all-vars-table-wrap{
  overflow-x:auto;
  border:1px solid #e2e8f0;
  border-radius:14px;
}

.all-vars-table{
  width:100%;
  border-collapse:collapse;
  min-width:850px;
  background:#fff;
}

.all-vars-table th:first-child,
.all-vars-table td:first-child{
  min-width:260px;
  white-space:normal;
  overflow-wrap:anywhere;
}

.all-vars-table th,
.all-vars-table td{
  padding:10px 11px;
  border-bottom:1px solid #e2e8f0;
  text-align:left;
  font-size:13px;
}

.all-vars-table th{
  background:#f1f5f9;
  color:#0f172a;
  font-weight:950;
}

.all-vars-table td{
  color:#334155;
  font-weight:750;
}

.all-empty{
  padding:22px;
  text-align:center;
  font-weight:900;
  color:#64748b;
}

@media(max-width:1200px){
  .all-sensors-grid{
    grid-template-columns:repeat(2,minmax(0,1fr));
  }
}

@media(max-width:1050px){
  .all-summary-grid{
    grid-template-columns:repeat(2,minmax(0,1fr));
  }

  .general-filter-row{
    grid-template-columns:1fr 1fr;
  }

  .general-search-wrap{
    grid-column:1 / -1;
  }
}

@media(max-width:720px){
  .all-sensors-grid{
    grid-template-columns:1fr;
  }

  .general-filter-row{
    grid-template-columns:1fr;
  }

  .general-search-wrap{
    grid-column:auto;
  }

  .all-sensor-head{
    grid-template-columns:56px minmax(0,1fr) auto;
    gap:10px;
  }

  .general-card-head-actions{
    gap:5px;
  }

  .status-chip,
  .general-battery-chip{
    font-size:9px;
    padding:5px 7px;
  }

  .general-vars-list{
    grid-template-columns:repeat(2,minmax(0,1fr));
  }

  .general-var-row{
    min-height:80px;
  }

  .general-var-value{
    font-size:21px;
  }

  .general-card-footer{
    align-items:flex-end;
  }
}

     .muted{opacity:.7}
    `}</style>
  );

  const goHome = () => {
    setLeftNavOpen(false);
    navigate("/seleccionar-tipo", { replace: false });
  };

  const openSensorsOverview = () => {
    setLeftNavOpen(false);
    navigate(
      `/dashboard/General${viewAs ? `?as=${encodeURIComponent(viewAs)}` : ""}`
    );
  };

  const openClaimFromSidebar = () => {
    setLeftNavOpen(false);
    setClaimMsg("");
    setClaimDeviceId("");
    setClaimPairingCode("");
    setClaimOpen(true);
  };

  const openObservationsFromSidebar = () => {
    if (!selectedDevice || isAllDevices) return;

    setLeftNavOpen(false);
    setObservationsOpen(true);
  };

  const openReportsFromSidebar = () => {
    if (!selectedDevice || isAllDevices) return;

    setLeftNavOpen(false);

    navigate(
      `/report/${encodeURIComponent(
        activeType
      )}?sensorId=${encodeURIComponent(selectedDevice)}${
        viewAs ? `&as=${encodeURIComponent(viewAs)}` : ""
      }`
    );
  };

  const openInstallationFromSidebar = () => {
    if (!selectedDevice || isAllDevices || !canShowInstallations) return;

    setLeftNavOpen(false);

    navigate(
      `/instalacion/${encodeURIComponent(selectedDevice)}?tipo=${encodeURIComponent(
        activeType
      )}&sensorName=${encodeURIComponent(
        sensorInfo?.name || selectedDevice || ""
      )}&latitude=${encodeURIComponent(
        sensorInfo?.lat ?? sensorInfo?.latitude ?? ""
      )}&longitude=${encodeURIComponent(
        sensorInfo?.lng ?? sensorInfo?.lon ?? sensorInfo?.longitude ?? ""
      )}`
    );
  };

  const openAlertsFromSidebar = () => {
    if (!selectedDevice || isAllDevices || !canUseAlarms) return;

    setLeftNavOpen(false);
    openAlerts();
  };

  const openAccountFromSidebar = () => {
    setLeftNavOpen(false);
    setAccountModalOpen(true);
  };

  const closeAccountModal = () => {
    setAccountModalOpen(false);
  };

  const openColdChainTracking = () => {
    if (!selectedDevice || isAllDevices) return;

    const asPart = viewAs ? `?as=${encodeURIComponent(viewAs)}` : "";
    navigate(`/tracking/${encodeURIComponent(selectedDevice)}${asPart}`);
  };

  const openSensorFromGeneral = (sensor) => {
    const sensorId = String(sensor?.id || "").trim();
    const sensorType = resolveType(sensor?.sensor_type);
    if (!sensorId) return;

    const asPart = viewAs ? `&as=${encodeURIComponent(viewAs)}` : "";

    if (sensorType === "GPS") {
      navigate(`/gps${viewAs ? `?as=${encodeURIComponent(viewAs)}` : ""}`);
      return;
    }

    navigate(
      `/dashboard/${encodeURIComponent(sensorType)}?sensorId=${encodeURIComponent(sensorId)}${asPart}`
    );
  };
const handleLogout = () => {
  logout();
};

const handleOpenUserAdmin = () => {
  setMenuOpen(false);
  navigate("/admin/usuarios");
};

const handleOpenAuditTrail = () => {
  setMenuOpen(false);
  navigate("/admin/audit-trail");
};

const handleOpenPart11 = () => {
  setMenuOpen(false);
  navigate("/admin/part11");
};

const handleOpenCalibrations = () => {
  setMenuOpen(false);
  navigate("/admin/calibraciones");
};

const handleOpenPasswordModal = () => {
  setMenuOpen(false);
  setCurrentPassword("");
  setNewPassword("");
  setConfirmPassword("");
  setPasswordMsg("");
  setPasswordModalOpen(true);
};

const handleClosePasswordModal = () => {
  if (passwordLoading) return;

  setPasswordModalOpen(false);
  setCurrentPassword("");
  setNewPassword("");
  setConfirmPassword("");
  setPasswordMsg("");
};

const handleChangePassword = async (e) => {
  e.preventDefault();
  setPasswordMsg("");

  if (!currentPassword || !newPassword || !confirmPassword) {
    setPasswordMsg("Debes completar todos los campos.");
    return;
  }

  if (newPassword.length < 8) {
    setPasswordMsg("La nueva contraseña debe tener al menos 8 caracteres.");
    return;
  }

  if (newPassword !== confirmPassword) {
    setPasswordMsg("La nueva contraseña y su confirmación no coinciden.");
    return;
  }

  if (currentPassword === newPassword) {
    setPasswordMsg(
      "La nueva contraseña debe ser diferente de la contraseña actual."
    );
    return;
  }

  setPasswordLoading(true);

  try {
    const url = `${API_BASE}/change-password`;

    const { data } = await axios.put(
      url,
      {
        currentPassword,
        newPassword,
      },
      authHeader
    );

    setPasswordMsg(
      data?.message || "Contraseña actualizada correctamente."
    );

    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");

    setTimeout(() => {
      setPasswordModalOpen(false);
      setPasswordMsg("");
    }, 1800);
  } catch (error) {
    setPasswordMsg(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        "No se pudo cambiar la contraseña."
    );
  } finally {
    setPasswordLoading(false);
  }
};

const handleOpenTeamLogo = async () => {
  setMenuOpen(false);
  setLogoModalOpen(true);
  setLogoMsg("");
  setLogoFile(null);
  setLogoPreview("");

  try {
    setLogoLoading(true);

    const { data } = await axios.get(
      `${API_ROOT}/team/logo`,
      authHeader
    );

    setTeamLogoInfo(normalizeTeamLogoInfo(data));
  } catch (error) {
    setTeamLogoInfo(null);
    setLogoMsg(
      error?.response?.data?.error ||
        "No se pudo cargar el logo actual del equipo."
    );
  } finally {
    setLogoLoading(false);
  }
};

const handleCloseTeamLogo = () => {
  if (logoLoading) return;

  setLogoModalOpen(false);
  setLogoFile(null);
  setLogoPreview("");
  setLogoMsg("");
};

const handleSelectLogoFile = (e) => {
  const file = e.target.files?.[0];

  setLogoMsg("");
  setLogoFile(null);
  setLogoPreview("");

  if (!file) return;

  const allowed = ["image/jpeg", "image/png", "image/webp"];

  if (!allowed.includes(file.type)) {
    setLogoMsg("Solo se permiten imágenes JPG, PNG o WEBP.");
    return;
  }

  if (file.size > 2 * 1024 * 1024) {
    setLogoMsg("La imagen no debe superar 2 MB.");
    return;
  }

  setLogoFile(file);
  setLogoPreview(URL.createObjectURL(file));
};

const handleUploadTeamLogo = async () => {
  if (!logoFile) {
    setLogoMsg("Selecciona una imagen primero.");
    return;
  }

  try {
    setLogoLoading(true);
    setLogoMsg("");

    const formData = new FormData();
    formData.append("logo", logoFile);

    const { data } = await axios.post(
      `${API_ROOT}/team/logo`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );

    setTeamLogoInfo(normalizeTeamLogoInfo(data));
    setTeamLogoVersion(Date.now());
    setLogoFile("");
    setLogoPreview("");
    setLogoMsg("✅ Logo actualizado correctamente. Los próximos PDF y el encabezado usarán esta imagen.");
  } catch (error) {
    setLogoMsg(
      error?.response?.data?.error ||
        "No se pudo subir el logo del equipo."
    );
  } finally {
    setLogoLoading(false);
  }
};

const handleDeleteTeamLogo = async () => {
  try {
    setLogoLoading(true);
    setLogoMsg("");

    const { data } = await axios.delete(
      `${API_ROOT}/team/logo`,
      authHeader
    );

    setTeamLogoInfo(normalizeTeamLogoInfo(data));
    setTeamLogoVersion(Date.now());
    setLogoFile(null);
    setLogoPreview("");
    setLogoMsg("✅ Logo eliminado. El encabezado y el PDF volverán a usar ELICROM IOT.");
  } catch (error) {
    setLogoMsg(
      error?.response?.data?.error ||
        "No se pudo eliminar el logo del equipo."
    );
  } finally {
    setLogoLoading(false);
  }
};


const GENERAL_CARD_HIDDEN_VARS = new Set([
  "BAT",
  "BATV",
  "BAT_V",
  "LAT",
  "LON",
  "LATITUDE",
  "LONGITUDE",
  "ALT",
  "ALTITUDE",
]);

function getGeneralCardVariables(sensor) {
  const rows = Array.isArray(sensor?.variables) ? sensor.variables : [];
  const seen = new Set();

  return rows
    .map((row, originalIndex) => ({
      ...row,
      originalIndex,
      key: normalizeVariableDisplayKey(row?.variable_name),
    }))
    .filter((row) => {
      if (!row.key || GENERAL_CARD_HIDDEN_VARS.has(row.key)) return false;
      if (seen.has(row.key)) return false;
      seen.add(row.key);
      return true;
    })
    .sort((a, b) => {
      const aName = canonVar(a.variable_name);
      const bName = canonVar(b.variable_name);
      const aIndex = OVERVIEW_VARIABLE_ORDER.findIndex(
        (item) => normalizeVariableDisplayKey(item) === a.key
      );
      const bIndex = OVERVIEW_VARIABLE_ORDER.findIndex(
        (item) => normalizeVariableDisplayKey(item) === b.key
      );

      const safeA = aIndex < 0 ? 999 : aIndex;
      const safeB = bIndex < 0 ? 999 : bIndex;

      if (safeA !== safeB) return safeA - safeB;
      return String(aName).localeCompare(String(bName));
    })
    .slice(0, 3);
}

function formatGeneralCardValue(variable, sensorStatus, sensorType = null) {
  if (sensorStatus === "offline" || variable?.display_invalid) return "--.--";

  const value = Number(variable?.last_value);
  if (!Number.isFinite(value)) return "—";

  return fmtOverviewValue(value, canonVar(variable?.variable_name), sensorType);
}

const allSummary = useMemo(() => {
  const total = allSensorsOverview.length || devices.length || 0;
  const online = allSensorsOverview.filter((s) => s.status === "online").length;
  const stale = allSensorsOverview.filter((s) => s.status === "stale").length;
  const offline = Math.max(total - online - stale, 0);
  const lastTs = allSensorsOverview
    .map((s) => s.last_ts)
    .filter(Number.isFinite)
    .reduce((max, ts) => Math.max(max, ts), null);

  return { total, online, stale, offline, lastTs };
}, [allSensorsOverview, devices.length]);

const isSingleProcessVariable =
  visibleAvailableVars.filter((v) => canonVar(v) !== "BAT").length === 1 &&
  visibleKpiVars.length === 1;

const batteryKpi = kpis?.BAT;
const batteryValue =
  batteryKpi?.value !== null && batteryKpi?.value !== undefined
    ? Number(batteryKpi.value)
    : null;

  return (
    <div className="sensor-dashboard sensor-dashboard-with-sidebar">
      {styles}

      {/* ===== PANEL DE NAVEGACIÓN IZQUIERDO ===== */}
      <button
        type="button"
        className="sensor-sidebar-mobile-toggle"
        aria-label="Abrir menú de navegación"
        title="Menú"
        onClick={() => setLeftNavOpen(true)}
      >
        <span aria-hidden="true">☰</span>
      </button>

      {leftNavOpen && (
        <button
          type="button"
          className="sensor-sidebar-mobile-backdrop"
          aria-label="Cerrar menú de navegación"
          onClick={() => setLeftNavOpen(false)}
        />
      )}

      <aside
        className={`sensor-left-sidebar ${leftNavOpen ? "open" : ""}`}
        aria-label="Navegación principal"
      >
        <div className="sensor-left-sidebar-brand">
          <div className="sensor-left-sidebar-brand-mark sd-client-brand-mark">
            <span className="sd-client-initial">{clientInitial}</span>
            {teamLogoSrc && (
              <img
                className="sd-client-brand-image"
                src={teamLogoSrc}
                alt={`Logotipo de ${clientDisplayName}`}
                onError={(event) => { event.currentTarget.style.display = "none"; }}
              />
            )}
          </div>

          <div className="sensor-left-sidebar-brand-copy">
            <strong title={clientDisplayName}>{clientDisplayName}</strong>
            <span>Panel de monitoreo</span>
          </div>

          <button
            type="button"
            className="sensor-left-sidebar-close"
            aria-label="Cerrar menú"
            onClick={() => setLeftNavOpen(false)}
          >
            ×
          </button>
        </div>

        <nav className="sensor-left-sidebar-nav">
          <div className="sensor-left-sidebar-section-label">
            Navegación
          </div>

          <button
            type="button"
            className="sensor-left-sidebar-item"
            onClick={goHome}
          >
            <span className="sensor-left-sidebar-icon"><SidebarIcon name="home" /></span>
            <span>Inicio</span>
          </button>

          <button
            type="button"
            className={`sensor-left-sidebar-item${
              isGeneralDashboard ? " active" : ""
            }`}
            onClick={openSensorsOverview}
            title="Ver todos los sensores"
          >
            <span className="sensor-left-sidebar-icon">
              <SidebarIcon name="sensors" />
            </span>
            <span>Todos los sensores</span>
          </button>

          {(canUseAlarms ||
            (canClaimSensors && !isGeneralDashboard) ||
            (canGenerateReports && !isGeneralDashboard) ||
            (activeType === "HumedadTemperaturaGPS" && !isGeneralDashboard) ||
            (isMolinosUser && canViewData && !isGeneralDashboard) ||
            canShowInstallations) && (
            <div className="sensor-left-sidebar-section-label sensor-left-sidebar-section-spaced">
              Operación
            </div>
          )}

          {canUseAlarms && !isGeneralDashboard && (
            <button
              type="button"
              className="sensor-left-sidebar-item"
              onClick={openAlertsFromSidebar}
              disabled={!selectedDevice || isAllDevices}
              title={
                !selectedDevice || isAllDevices
                  ? "Selecciona un sensor específico para configurar alertas"
                  : "Configurar alertas del sensor"
              }
            >
              <span className="sensor-left-sidebar-icon"><SidebarIcon name="alerts" /></span>
              <span>Alarmas</span>
            </button>
          )}

          {canClaimSensors && !isGeneralDashboard && (
            <button
              type="button"
              className="sensor-left-sidebar-item"
              onClick={openClaimFromSidebar}
              title="Vincular un nuevo sensor"
            >
              <span className="sensor-left-sidebar-icon"><SidebarIcon name="add" /></span>
              <span>Agregar sensor</span>
            </button>
          )}

          {canGenerateReports && !isGeneralDashboard && (
            <button
              type="button"
              className="sensor-left-sidebar-item"
              onClick={openReportsFromSidebar}
              disabled={!selectedDevice || isAllDevices}
              title={
                !selectedDevice || isAllDevices
                  ? "Selecciona un sensor específico para abrir reportes"
                  : "Abrir reportes y descargas"
              }
            >
              <span className="sensor-left-sidebar-icon"><SidebarIcon name="reports" /></span>
              <span>Reportes</span>
            </button>
          )}

          {activeType === "HumedadTemperaturaGPS" && !isGeneralDashboard && (
            <button
              type="button"
              className="sensor-left-sidebar-item"
              onClick={() => {
                setLeftNavOpen(false);
                openColdChainTracking();
              }}
              disabled={!selectedDevice || isAllDevices}
              title={
                !selectedDevice || isAllDevices
                  ? "Selecciona un sensor específico para ver el recorrido"
                  : "Ver recorrido y tracking GPS del sensor"
              }
            >
              <span className="sensor-left-sidebar-icon"><SidebarIcon name="tracking" /></span>
              <span>Tracking</span>
            </button>
          )}

          {isMolinosUser && canViewData && !isGeneralDashboard && (
            <button
              type="button"
              className="sensor-left-sidebar-item"
              onClick={openObservationsFromSidebar}
              disabled={!selectedDevice || isAllDevices}
              title={
                !selectedDevice || isAllDevices
                  ? "Selecciona un sensor específico para registrar observaciones"
                  : "Registrar y consultar observaciones operativas"
              }
            >
              <span className="sensor-left-sidebar-icon"><SidebarIcon name="observations" /></span>
              <span>Observaciones</span>
            </button>
          )}

          {canShowInstallations && (
            <button
              type="button"
              className="sensor-left-sidebar-item"
              onClick={openInstallationFromSidebar}
              title="Abrir módulo de instalación"
            >
              <span className="sensor-left-sidebar-icon"><SidebarIcon name="installations" /></span>
              <span>Instalaciones</span>
            </button>
          )}

          {(canManageUsers || canAccessPart11 || canManageCalibrations) && (
            <div className="sensor-left-sidebar-section-label sensor-left-sidebar-section-spaced">
              Administración
            </div>
          )}

          {canManageUsers && (
            <button
              type="button"
              className="sensor-left-sidebar-item"
              onClick={() => {
                setLeftNavOpen(false);
                handleOpenUserAdmin();
              }}
            >
              <span className="sensor-left-sidebar-icon"><SidebarIcon name="users" /></span>
              <span>Usuarios</span>
            </button>
          )}

          {canManageUsers && (
            <button
              type="button"
              className="sensor-left-sidebar-item"
              onClick={() => {
                setLeftNavOpen(false);
                handleOpenTeamLogo();
              }}
            >
              <span className="sensor-left-sidebar-icon"><SidebarIcon name="logo" /></span>
              <span>Logo de reportes</span>
            </button>
          )}

          {canAccessPart11 && (
            <button
              type="button"
              className="sensor-left-sidebar-item"
              onClick={() => {
                setLeftNavOpen(false);
                handleOpenAuditTrail();
              }}
            >
              <span className="sensor-left-sidebar-icon"><SidebarIcon name="audit" /></span>
              <span>Audit Trail</span>
            </button>
          )}

          {canAccessPart11 && (
            <button
              type="button"
              className="sensor-left-sidebar-item"
              onClick={() => {
                setLeftNavOpen(false);
                handleOpenPart11();
              }}
            >
              <span className="sensor-left-sidebar-icon"><SidebarIcon name="part11" /></span>
              <span>CFR 21 Part 11</span>
            </button>
          )}

          {canManageCalibrations && (
            <button
              type="button"
              className="sensor-left-sidebar-item"
              onClick={() => {
                setLeftNavOpen(false);
                handleOpenCalibrations();
              }}
            >
              <span className="sensor-left-sidebar-icon"><SidebarIcon name="calibration" /></span>
              <span>Calibraciones</span>
            </button>
          )}
        </nav>

        <div className="sensor-left-sidebar-footer">
          <div className="sensor-left-sidebar-user">
            <span className="sensor-left-sidebar-avatar" aria-hidden="true">
              {String(username || "U").trim().charAt(0).toUpperCase() || "U"}
            </span>
            <span className="sensor-left-sidebar-user-copy">
              <strong>{username}</strong>
              <small>{accountRoleLabel}</small>
            </span>
          </div>

          <button
            type="button"
            className="sensor-left-sidebar-item sensor-left-sidebar-logout"
            onClick={handleLogout}
          >
            <span className="sensor-left-sidebar-icon"><SidebarIcon name="logout" /></span>
            <span>Cerrar sesión</span>
          </button>
        </div>
      </aside>

      {/* Botón Home */}
      <div className="action-float left">
        <button
          type="button"
          className="btn-solid btn-home btn-circle"
          title="Inicio"
          onClick={goHome}
        >
          <svg className="icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 3.172l9 8V20a2 2 0 0 1-2 2h-5v-6H10v6H5a2 2 0 0 1-2-2v-8.828l9-8zM3 10.172L12 2l9 8.172V11h-2V9.414l-7-6.293-7 6.293V11H3v-.828z"/>
          </svg>
        </button>
      </div>

      {/* Menú usuario */}
      <div className="action-float right">
        <div className="user-menu-wrap" ref={menuRef}>
          <button
            type="button"
            className="btn-solid btn-menu btn-circle sd-admin-menu-trigger"
            title="Opciones de mi cuenta"
            aria-expanded={menuOpen}
            aria-haspopup="menu"
            onClick={() => setMenuOpen((v) => !v)}
          >
            <span className="sd-admin-avatar" aria-hidden="true">
              {String(username || "U").trim().charAt(0).toUpperCase() || "U"}
            </span>
            <span className="sd-admin-name">{username}</span>
            <span className="sd-admin-chevron" aria-hidden="true">⌄</span>
          </button>
          {menuOpen && (
            <div className="menu-panel sd-profile-menu" role="menu" aria-label="Opciones personales">
              <button
                type="button"
                role="menuitem"
                className="menu-item clickable sd-menu-action"
                onClick={() => { setMenuOpen(false); openAccountFromSidebar(); }}
              >
                <SidebarIcon name="account" />
                <span>Mi perfil</span>
              </button>
              <button
                type="button"
                role="menuitem"
                className="menu-item clickable sd-menu-action"
                onClick={handleOpenPasswordModal}
              >
                <svg className="icon" viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><rect x="5" y="10" width="14" height="11" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></svg>
                <span>Cambiar contraseña</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Encabezado del panel; marca del cliente en la barra lateral */}
      <div className="page-title-centered sd-page-heading">
        <div>
          <h1>{isGeneralDashboard ? "Panel General de Sensores" : "Panel de Sensores"}</h1>
          <p>Monitoreo en tiempo real de variables del sensor</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="toolbar-row">
        {isGeneralDashboard ? (
          <div className="general-filter-row">
            <div className="general-search-wrap">
              <svg
                className="general-search-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" />
              </svg>
              <input
                className="general-search-input"
                type="search"
                value={generalSearch}
                onChange={(e) => setGeneralSearch(e.target.value)}
                placeholder="Buscar por nombre o ID..."
                aria-label="Buscar sensor"
              />
            </div>
        
            <select
              value={generalTypeFilter}
              onChange={(e) => setGeneralTypeFilter(e.target.value)}
              aria-label="Filtrar por aplicación"
            >
              <option value="all">Aplicación: Todas</option>
              {generalTypeOptions.map((type) => (
                <option key={type} value={type}>
                  {getTypeDisplayName(type)}
                </option>
              ))}
            </select>

            <select
              value={generalStatusFilter}
              onChange={(e) => setGeneralStatusFilter(e.target.value)}
              aria-label="Filtrar por estado"
            >
              <option value="all">Estado: Todos</option>
              <option value="online">En línea</option>
              <option value="stale">Intermitentes</option>
              <option value="offline">Fuera de línea</option>
            </select>
          </div>
        ) : (
          <select
            value={selectedDevice}
            onChange={(e) => setSelectedDevice(e.target.value)}
            title="Seleccionar dispositivo"
          >
            <option value="">Seleccionar dispositivo</option>
            <option value={ALL_DEVICES_VALUE}>Todos los sensores</option>
            {devices.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name || d.id}
              </option>
            ))}
          </select>
        )}

        {canManageUsers && usersList.length > 0 && (
          <select value={viewAs} onChange={(e) => setViewAs(e.target.value)}>
            <option value="">Ver como: (ADMIN)</option>
            {usersList.map((u) => (
              <option key={u.id} value={u.username}>
                {u.username}
              </option>
            ))}
          </select>
        )}

        {!isGeneralDashboard && <div style={{ flex: 1 }} />}
</div>

{isAllDevices ? (
  isGeneralDashboard ? (
    <div className="all-overview-wrap">
      <div className="all-summary-grid">
        <div className="card glass all-summary-card">
          <span>Total sensores</span>
          <strong>{allSummary.total}</strong>
        </div>
        <div className="card glass all-summary-card">
          <span>En línea</span>
          <strong>{allSummary.online}</strong>
        </div>
        <div className="card glass all-summary-card">
          <span>Intermitentes</span>
          <strong>{allSummary.stale}</strong>
        </div>
        <div className="card glass all-summary-card">
          <span>Fuera de línea</span>
          <strong>{allSummary.offline}</strong>
        </div>
        <div className="card glass all-summary-card">
          <span>Última lectura</span>
          <strong className="all-summary-date">
            {allSummary.lastTs ? fmtDT(allSummary.lastTs) : "—"}
          </strong>
        </div>
      </div>

      <div className="general-results-strip">
        <span>
          {allSensorsLoading ? (
            <>Cargando resumen de <strong>{allSummary.total}</strong> sensores...</>
          ) : (
            <>
              Mostrando <strong>{filteredGeneralSensors.length}</strong> de{" "}
              <strong>{allSummary.total}</strong> sensores
            </>
          )}
        </span>
        {(generalSearch ||
          generalTypeFilter !== "all" ||
          generalStatusFilter !== "all") && (
          <button
            type="button"
            className="btn-link"
            onClick={() => {
              setGeneralSearch("");
              setGeneralTypeFilter("all");
              setGeneralStatusFilter("all");
            }}
          >
            Limpiar filtros
          </button>
        )}
      </div>

      <div id="allSensorsMapSection" className="card glass all-map-card">
        <div className="general-map-head">
          <div>
            <h3 className="general-map-title">Mapa general</h3>
            <div className="general-map-note">
              {generalMissingCoordsCount > 0
                ? `${generalMissingCoordsCount} sensor${
                    generalMissingCoordsCount === 1 ? "" : "es"
                  } sin coordenadas no se muestra${
                    generalMissingCoordsCount === 1 ? "" : "n"
                  } en el mapa.`
                : "Todos los sensores filtrados con coordenadas se muestran en el mapa."}
            </div>
          </div>
        </div>
        <div id="allSensorsMap" />
      </div>

      {allSensorsLoading && (
        <div className="card glass all-empty">Cargando resumen de sensores...</div>
      )}

      {allSensorsMsg && (
        <div className="card glass all-empty">{allSensorsMsg}</div>
      )}

      {!allSensorsLoading &&
        !filteredGeneralSensors.length &&
        !allSensorsMsg && (
          <div className="card glass all-empty">
            No hay sensores que coincidan con los filtros seleccionados.
          </div>
        )}

      <div className="all-sensors-grid">
        {visibleGeneralSensors.map((sensor) => {
          const cardVariables = getGeneralCardVariables(sensor);

          return (
            <div
              key={sensor.id}
              className="card glass all-sensor-card general-compact-card"
            >
              <div className="all-sensor-head">
                <img
                  className="all-sensor-photo"
                  src={getSensorImage(sensor)}
                  alt={sensor.name || sensor.id || "sensor"}
                  onError={(e) =>
                    (e.currentTarget.src = "/images/default-sensor.png")
                  }
                />

                <div style={{ minWidth: 0 }}>
                  <h3 className="all-sensor-title">
                    {sensor.name || sensor.id}
                  </h3>
                  <span className="all-sensor-app">
                    {getTypeDisplayName(sensor.sensor_type)}
                  </span>
                </div>

                <div className="general-card-head-actions">
                  <span className={`status-chip ${sensor.status}`}>
                    {statusText(sensor.status)}
                  </span>

                  {sensor.battery !== null &&
                    Number.isFinite(Number(sensor.battery)) && (
                      <span
                        className="general-battery-chip"
                        title={`Batería: ${Number(sensor.battery).toFixed(0)}%`}
                      >
                        <span className="battery-icon">🔋</span>
                        <span>{Number(sensor.battery).toFixed(0)}%</span>
                      </span>
                    )}
                </div>
              </div>

              {cardVariables.length ? (
                <div
                  className={`general-vars-list ${
                    sensor.status === "offline" ? "is-offline" : ""
                  }`}
                >
                  {cardVariables.map((variable) => {
                    const vKey = canonVar(variable.variable_name);

                    return (
                      <div
                        key={`${sensor.id}-${normalizeVariableDisplayKey(vKey)}`}
                        className="general-var-row"
                      >
                        <span
                          className="general-var-name"
                          title={getVariableDisplayLabel(
                            vKey,
                            false,
                            variable.unit,
                            sensor.sensor_type
                          )}
                        >
                          {getVariableDisplayLabel(
                            vKey,
                            false,
                            variable.unit,
                            sensor.sensor_type
                          )}
                        </span>
                        <strong className="general-var-value">
                          {formatGeneralCardValue(variable, sensor.status, sensor.sensor_type)}
                        </strong>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="general-card-empty">
                  Este sensor todavía no tiene lecturas disponibles.
                </div>
              )}

              <div className="general-card-footer">
                <div className="general-last-reading">
                  Última lectura
                  <br />
                  <strong>
                    {sensor.last_ts ? fmtDT(sensor.last_ts) : "Sin datos"}
                  </strong>
                </div>

                <button
                  type="button"
                  className="btn-solid general-view-btn"
                  onClick={() => openSensorFromGeneral(sensor)}
                >
                  VER SENSOR
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {visibleGeneralSensors.length < filteredGeneralSensors.length && (
        <div className="general-load-more">
          <button
            type="button"
            className="btn-solid"
            onClick={() =>
              setGeneralVisibleCount((current) => current + 12)
            }
          >
            MOSTRAR 12 MÁS
          </button>
        </div>
      )}
    </div>
  ) : (

  <div className="all-overview-wrap">
    <div className="all-summary-grid">
      <div className="card glass all-summary-card">
        <span>Total sensores</span>
        <strong>{allSummary.total}</strong>
      </div>
      <div className="card glass all-summary-card">
        <span>En línea</span>
        <strong>{allSummary.online}</strong>
      </div>
      <div className="card glass all-summary-card">
        <span>Intermitentes</span>
        <strong>{allSummary.stale}</strong>
      </div>
      <div className="card glass all-summary-card">
        <span>Fuera de línea</span>
        <strong>{allSummary.offline}</strong>
      </div>
      <div className="card glass all-summary-card">
        <span>Última lectura</span>
        <strong className="all-summary-date">
          {allSummary.lastTs ? fmtDT(allSummary.lastTs) : "—"}
        </strong>
      </div>
    </div>

    <div id="allSensorsMapSection" className="card glass all-map-card">
      <div id="allSensorsMap" />
    </div>

    {allSensorsLoading && (
      <div className="card glass all-empty">Cargando resumen de sensores...</div>
    )}

    {allSensorsMsg && (
      <div className="card glass all-empty">{allSensorsMsg}</div>
    )}

    {!allSensorsLoading && !allSensorsOverview.length && !allSensorsMsg && (
      <div className="card glass all-empty">No hay sensores para mostrar.</div>
    )}

    <div className="all-sensors-grid">
      {allSensorsOverview.map((sensor) => (
        <div key={sensor.id} className="card glass all-sensor-card">
          <div className="all-sensor-head">
            <img
              className="all-sensor-photo"
              src={getSensorImage(sensor)}
              alt={sensor.name || sensor.id || "sensor"}
              onError={(e) => (e.currentTarget.src = "/images/default-sensor.png")}
            />

            <div style={{ minWidth: 0 }}>
              <h3 className="all-sensor-title">{sensor.name || sensor.id}</h3>
              <div className="all-sensor-meta">
                <span><strong>ID:</strong> {sensor.id}</span>
                <span><strong>Aplicación:</strong> {getTypeDisplayName(sensor.sensor_type || activeType)}</span>
                <span><strong>Lat/Lng:</strong> {sensor.lat ?? "—"} / {sensor.lng ?? "—"}</span>
                <span><strong>Última lectura:</strong> {sensor.last_ts ? fmtDT(sensor.last_ts) : "Sin datos"}</span>
                {sensor.battery !== null && (
                  <span><strong>Batería:</strong> {Number(sensor.battery).toFixed(0)}%</span>
                )}
              </div>
            </div>

            <span className={`status-chip ${sensor.status}`}>
              {statusText(sensor.status)}
            </span>
          </div>

          {sensor.variables?.length ? (
            <div className="all-vars-table-wrap">
              <table className="all-vars-table">
                <thead>
                  <tr>
                    <th>Variable</th>
                    <th>Último valor</th>
                    <th>Promedio 24h</th>
                    <th>Mín 24h</th>
                    <th>Máx 24h</th>
                    <th>Datos 24h</th>
                    <th>Última lectura</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {sensor.variables.map((variable) => {
                    const vKey = canonVar(variable.variable_name);

                    return (
                      <tr key={`${sensor.id}-${vKey}`}>
                        <td title={`Variable interna: ${vKey}`}>
                          <strong>
                            {getVariableDisplayLabel(
                              vKey,
                              true,
                              variable.unit,
                              sensor.sensor_type
                            )}
                          </strong>
                        </td>
                        <td>{fmtOverviewValue(variable.last_value, vKey, sensor.sensor_type)}</td>
                        <td>{fmtOverviewValue(variable.avg_value, vKey, sensor.sensor_type)}</td>
                        <td>{fmtOverviewValue(variable.min_value, vKey, sensor.sensor_type)}</td>
                        <td>{fmtOverviewValue(variable.max_value, vKey, sensor.sensor_type)}</td>
                        <td>{variable.count_data || 0}</td>
                        <td>{variable.last_ts ? fmtDT(variable.last_ts) : "Sin datos"}</td>
                        <td>
                          <span className={`status-chip ${variable.status}`}>
                            {statusText(variable.status)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="all-empty">Este sensor no tiene datos en las últimas 24 horas.</div>
          )}

          {isGeneralDashboard && (
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
              <button
                type="button"
                className="btn-solid"
                style={{ background: "#0b67b2" }}
                onClick={() => openSensorFromGeneral(sensor)}
              >
                VER SENSOR
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  </div>

  )) : (
  <>

{/* ALERTA PERSISTENTE DE APERTURA DE TAPA */}
{coverState?.has_accessory === true &&
  (coverState?.is_open === true || coverState?.state === "open") && (
    <div
      role="alert"
      aria-live="assertive"
      style={{
        marginTop: 16,
        marginBottom: 16,
        padding: "18px 20px",
        borderRadius: 16,
        border: "2px solid #dc2626",
        background:
          "linear-gradient(135deg, rgba(254,226,226,.98), rgba(255,247,237,.98))",
        boxShadow: "0 8px 24px rgba(220,38,38,.14)",
        display: "flex",
        alignItems: "center",
        gap: 16,
        flexWrap: "wrap",
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: 52,
          height: 52,
          minWidth: 52,
          borderRadius: "50%",
          background: "#dc2626",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 30,
          lineHeight: 1,
          fontWeight: 900,
        }}
      >
        !
      </div>

      <div style={{ minWidth: 220, flex: "1 1 320px" }}>
        <div
          style={{
            fontSize: 18,
            fontWeight: 900,
            color: "#991b1b",
            lineHeight: 1.2,
            marginBottom: 5,
          }}
        >
          APERTURA DE TAPA DETECTADA
        </div>

        <div
          style={{
            fontSize: 15,
            fontWeight: 700,
            color: "#7f1d1d",
            lineHeight: 1.45,
          }}
        >
          La tapa de la alcantarilla correspondiente a{" "}
          <strong>
            {coverState?.sensor_name || sensorInfo?.name || selectedDevice}
          </strong>{" "}
          se encuentra ABIERTA.
        </div>

        {coverState?.changed_at && (
          <div
            style={{
              marginTop: 7,
              fontSize: 13,
              fontWeight: 700,
              color: "#991b1b",
              opacity: 0.88,
            }}
          >
            Abierta desde: {fmtDT(coverState.changed_at)}
          </div>
        )}
      </div>

      <div
        style={{
          marginLeft: "auto",
          padding: "8px 12px",
          borderRadius: 999,
          background: "#dc2626",
          color: "#fff",
          fontWeight: 900,
          fontSize: 12,
          whiteSpace: "nowrap",
        }}
      >
        TAPA ABIERTA
      </div>
    </div>
  )}

{/* KPIs */}
{hasCalibratedKpis && (
  <div className="calibration-switch-wrap">
    <span className="calibration-switch-label">Ver dato:</span>

    <div className="calibration-switch">
      <button
        type="button"
        className={calibrationMode === "raw" ? "active" : ""}
        onClick={() => setCalibrationMode("raw")}
      >
        Original
      </button>

      <button
        type="button"
        className={calibrationMode === "corrected" ? "active" : ""}
        onClick={() => setCalibrationMode("corrected")}
      >
        Corregido
      </button>
    </div>
  </div>
)}

{activeType === "EnergiaElectrica" && (() => {
  const voltagePhases = [1, 2];
  const voltageL3Number = getElectricalKpiNumber(kpis, "VOLTAGE_L3");

  if (voltageL3Number !== null && Math.abs(voltageL3Number) > 1) {
    voltagePhases.push(3);
  }

  const voltageItems = voltagePhases.map((phase) => ({
    phase,
    item: getElectricalMainValue(`VOLTAGE_L${phase}`),
  }));

  const currentChannels = visibleElectricalChannels.map((channel) => ({
    channel,
    item: getElectricalMainValue(`CURRENT_CH${channel}`),
  }));

  const temperature = getElectricalMainValue("TEMP");

  const groupStatus = (...items) => {
    const statuses = items
      .map((item) => item?.status)
      .filter(Boolean);

    if (statuses.includes("online")) return "online";
    if (statuses.includes("stale")) return "stale";
    return "offline";
  };

  const latestTs = (...items) => {
    const values = items
      .map((item) => toMsSafe(item?.ts))
      .filter(Number.isFinite);

    return values.length ? Math.max(...values) : null;
  };

  const voltageStatus = groupStatus(...voltageItems.map(({ item }) => item));
  const voltageLatestTs = latestTs(...voltageItems.map(({ item }) => item));
  const currentStatus = groupStatus(...currentChannels.map(({ item }) => item));
  const currentLatestTs = latestTs(...currentChannels.map(({ item }) => item));

  const mainCardStyle = {
    position: "relative",
    overflow: "hidden",
    borderRadius: "20px",
    padding: "24px",
    minHeight: "215px",
    border: "1px solid rgba(148,163,184,.25)",
    boxShadow: "0 12px 30px rgba(15,23,42,.08)",
  };

  const smallValueBoxStyle = {
    background: "rgba(255,255,255,.72)",
    border: "1px solid rgba(148,163,184,.20)",
    borderRadius: "14px",
    padding: "16px",
  };

  const channelLabelStyle = {
    fontSize: "12px",
    fontWeight: 900,
    letterSpacing: ".7px",
    color: "#64748b",
    marginBottom: "5px",
  };

  const mainValueStyle = {
    fontSize: "34px",
    lineHeight: 1,
    fontWeight: 950,
    color: "#0f172a",
    letterSpacing: "-1px",
  };

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
        gap: "18px",
        marginTop: hasCalibratedKpis ? "0" : "16px",
        marginBottom: "22px",
      }}
    >
      {/* VOLTAJE */}
      <div
        className="card glass"
        style={{
          ...mainCardStyle,
          background: "linear-gradient(145deg, rgba(239,246,255,.98), rgba(255,255,255,.98))",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "5px",
            background: "linear-gradient(90deg, #2563eb, #38bdf8)",
          }}
        />

        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "12px",
          }}
        >
          <div>
            <div
              style={{
                fontSize: "12px",
                fontWeight: 900,
                color: "#2563eb",
                letterSpacing: "1px",
                textTransform: "uppercase",
              }}
            >
              Parámetro eléctrico
            </div>
            <div
              style={{
                marginTop: "3px",
                fontSize: "21px",
                fontWeight: 950,
                color: "#0f172a",
              }}
            >
              Voltaje
            </div>
            <div
              style={{
                fontSize: "12px",
                fontWeight: 700,
                color: "#64748b",
                marginTop: "2px",
              }}
            >
              Tensión RMS
            </div>
          </div>

          <StatusDot status={voltageStatus} showLabel />
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
            gap: "12px",
            marginTop: "22px",
          }}
        >
          {voltageItems.map(({ phase, item }) => (
            <div key={`voltage-l${phase}`} style={smallValueBoxStyle}>
              <div style={channelLabelStyle}>L{phase}</div>
              <div style={mainValueStyle}>{item.value}</div>
            </div>
          ))}
        </div>

        <div
          style={{
            marginTop: "15px",
            fontSize: "11px",
            fontWeight: 700,
            color: "#94a3b8",
          }}
        >
          Última lectura:{" "}
          {voltageLatestTs ? fmtDT(voltageLatestTs) : "sin datos"}
        </div>
      </div>

      {/* CORRIENTE */}
      <div
        className="card glass"
        style={{
          ...mainCardStyle,
          background: "linear-gradient(145deg, rgba(255,251,235,.98), rgba(255,255,255,.98))",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "5px",
            background: "linear-gradient(90deg, #d97706, #fbbf24)",
          }}
        />

        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "12px",
          }}
        >
          <div>
            <div
              style={{
                fontSize: "12px",
                fontWeight: 900,
                color: "#d97706",
                letterSpacing: "1px",
                textTransform: "uppercase",
              }}
            >
              Parámetro eléctrico
            </div>
            <div
              style={{
                marginTop: "3px",
                fontSize: "21px",
                fontWeight: 950,
                color: "#0f172a",
              }}
            >
              Corriente
            </div>
            <div
              style={{
                fontSize: "12px",
                fontWeight: 700,
                color: "#64748b",
                marginTop: "2px",
              }}
            >
              Corriente RMS
            </div>
          </div>

          <StatusDot status={currentStatus} showLabel />
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
            gap: "12px",
            marginTop: "22px",
          }}
        >
          {currentChannels.map(({ channel, item }) => (
            <div key={`current-ch${channel}`} style={smallValueBoxStyle}>
              <div style={channelLabelStyle}>CH{channel}</div>
              <div style={mainValueStyle}>{item.value}</div>
            </div>
          ))}
        </div>

        <div
          style={{
            marginTop: "15px",
            fontSize: "11px",
            fontWeight: 700,
            color: "#94a3b8",
          }}
        >
          Última lectura:{" "}
          {currentLatestTs ? fmtDT(currentLatestTs) : "sin datos"}
        </div>
      </div>

      {/* TEMPERATURA */}
      <div
        className="card glass"
        style={{
          ...mainCardStyle,
          background: "linear-gradient(145deg, rgba(255,247,237,.98), rgba(255,255,255,.98))",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "5px",
            background: "linear-gradient(90deg, #ea580c, #fb7185)",
          }}
        />

        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "12px",
          }}
        >
          <div>
            <div
              style={{
                fontSize: "12px",
                fontWeight: 900,
                color: "#ea580c",
                letterSpacing: "1px",
                textTransform: "uppercase",
              }}
            >
              Condición térmica
            </div>
            <div
              style={{
                marginTop: "3px",
                fontSize: "21px",
                fontWeight: 950,
                color: "#0f172a",
              }}
            >
              Temperatura
            </div>
            <div
              style={{
                fontSize: "12px",
                fontWeight: 700,
                color: "#64748b",
                marginTop: "2px",
              }}
            >
              Sonda externa
            </div>
          </div>

          <StatusDot status={temperature.status} showLabel />
        </div>

        <div
          style={{
            marginTop: "24px",
            background: "rgba(255,255,255,.72)",
            border: "1px solid rgba(148,163,184,.20)",
            borderRadius: "14px",
            padding: "18px",
          }}
        >
          <div
            style={{
              fontSize: "48px",
              lineHeight: 1,
              fontWeight: 950,
              letterSpacing: "-1.5px",
              color: "#0f172a",
            }}
          >
            {temperature.value}
          </div>
        </div>

        <div
          style={{
            marginTop: "15px",
            fontSize: "11px",
            fontWeight: 700,
            color: "#94a3b8",
          }}
        >
          Última lectura:{" "}
          {temperature.ts ? fmtDT(temperature.ts) : "sin datos"}
        </div>
      </div>
    </div>
  );
})()}

{activeType === "EnergiaElectrica" && (() => {
  const read = (variable) => getElectricalMainValue(variable);

  const voltagePhases = [1, 2];
  const voltageL3Number = getElectricalKpiNumber(kpis, "VOLTAGE_L3");
  if (voltageL3Number !== null && Math.abs(voltageL3Number) > 1) {
    voltagePhases.push(3);
  }

  const channelVars = (prefix) =>
    visibleElectricalChannels.map((ch) => `${prefix}_CH${ch}`);

  const sectionMeta = (...variables) => {
    const items = variables.map(read);
    const statuses = items.map((item) => item?.status).filter(Boolean);

    const status = statuses.includes("online")
      ? "online"
      : statuses.includes("stale")
      ? "stale"
      : "offline";

    const timestamps = items
      .map((item) => toMsSafe(item?.ts))
      .filter(Number.isFinite);

    return {
      status,
      ts: timestamps.length ? Math.max(...timestamps) : null,
    };
  };

  const powerMeta = sectionMeta(
    ...channelVars("ACTIVE_POWER"),
    ...channelVars("REACTIVE_POWER"),
    ...channelVars("APPARENT_POWER"),
    ...channelVars("POWER_FACTOR")
  );

  const energyMeta = sectionMeta(
    ...channelVars("ACTIVE_ENERGY"),
    ...channelVars("REACTIVE_ENERGY"),
    ...channelVars("APPARENT_ENERGY")
  );

  const qualityMeta = sectionMeta(
    ...channelVars("THDI"),
    ...voltagePhases.map((phase) => `THDV_L${phase}`)
  );

  const cellStyle = {
    padding: "13px 14px",
    borderRadius: "12px",
    background: "rgba(255,255,255,.72)",
    border: "1px solid rgba(148,163,184,.20)",
  };

  const rowLabelStyle = {
    fontSize: "12px",
    fontWeight: 900,
    color: "#64748b",
  };

  const valueStyle = {
    marginTop: "3px",
    fontSize: "22px",
    lineHeight: 1.1,
    fontWeight: 950,
    color: "#0f172a",
  };

  const sectionStyle = {
    position: "relative",
    overflow: "hidden",
    borderRadius: "20px",
    padding: "22px",
    border: "1px solid rgba(148,163,184,.25)",
    boxShadow: "0 10px 28px rgba(15,23,42,.07)",
  };

  const ElectricalChannelRow = ({ label, prefix }) => (
    <div style={{ display: "grid", gap: "8px" }}>
      <div
        style={{
          fontSize: "13px",
          fontWeight: 900,
          color: "#475569",
        }}
      >
        {label}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(105px, 1fr))",
          gap: "10px",
        }}
      >
        {visibleElectricalChannels.map((channel) => {
          const item = read(`${prefix}_CH${channel}`);

          return (
            <div key={`${prefix}-ch${channel}`} style={cellStyle}>
              <div style={rowLabelStyle}>CH{channel}</div>
              <div style={valueStyle}>{item.value}</div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const ElectricalPhaseRow = ({ label, prefix }) => (
    <div style={{ display: "grid", gap: "8px" }}>
      <div
        style={{
          fontSize: "13px",
          fontWeight: 900,
          color: "#475569",
        }}
      >
        {label}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(105px, 1fr))",
          gap: "10px",
        }}
      >
        {voltagePhases.map((phase) => {
          const item = read(`${prefix}_L${phase}`);

          return (
            <div key={`${prefix}-l${phase}`} style={cellStyle}>
              <div style={rowLabelStyle}>L{phase}</div>
              <div style={valueStyle}>{item.value}</div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const SectionHeader = ({ title, meta }) => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: "12px",
        marginBottom: "18px",
      }}
    >
      <div
        style={{
          fontSize: "22px",
          fontWeight: 950,
          color: "#0f172a",
        }}
      >
        {title}
      </div>

      <StatusDot status={meta.status} showLabel />
    </div>
  );

  const SectionFooter = ({ meta }) => (
    <div
      style={{
        marginTop: "15px",
        fontSize: "11px",
        fontWeight: 700,
        color: "#94a3b8",
      }}
    >
      Última lectura: {meta.ts ? fmtDT(meta.ts) : "sin datos"}
    </div>
  );

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))",
        gap: "18px",
        alignItems: "start",
        marginBottom: "22px",
      }}
    >
      {/* POTENCIA */}
      <div
        className="card glass"
        style={{
          ...sectionStyle,
          background: "linear-gradient(145deg, rgba(239,246,255,.96), rgba(255,255,255,.98))",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "5px",
            background: "linear-gradient(90deg, #2563eb, #06b6d4)",
          }}
        />

        <SectionHeader title="Potencia" meta={powerMeta} />

        <div style={{ display: "grid", gap: "14px" }}>
          <ElectricalChannelRow label="Activa" prefix="ACTIVE_POWER" />
          <ElectricalChannelRow label="Reactiva" prefix="REACTIVE_POWER" />
          <ElectricalChannelRow label="Aparente" prefix="APPARENT_POWER" />
          <ElectricalChannelRow label="Factor de potencia" prefix="POWER_FACTOR" />
        </div>

        <SectionFooter meta={powerMeta} />
      </div>

      {/* ENERGÍA */}
      <div
        className="card glass"
        style={{
          ...sectionStyle,
          background: "linear-gradient(145deg, rgba(239,246,255,.96), rgba(255,255,255,.98))",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "5px",
            background: "linear-gradient(90deg, #2563eb, #06b6d4)",
          }}
        />

        <SectionHeader title="Energía" meta={energyMeta} />

        <div style={{ display: "grid", gap: "14px" }}>
          <ElectricalChannelRow label="Activa" prefix="ACTIVE_ENERGY" />
          <ElectricalChannelRow label="Reactiva" prefix="REACTIVE_ENERGY" />
          <ElectricalChannelRow label="Aparente" prefix="APPARENT_ENERGY" />
        </div>

        <SectionFooter meta={energyMeta} />
      </div>

      {/* DISTORSIÓN ARMÓNICA */}
      <div
        className="card glass"
        style={{
          ...sectionStyle,
          background: "linear-gradient(145deg, rgba(239,246,255,.96), rgba(255,255,255,.98))",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "5px",
            background: "linear-gradient(90deg, #2563eb, #06b6d4)",
          }}
        />

        <SectionHeader title="Distorsión armónica" meta={qualityMeta} />

        <div style={{ display: "grid", gap: "14px" }}>
          <ElectricalChannelRow label="THDi" prefix="THDI" />
          <ElectricalPhaseRow label="THDv" prefix="THDV" />
        </div>

        <SectionFooter meta={qualityMeta} />
      </div>
    </div>
  );
})()}

<div className={`sd-summary-layout ${isSingleProcessVariable ? "sd-single-variable" : ""}`}>
<div className="kpis sd-dynamic-kpis" style={{ marginTop: hasCalibratedKpis ? 0 : 16 }}>
  {visibleKpiVars
    .filter((v) => {

      // Las demás aplicaciones funcionan exactamente igual.
      if (activeType !== "EnergiaElectrica") {
        return true;
      }

      // En Energía eléctrica ocultamos de las tarjetas
      // individuales todo lo que ya está agrupado.
      return !ELECTRICAL_GROUPED_KPI_VARS.has(
        normalizeVariableDisplayKey(v)
      );
    })
    .map((v) => {

const vv = canonVar(v);

const kpiRow = kpis?.[vv] || null;

const ts =
  kpiRow?.ts ??
  kpiRow?.fecha_ms ??
  kpiRow?.fecha ??
  kpiRow?.time ??
  null;

const st = computeContextStatus(ts);

// La sonda K10 devuelve una lectura anormalmente alta cuando no hay
// suficiente flujo de agua. El dato se conserva en alert_value, pero sigue
// excluido de la visualización, gráficas y estadísticas mediante el límite
// definido en SENSOR_DISPLAY_MAX_BY_VARIABLE.
const ecK10Limit = SENSOR_DISPLAY_MAX_BY_VARIABLE.EC_K10;
const isLowFlow =
  vv === "EC_K10" &&
  kpiRow?.display_invalid === true &&
  Number(kpiRow?.alert_value) > ecK10Limit;

const displayStatus = isLowFlow ? "lowflow" : st;

const ttl = isLowFlow
  ? `Flujo bajo detectado. Lectura recibida: ${Number(
      kpiRow?.alert_value
    ).toFixed(0)} µS/cm. ${ts ? `Última lectura: ${fmtDT(ts)}` : ""}`
  : ts
  ? `Estado: ${st}. Última lectura: ${fmtDT(ts)}`
  : `Estado: ${st}`;

const displayValue =
  kpiRow?.calibrated === true
    ? calibrationMode === "corrected"
      ? kpiRow.corrected_value ?? kpiRow.value
      : kpiRow.raw_value ?? kpiRow.value
    : kpiRow?.value;

const certNumber = kpiRow?.calibration_certificate_number || null;

const isMeteorologicalWindDirection =
  activeType === "Meteorologica" && vv === "WIND_DIRECTION";

const windDegrees = isMeteorologicalWindDirection
  ? normalizeWindDegrees(displayValue)
  : null;

// La tarjeta principal muestra únicamente la última lectura y su estado.
// Las estadísticas históricas permanecen en la gráfica correspondiente.

return (

      <div
        key={v}
        className={`kpi card glass sd-kpi-refined ${st === "offline" ? "sd-kpi-is-offline" : ""} ${isLowFlow ? "sd-kpi-is-lowflow" : ""}`}
        style={
          isLowFlow
            ? {
                border: "3px solid #dc2626",
                background: "linear-gradient(180deg, #fff1f2 0%, #fef2f2 100%)",
                boxShadow: "0 0 0 4px rgba(220,38,38,0.14), 0 10px 28px rgba(220,38,38,0.18)",
              }
            : undefined
        }
      >
        {isLowFlow ? (
          <div
            title={ttl}
            style={{
              background: "#dc2626",
              color: "#ffffff",
              fontSize: "20px",
              fontWeight: 900,
              textAlign: "center",
              padding: "10px 12px",
              borderRadius: "10px",
              marginBottom: "10px",
              letterSpacing: "0.5px",
              lineHeight: 1.15,
            }}
          >
            ⚠ ALERTA: FLUJO BAJO
          </div>
        ) : (
          <StatusDot status={displayStatus} title={ttl} showLabel />
        )}

        <div
          className="kpi-title variable-display-name"
          title={`Variable interna: ${vv}`}
          style={{
            whiteSpace: "normal",
            lineHeight: 1.15,
            overflowWrap: "anywhere",
          }}
        >
          {getVariableDisplayName(vv, activeType)}
        </div>

       <div
         style={{
           display: "flex",
           alignItems: "center",
           justifyContent: "space-between",
           gap: 12,
         }}
       >
         <div className="kpi-value">
           {isLowFlow
             ? `--.-- ${getVariableUnit(vv, activeType)}`.trim()
             : st === "offline"
             ? `--.-- ${getVariableUnit(vv, activeType)}`.trim()
             : displayValue != null
             ? `${formatSensorNumber(
                 displayValue,
                 vv
               )} ${getVariableUnit(vv, activeType)}`.trim()
             : `--.-- ${getVariableUnit(vv, activeType)}`.trim()}
         </div>

         {isMeteorologicalWindDirection &&
           st !== "offline" &&
           windDegrees !== null && (
             <WindCompass degrees={windDegrees} size={50} />
           )}
       </div>

        {isLowFlow && (
          <div
            style={{
              marginTop: "8px",
              padding: "7px 10px",
              borderRadius: "8px",
              background: "rgba(220,38,38,0.10)",
              color: "#991b1b",
              fontSize: "14px",
              fontWeight: 900,
              textAlign: "center",
              letterSpacing: "0.2px",
            }}
          >
            REVISAR FLUJO DE AGUA
          </div>
        )}

        <div className="kpi-time">
          {ts ? fmtDT(ts) : "sin datos"}
        </div>


{kpiRow?.calibrated === true && (
  <div
    className={
      calibrationMode === "corrected"
        ? "kpi-calibration-note"
        : "kpi-calibration-note raw"
    }
    title={
      calibrationMode === "corrected"
        ? `Original: ${formatSensorNumber(
            kpiRow.raw_value,
            vv
          )} ${getVariableUnit(vv, activeType)} · Corrección: ${Number(
            kpiRow.correction || 0
          ).toFixed(4)} · Certificado: ${certNumber || "—"}`
        : `Dato original del sensor. Certificado disponible: ${certNumber || "—"}`
    }
  >
    {calibrationMode === "corrected"
      ? `Corregido por calibración · ${certNumber || "Certificado"}`
      : "Valor original del sensor"}
      </div>
      )}
      </div>
    );
  })}
</div>

      {/* HERO + MAPA */}
      <div className="hero-grid">
<div className="card glass hero-card">
  <div className="sd-hero-section-heading">Información del sensor</div>
  {(() => {
    const st = sensorStatus ?? computeContextStatus(lastTsOverall);
    const ts = statusTs ?? lastTsOverall ?? null;
    const ttl = ts ? `Última lectura: ${fmtDT(ts)}` : "Sin datos";
    return <StatusDot status={st} title={ttl} />;
  })()}

  {batteryValue !== null && (
    <div className="battery-badge" title={`Batería: ${batteryValue.toFixed(0)}%`}>
      <span className="battery-icon">🔋</span>
      <span>{batteryValue.toFixed(0)}%</span>
    </div>
  )}

  <img

            className="hero-photo"
            src={getSensorImage(sensorInfo)}
            alt={sensorInfo?.name || sensorInfo?.id || "sensor"}
            onError={(e) => (e.currentTarget.src = "/images/default-sensor.png")}
          />
          <div style={{ minWidth: 0 }}>

          <div className="sensor-title-row">
  <h3
    style={{
      margin: 0,
      fontSize: 20,
      fontWeight: 900,
      color: "#0f172a",
    }}
  >
    {sensorInfo?.name || sensorInfo?.id || "—"}
  </h3>

  {sensorInfo && selectedDevice && !isAllDevices && (
    <button
      type="button"
      className="sensor-edit-btn"
      onClick={openSensorEditor}
      title="Editar nombre y ubicación del sensor"
    >
      <span aria-hidden="true">✎</span>
      <span>Editar</span>
    </button>
  )}
</div>         

            <div className="meta-list">
              <div>
                <strong>ID:</strong> {sensorInfo?.id || "—"}
              </div>
              <div>
                <strong>Lat/Lng:</strong> {sensorInfo?.lat ?? "—"} /{" "}
                {sensorInfo?.lng ?? "—"}
              </div>
              <div>
                <strong>Tipo:</strong> {getTypeDisplayName(activeType)}
              </div>
            </div>
            <div className="chips">
          {(activeType === "EnergiaElectrica" ? visibleAvailableVars : availableVars).map((v) => (
            <span
              key={v}
              className="chip variable-display-name"
              title={`Variable interna: ${v}`}
              style={{
                whiteSpace: "normal",
                lineHeight: 1.2,
                overflowWrap: "anywhere",
              }}
            >
              {getVariableDisplayLabel(v, true, "", activeType)}
            </span>
          ))}
          </div>
          </div>
        </div>

        <div id="sensorMapSection" className="card glass map-card sd-sensor-map-card">
          <div className="sd-map-heading" aria-hidden="true">Ubicación del sensor</div>
          <div id="sensorMap" />
          {!hasUsableCoordinates(sensorInfo) && (
            <div className="sd-map-unavailable" role="status">
              <span className="sd-map-unavailable-icon" aria-hidden="true">⌖</span>
              <strong>Ubicación no disponible</strong>
              <span>Este sensor no tiene coordenadas configuradas.</span>
            </div>
          )}
        </div>
      </div>
</div>{/* Fin de la distribucion de tarjetas y detalles */}
{/* Gráficas */}
<div className="charts-grid sd-dynamic-charts">
  {visibleAvailableVars
    .filter((v) => canonVar(v) !== "BAT")
    .map((v) => {
      const vKey = canonVar(v);
      const currentRange = getChartRange(vKey);
      const stats = chartStats[vKey];
      const customRange =
        chartCustomRanges[vKey] || getDefaultCustomRange(vKey);
      const customRangeIsOpen = chartCustomRangeOpen[vKey] === true;

      return (
        <div
          key={vKey}
          className="card glass chart-card"
        >
          <div className="chart-header">
            <div
              className="chart-title variable-display-name"
              title={`Variable interna: ${vKey}`}
              style={{
                lineHeight: 1.2,
                overflowWrap: "anywhere",
              }}
            >
              <span className="sd-chart-mini-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 3v18h18" />
                  <path d="m6 16 5-6 4 3 5-7" />
                </svg>
              </span>
              {activeType === "Meteorologica" && vKey === "RAINFALL"
                ? `${getChartVariableDisplayName(vKey, activeType)} · ${getVariableUnit(vKey, activeType)}`
                : getVariableDisplayLabel(vKey, true, "", activeType)}
            </div>

         <div
  className="range-tabs chart-range-tabs"
  role="tablist"
  aria-label={`Rango de ${getChartVariableDisplayName(vKey, activeType)}`}
>
  {RANGE_OPTIONS.map((r) => (
    <button
      key={r}
      type="button"
      className={`range-btn ${
        currentRange === r ? "active" : ""
      }`}
      onClick={() =>
        changeChartRange(vKey, r)
      }
      aria-pressed={currentRange === r}
    >
      {r}
    </button>
  ))}

  <button
    type="button"
    className={`range-btn ${currentRange === "custom" ? "active" : ""}`}
    onClick={() => toggleCustomChartRange(vKey)}
    aria-pressed={currentRange === "custom"}
    title="Seleccionar fecha y hora desde/hasta"
  >
    Rango
  </button>

{false && selectedDevice === "24e124445f287697" && vKey === "CLORO_LIBRE" && (
  <button
    type="button"
    className="range-btn"
    onClick={() => openLabComparison(vKey)}
    title="Comparar muestras de laboratorio con la última lectura en línea"
    style={{
      background: "#0f766e",
      color: "#fff",
      fontWeight: 900,
    }}
  >
    Comparación de laboratorio
  </button>
)}

  <button
    type="button"
    className="range-btn"
    onClick={() => zoomInChart(vKey)}
    title="Acercar gráfica"
  >
    Zoom +
  </button>

  <button
    type="button"
    className="range-btn"
    onClick={() => zoomOutChart(vKey)}
    title="Alejar gráfica"
  >
    Zoom -
  </button>

  <button
    type="button"
    className="range-btn"
    onClick={() => resetChartZoom(vKey)}
    title="Restablecer zoom"
  >
    Reset
  </button>
</div>

        </div>

        {customRangeIsOpen ? (
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              gap: 10,
              flexWrap: "wrap",
              margin: "10px 0 4px",
              padding: "10px 12px",
              border: "1px solid rgba(148,163,184,.28)",
              borderRadius: 12,
              background: "rgba(248,250,252,.72)",
            }}
          >
            <label
              style={{
                display: "grid",
                gap: 4,
                minWidth: 190,
                flex: "1 1 190px",
              }}
            >
              <span className="small" style={{ fontWeight: 800 }}>
                Desde
              </span>
              <input
                className="input"
                type="datetime-local"
                value={customRange.from || ""}
                onChange={(event) =>
                  updateCustomChartRange(vKey, "from", event.target.value)
                }
              />
            </label>

            <label
              style={{
                display: "grid",
                gap: 4,
                minWidth: 190,
                flex: "1 1 190px",
              }}
            >
              <span className="small" style={{ fontWeight: 800 }}>
                Hasta
              </span>
              <input
                className="input"
                type="datetime-local"
                value={customRange.to || ""}
                onChange={(event) =>
                  updateCustomChartRange(vKey, "to", event.target.value)
                }
              />
            </label>

            <button
              type="button"
              className="btn-solid btn-download"
              onClick={() => applyCustomChartRange(vKey)}
              style={{ minHeight: 40 }}
            >
              Aplicar
            </button>

            {chartCustomRangeMessages[vKey] ? (
              <div
                className="msg"
                style={{ flexBasis: "100%", fontSize: 13 }}
              >
                {chartCustomRangeMessages[vKey]}
              </div>
            ) : null}
          </div>
        ) : null}

        {stats && (
            <div className="chart-stats-panel">
              <div>
                <span>Promedio</span>
                <strong>{fmtStatValue(stats.mean, vKey, activeType)}</strong>
              </div>

              <div>
                <span>Mín / Máx</span>
                <strong>
                  {fmtStatValue(stats.min, vKey, activeType)} / {fmtStatValue(stats.max, vKey, activeType)}
                </strong>
              </div>

              <div>
                <span>Desv. estándar</span>
                <strong>{fmtStatValue(stats.std, vKey, activeType)}</strong>
              </div>

              <div>
                <span>Último valor</span>
                <strong>{fmtStatValue(stats.last, vKey, activeType)}</strong>
              </div>

              <div>
                <span>Datos</span>
                <strong>{stats.count}</strong>
              </div>
            </div>
          )}

          <canvas id={idForVar(vKey)} />
        </div>
      );
    })}
</div>
  </>
)}

     {/* ===== MODAL COMPARACIÓN DE LABORATORIO ===== */}
{labComparisonOpen && (
  <div
    role="dialog"
    aria-modal="true"
    aria-label="Comparación de laboratorio"
    style={{
      position: "fixed",
      inset: 0,
      zIndex: 5000,
      background: "rgba(15,23,42,.58)",
      backdropFilter: "blur(5px)",
      WebkitBackdropFilter: "blur(5px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 20,
    }}
  >
    <div
      style={{
        width: "min(900px, 100%)",
        maxHeight: "calc(100vh - 40px)",
        overflowY: "auto",
        background: "#f8fafc",
        borderRadius: 22,
        border: "1px solid rgba(148,163,184,.30)",
        boxShadow: "0 28px 70px rgba(15,23,42,.30)",
      }}
    >
      {/* ENCABEZADO */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          padding: "18px 22px",
          background: "rgba(255,255,255,.97)",
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 20,
              fontWeight: 900,
              color: "#0f172a",
            }}
          >
            Comparación de laboratorio
          </div>

          <div
            style={{
              marginTop: 4,
              fontSize: 13,
              color: "#64748b",
              fontWeight: 700,
            }}
          >
            {sensorInfo?.name || selectedDevice}
            {" · "}
            {getVariableDisplayLabel(
              labComparisonVariable,
              true,
              "",
              activeType
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={closeLabComparison}
          disabled={labComparisonLoading}
          style={{
            width: 38,
            height: 38,
            borderRadius: 10,
            border: "1px solid #cbd5e1",
            background: "#fff",
            color: "#334155",
            fontSize: 22,
            fontWeight: 900,
            cursor: labComparisonLoading ? "not-allowed" : "pointer",
          }}
        >
          ×
        </button>
      </div>

      <div
        style={{
          padding: 22,
          display: "grid",
          gap: 20,
        }}
      >
        {/* MUESTRAS */}
        <div
          style={{
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: 16,
            padding: 18,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
              marginBottom: 16,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 900,
                  color: "#0f172a",
                }}
              >
                Muestras de laboratorio
              </div>

              <div
                style={{
                  marginTop: 3,
                  fontSize: 12,
                  color: "#64748b",
                  fontWeight: 700,
                }}
              >
                Ingrese mínimo 5 y máximo 10 muestras.
              </div>
            </div>

            <div
              style={{
                padding: "7px 11px",
                borderRadius: 999,
                background: "#ecfeff",
                border: "1px solid #a5f3fc",
                color: "#0f766e",
                fontWeight: 900,
                fontSize: 12,
              }}
            >
              {labSamples.length} muestras
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 12,
            }}
          >
            {labSamples.map((value, index) => (
              <div
                key={index}
                style={{
                  display: "grid",
                  gap: 6,
                }}
              >
                <label
                  style={{
                    fontSize: 12,
                    fontWeight: 900,
                    color: "#475569",
                  }}
                >
                  Muestra {index + 1}
                </label>

                <div
                  style={{
                    display: "flex",
                    gap: 6,
                  }}
                >
                  <input
                    type="number"
                    step="any"
                    value={value}
                    disabled={labComparisonLoading}
                    onChange={(event) =>
                      updateLabSample(index, event.target.value)
                    }
                    placeholder="0.00"
                    style={{
                      width: "100%",
                      minWidth: 0,
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: "1px solid #cbd5e1",
                      background: "#fff",
                      color: "#0f172a",
                      fontWeight: 800,
                      outline: "none",
                    }}
                  />

                  {labSamples.length > 5 && (
                    <button
                      type="button"
                      disabled={labComparisonLoading}
                      onClick={() => removeLabSample(index)}
                      title="Eliminar muestra"
                      style={{
                        width: 38,
                        flex: "0 0 38px",
                        border: "1px solid #fecaca",
                        borderRadius: 10,
                        background: "#fff1f2",
                        color: "#be123c",
                        fontWeight: 900,
                        cursor: "pointer",
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div
            style={{
              marginTop: 16,
              display: "flex",
              gap: 10,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              disabled={
                labComparisonLoading ||
                labSamples.length >= 10
              }
              onClick={addLabSample}
              style={{
                border: "1px solid #99f6e4",
                borderRadius: 10,
                padding: "9px 13px",
                background:
                  labSamples.length >= 10
                    ? "#f1f5f9"
                    : "#f0fdfa",
                color:
                  labSamples.length >= 10
                    ? "#94a3b8"
                    : "#0f766e",
                fontWeight: 900,
                cursor:
                  labSamples.length >= 10
                    ? "not-allowed"
                    : "pointer",
              }}
            >
              + Agregar muestra
            </button>

            <button
              type="button"
              disabled={labComparisonLoading}
              onClick={submitLabComparison}
              style={{
                border: "none",
                borderRadius: 10,
                padding: "10px 16px",
                background: "#0f766e",
                color: "#fff",
                fontWeight: 900,
                cursor: labComparisonLoading
                  ? "not-allowed"
                  : "pointer",
                opacity: labComparisonLoading ? 0.65 : 1,
              }}
            >
              {labComparisonLoading
                ? "Calculando..."
                : "Calcular comparación"}
            </button>
          </div>

          {labComparisonMsg && (
            <div
              style={{
                marginTop: 14,
                padding: "10px 12px",
                borderRadius: 10,
                background: labComparisonResult
                  ? "#ecfdf5"
                  : "#fff7ed",
                border: labComparisonResult
                  ? "1px solid #a7f3d0"
                  : "1px solid #fed7aa",
                color: labComparisonResult
                  ? "#047857"
                  : "#c2410c",
                fontSize: 13,
                fontWeight: 800,
              }}
            >
              {labComparisonMsg}
            </div>
          )}
        </div>

        {/* RESULTADOS */}
        {labComparisonResult && (
          <>
            <div
              style={{
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: 16,
                padding: 18,
              }}
            >
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 900,
                  color: "#0f172a",
                  marginBottom: 14,
                }}
              >
                Resultados de laboratorio
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit, minmax(150px, 1fr))",
                  gap: 12,
                }}
              >
                {[
                  [
                    "Cantidad",
                    labComparisonResult.sample_count,
                  ],
                  [
                    "Promedio",
                    fmtStatValue(
                      Number(labComparisonResult.sample_mean),
                      labComparisonVariable,
                      activeType
                    ),
                  ],
                  [
                    "Mínimo",
                    fmtStatValue(
                      Number(labComparisonResult.sample_min),
                      labComparisonVariable,
                      activeType
                    ),
                  ],
                  [
                    "Máximo",
                    fmtStatValue(
                      Number(labComparisonResult.sample_max),
                      labComparisonVariable,
                      activeType
                    ),
                  ],
                  [
                    "Rango",
                    fmtStatValue(
                      Number(labComparisonResult.sample_range),
                      labComparisonVariable,
                      activeType
                    ),
                  ],
                  [
                    "Desv. estándar",
                    fmtStatValue(
                      Number(
                        labComparisonResult.standard_deviation
                      ),
                      labComparisonVariable,
                      activeType
                    ),
                  ],
                  [
                    "Varianza",
                    Number.isFinite(
                      Number(labComparisonResult.variance)
                    )
                      ? Number(
                          labComparisonResult.variance
                        ).toFixed(6)
                      : "—",
                  ],
                  [
                    "RSD",
                    Number.isFinite(
                      Number(labComparisonResult.rsd_percent)
                    )
                      ? `${Number(
                          labComparisonResult.rsd_percent
                        ).toFixed(2)} %`
                      : "No calculable",
                  ],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    style={{
                      background: "#f8fafc",
                      border: "1px solid #e2e8f0",
                      borderRadius: 12,
                      padding: 12,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        color: "#64748b",
                        fontWeight: 900,
                        textTransform: "uppercase",
                      }}
                    >
                      {label}
                    </div>

                    <div
                      style={{
                        marginTop: 5,
                        fontSize: 17,
                        color: "#0f172a",
                        fontWeight: 900,
                      }}
                    >
                      {value}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div
              style={{
                background: "#fff",
                border: "1px solid #bae6fd",
                borderRadius: 16,
                padding: 18,
              }}
            >
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 900,
                  color: "#0f172a",
                  marginBottom: 14,
                }}
              >
                Laboratorio vs. equipo en línea
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit, minmax(170px, 1fr))",
                  gap: 12,
                }}
              >
                <div
                  style={{
                    padding: 14,
                    borderRadius: 12,
                    background: "#f8fafc",
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      color: "#64748b",
                      fontWeight: 800,
                    }}
                  >
                    Promedio laboratorio
                  </div>

                  <strong
                    style={{
                      display: "block",
                      marginTop: 5,
                      fontSize: 20,
                      color: "#0f172a",
                    }}
                  >
                    {fmtStatValue(
                      Number(labComparisonResult.sample_mean),
                      labComparisonVariable,
                      activeType
                    )}
                  </strong>
                </div>

                <div
                  style={{
                    padding: 14,
                    borderRadius: 12,
                    background: "#eff6ff",
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      color: "#1d4ed8",
                      fontWeight: 800,
                    }}
                  >
                    Equipo en línea
                  </div>

                  <strong
                    style={{
                      display: "block",
                      marginTop: 5,
                      fontSize: 20,
                      color: "#1d4ed8",
                    }}
                  >
                    {fmtStatValue(
                      Number(labComparisonResult.online_value),
                      labComparisonVariable,
                      activeType
                    )}
                  </strong>

                  <div
                    style={{
                      marginTop: 4,
                      fontSize: 11,
                      color: "#64748b",
                    }}
                  >
                    {fmtDT(
                      labComparisonResult.online_timestamp
                    )}
                  </div>
                </div>

                <div
                  style={{
                    padding: 14,
                    borderRadius: 12,
                    background: "#fff7ed",
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      color: "#c2410c",
                      fontWeight: 800,
                    }}
                  >
                    Diferencia absoluta
                  </div>

                  <strong
                    style={{
                      display: "block",
                      marginTop: 5,
                      fontSize: 20,
                      color: "#c2410c",
                    }}
                  >
                    {fmtStatValue(
                      Number(
                        labComparisonResult.absolute_difference
                      ),
                      labComparisonVariable,
                      activeType
                    )}
                  </strong>
                </div>

                <div
                  style={{
                    padding: 14,
                    borderRadius: 12,
                    background: "#fff7ed",
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      color: "#c2410c",
                      fontWeight: 800,
                    }}
                  >
                    Diferencia porcentual
                  </div>

                  <strong
                    style={{
                      display: "block",
                      marginTop: 5,
                      fontSize: 20,
                      color: "#c2410c",
                    }}
                  >
                    {Number.isFinite(
                      Number(
                        labComparisonResult.percentage_difference
                      )
                    )
                      ? `${Number(
                          labComparisonResult.percentage_difference
                        ).toFixed(2)} %`
                      : "No calculable"}
                  </strong>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  </div>
)}

      {/* ===== MODAL ALERTAS ===== */}
      {canUseAlarms && alertsOpen && (
        <div
          className="modal-overlay alerts-ui-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Alertas y umbrales"
        >
          <style>{`
            .alerts-ui-overlay {
              padding: 24px;
              background: rgba(15, 23, 42, .58);
              backdrop-filter: blur(5px);
              -webkit-backdrop-filter: blur(5px);
            }

            .alerts-ui-card {
              width: min(960px, calc(100vw - 32px));
              max-width: 960px !important;
              max-height: min(900px, calc(100vh - 40px));
              container-type: inline-size;
              border: 1px solid rgba(148, 163, 184, .28);
              border-radius: 22px !important;
              overflow: hidden;
              background: #f8fafc;
              box-shadow: 0 28px 70px rgba(15, 23, 42, .28);
            }

            .alerts-ui-head {
              position: sticky;
              top: 0;
              z-index: 4;
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 16px;
              padding: 18px 22px;
              border-bottom: 1px solid #e2e8f0;
              background: rgba(255, 255, 255, .97);
              backdrop-filter: blur(12px);
              -webkit-backdrop-filter: blur(12px);
            }

            .alerts-ui-title-wrap {
              display: flex;
              align-items: center;
              gap: 13px;
              min-width: 0;
            }

            .alerts-ui-title-icon {
              width: 42px;
              height: 42px;
              flex: 0 0 42px;
              display: grid;
              place-items: center;
              border-radius: 13px;
              background: linear-gradient(135deg, #dbeafe, #eff6ff);
              border: 1px solid #bfdbfe;
              font-size: 20px;
              box-shadow: 0 5px 15px rgba(37, 99, 235, .10);
            }

            .alerts-ui-title {
              color: #0f172a;
              font-size: 18px;
              font-weight: 900;
              line-height: 1.15;
              letter-spacing: -.01em;
              overflow-wrap: anywhere;
            }

            .alerts-ui-subtitle {
              margin-top: 4px;
              color: #64748b;
              font-size: 12.5px;
              font-weight: 650;
            }

            .alerts-ui-body {
              padding: 18px !important;
              display: grid;
              gap: 14px;
              background:
                radial-gradient(circle at 100% 0%, rgba(59, 130, 246, .06), transparent 30%),
                #f8fafc;
            }

            .alerts-ui-section {
              margin: 0 !important;
              padding: 18px !important;
              border: 1px solid #e2e8f0 !important;
              border-radius: 16px !important;
              background: #ffffff !important;
              box-shadow: 0 5px 18px rgba(15, 23, 42, .045);
            }

            .alerts-ui-section-head {
              display: flex;
              align-items: flex-start;
              justify-content: space-between;
              gap: 14px;
              margin-bottom: 14px;
            }

            .alerts-ui-section-title-wrap {
              display: flex;
              align-items: flex-start;
              gap: 10px;
              min-width: 0;
            }

            .alerts-ui-section-icon {
              width: 34px;
              height: 34px;
              flex: 0 0 34px;
              display: grid;
              place-items: center;
              border-radius: 10px;
              background: #f1f5f9;
              border: 1px solid #e2e8f0;
              font-size: 16px;
            }

            .alerts-ui-section-title {
              color: #0f172a;
              font-size: 15px;
              font-weight: 900;
              line-height: 1.2;
            }

            .alerts-ui-section-copy {
              margin-top: 3px;
              color: #64748b;
              font-size: 12.5px;
              line-height: 1.45;
            }

            .alerts-ui-badge {
              display: inline-flex;
              align-items: center;
              gap: 6px;
              min-height: 28px;
              padding: 5px 10px;
              border-radius: 999px;
              border: 1px solid #dbe4ef;
              background: #f8fafc;
              color: #475569;
              font-size: 12px;
              font-weight: 800;
              white-space: nowrap;
            }

            .alerts-ui-badge.is-on {
              border-color: #bbf7d0;
              background: #f0fdf4;
              color: #166534;
            }

            .alerts-ui-badge.is-off {
              border-color: #fecaca;
              background: #fff7f7;
              color: #b91c1c;
            }

            .alerts-ui-dot {
              width: 7px;
              height: 7px;
              border-radius: 50%;
              background: currentColor;
              box-shadow: 0 0 0 3px currentColor;
              opacity: .55;
            }

            .alerts-ui-channel-grid {
              display: grid;
              grid-template-columns: repeat(2, minmax(0, 1fr));
              gap: 10px;
              margin-bottom: 16px;
            }

            .alerts-ui-channel {
              position: relative;
              display: flex;
              align-items: center;
              gap: 11px;
              min-height: 66px;
              padding: 11px 12px;
              border: 1px solid #e2e8f0;
              border-radius: 13px;
              background: #f8fafc;
              cursor: pointer;
              transition: border-color .18s ease, box-shadow .18s ease, transform .18s ease, background .18s ease;
            }

            .alerts-ui-channel:hover {
              transform: translateY(-1px);
              border-color: #bfdbfe;
              box-shadow: 0 6px 15px rgba(37, 99, 235, .07);
            }

            .alerts-ui-channel:has(input:checked) {
              border-color: #93c5fd;
              background: #eff6ff;
              box-shadow: inset 0 0 0 1px rgba(59, 130, 246, .07);
            }

            .alerts-ui-channel input {
              width: 18px;
              height: 18px;
              flex: 0 0 18px;
              accent-color: #2563eb;
            }

            .alerts-ui-channel-main {
              min-width: 0;
            }

            .alerts-ui-channel-name {
              color: #0f172a;
              font-size: 13px;
              font-weight: 900;
            }

            .alerts-ui-channel-desc {
              margin-top: 2px;
              color: #64748b;
              font-size: 11.5px;
              line-height: 1.3;
            }

            .alerts-ui-recipient-grid {
              display: grid;
              grid-template-columns: minmax(0, 1fr);
              gap: 12px;
            }

            .alerts-ui-recipient-card {
              padding: 14px;
              border: 1px solid #e2e8f0;
              border-radius: 14px;
              background: #fbfdff;
            }

            .alerts-ui-recipient-head {
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 10px;
              margin-bottom: 10px;
            }

            .alerts-ui-recipient-title {
              color: #0f172a;
              font-size: 13px;
              font-weight: 900;
            }

            .alerts-ui-counter {
              color: #64748b;
              font-size: 11.5px;
              font-weight: 750;
              white-space: nowrap;
            }

            .alerts-ui-counter.is-full {
              padding: 4px 8px;
              border: 1px solid #fde68a;
              border-radius: 999px;
              background: #fffbeb;
              color: #92400e;
              font-weight: 850;
            }

            .alerts-ui-limit-reached {
              display: flex;
              align-items: flex-start;
              gap: 9px;
              padding: 10px 11px;
              border: 1px solid #bbf7d0;
              border-radius: 11px;
              background: #f0fdf4;
              color: #166534;
            }

            .alerts-ui-limit-reached-icon {
              width: 22px;
              height: 22px;
              flex: 0 0 22px;
              display: grid;
              place-items: center;
              border-radius: 50%;
              background: #dcfce7;
              font-size: 12px;
              font-weight: 900;
            }

            .alerts-ui-limit-reached-title {
              font-size: 12px;
              font-weight: 900;
              line-height: 1.2;
            }

            .alerts-ui-limit-reached-copy {
              margin-top: 2px;
              font-size: 11px;
              line-height: 1.35;
              color: #3f6212;
            }

            .alerts-ui-list {
              display: grid;
              gap: 7px;
              margin-bottom: 10px;
            }

            .alerts-ui-list-item {
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 10px;
              padding: 9px 10px;
              border: 1px solid #e2e8f0;
              border-radius: 10px;
              background: #fff;
            }

            .alerts-ui-list-label {
              min-width: 0;
              color: #334155;
              font-size: 12.5px;
              font-weight: 750;
              overflow-wrap: anywhere;
            }

            .alerts-ui-empty {
              display: flex;
              align-items: center;
              min-height: 54px;
              padding: 10px 12px;
              margin-bottom: 10px;
              border: 1px dashed #cbd5e1;
              border-radius: 11px;
              color: #64748b;
              background: #f8fafc;
              font-size: 12px;
            }

            .alerts-ui-input-row {
              display: grid;
              grid-template-columns: minmax(0, 1fr) auto;
              gap: 8px;
              align-items: center;
            }

            .alerts-ui-input-row .input {
              width: 100%;
              min-width: 0;
              min-height: 40px;
            }

            .alerts-ui-helper {
              margin-top: 7px;
              color: #64748b;
              font-size: 11.5px;
              line-height: 1.4;
            }

            .alerts-ui-telegram-list {
              display: grid;
              gap: 8px;
              margin-bottom: 12px;
            }

            .alerts-ui-telegram-item {
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 10px;
              padding: 10px 12px;
              border: 1px solid #e2e8f0;
              border-radius: 11px;
              background: #f8fafc;
            }

            .alerts-ui-actions {
              display: flex;
              align-items: center;
              gap: 9px;
              flex-wrap: wrap;
            }

            .alerts-ui-code {
              display: inline-flex;
              align-items: center;
              gap: 6px;
              min-height: 38px;
              padding: 7px 12px;
              border-radius: 10px;
              border: 1px dashed #93c5fd;
              background: #eff6ff;
              color: #1d4ed8;
              font-size: 12px;
              font-weight: 800;
            }

            .alerts-ui-code strong {
              font-size: 16px;
              letter-spacing: .08em;
            }

            .alerts-ui-footer-action {
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 12px;
              margin-top: 14px;
              padding-top: 14px;
              border-top: 1px solid #e2e8f0;
            }

            .alerts-ui-footer-copy {
              color: #64748b;
              font-size: 11.5px;
              line-height: 1.4;
              max-width: 550px;
            }

            .alerts-ui-variable-list {
              display: grid;
              gap: 8px;
            }

            .alerts-ui-variable-row {
              display: grid;
              grid-template-columns: minmax(170px, 1.2fr) minmax(0, .9fr) minmax(0, .9fr) max-content;
              align-items: end;
              gap: 10px;
              padding: 11px 12px;
              border: 1px solid #e2e8f0;
              border-radius: 12px;
              background: #fbfdff;
            }

            .alerts-ui-variable-name {
              align-self: center;
              color: #0f172a;
              font-size: 12.5px;
              font-weight: 900;
              line-height: 1.3;
              overflow-wrap: anywhere;
            }

            .alerts-ui-field {
              display: grid;
              gap: 5px;
              min-width: 0;
            }

            .alerts-ui-field-label {
              color: #64748b;
              font-size: 10.5px;
              font-weight: 800;
              text-transform: uppercase;
              letter-spacing: .04em;
            }

            .alerts-ui-field .input {
              width: 100%;
              min-width: 0;
              max-width: 100%;
              min-height: 39px;
              box-sizing: border-box !important;
            }

            .alerts-ui-unit {
              color: #64748b;
              font-size: 11px;
              font-weight: 750;
            }

            .alerts-ui-variable-actions {
              display: flex;
              align-items: center;
              justify-content: flex-end;
              align-self: end;
              gap: 7px;
              flex-wrap: nowrap;
              min-width: max-content;
            }

            .alerts-ui-variable-actions .btn-solid {
              flex: 0 0 auto;
              margin: 0 !important;
              padding-left: 12px;
              padding-right: 12px;
              position: static !important;
              transform: none !important;
              white-space: nowrap;
            }

            .alerts-ui-test {
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 14px;
              margin-top: 14px;
              padding: 13px 14px;
              border: 1px solid #fed7aa;
              border-radius: 13px;
              background: #fffaf5;
            }

            .alerts-ui-test-title {
              color: #9a3412;
              font-size: 12.5px;
              font-weight: 900;
            }

            .alerts-ui-test-copy {
              margin-top: 2px;
              color: #9a3412;
              opacity: .78;
              font-size: 11.5px;
            }

            .alerts-ui-card .btn-solid {
              min-height: 38px;
              border-radius: 10px;
              font-weight: 850;
            }

            .alerts-ui-card .btn-ghost {
              background: #fff;
              border: 1px solid #dbe4ef;
            }

            .alerts-ui-card .msg {
              border-radius: 10px;
            }

            @container (max-width: 820px) {
              .alerts-ui-variable-row {
                grid-template-columns: minmax(150px, 1fr) minmax(0, 1fr) minmax(0, 1fr);
              }
              .alerts-ui-variable-actions {
                grid-column: 1 / -1;
                justify-content: flex-end;
                min-width: 0;
              }
            }

            @container (max-width: 620px) {
              .alerts-ui-variable-row { grid-template-columns: 1fr 1fr; }
              .alerts-ui-variable-name,
              .alerts-ui-variable-actions { grid-column: 1 / -1; }
            }

            @media (max-width: 780px) {
              .alerts-ui-overlay { padding: 10px; }
              .alerts-ui-card {
                width: calc(100vw - 20px);
                max-height: calc(100vh - 20px);
                border-radius: 16px !important;
              }
              .alerts-ui-head { padding: 14px; align-items: flex-start; }
              .alerts-ui-title { font-size: 16px; }
              .alerts-ui-title-icon { width: 36px; height: 36px; flex-basis: 36px; }
              .alerts-ui-body { padding: 12px !important; }
              .alerts-ui-section { padding: 14px !important; }
              .alerts-ui-channel-grid,
              .alerts-ui-recipient-grid { grid-template-columns: 1fr; }
              .alerts-ui-variable-row { grid-template-columns: 1fr 1fr; }
              .alerts-ui-variable-name,
              .alerts-ui-variable-actions { grid-column: 1 / -1; }
              .alerts-ui-footer-action,
              .alerts-ui-test { align-items: flex-start; flex-direction: column; }
            }

            @media (max-width: 520px) {
              .alerts-ui-head { gap: 10px; }
              .alerts-ui-subtitle { display: none; }
              .alerts-ui-section-head { flex-direction: column; }
              .alerts-ui-input-row { grid-template-columns: 1fr; }
              .alerts-ui-variable-row { grid-template-columns: 1fr; }
              .alerts-ui-variable-name,
              .alerts-ui-variable-actions { grid-column: auto; }
              .alerts-ui-actions { align-items: stretch; }
              .alerts-ui-actions .btn-solid { flex: 1 1 auto; }
            }
          `}</style>

          <div className="modal-card alerts-modal-card alerts-ui-card">
            <div className="modal-head alerts-ui-head">
              <div className="alerts-ui-title-wrap">
                <div className="alerts-ui-title-icon" aria-hidden="true">🔔</div>
                <div style={{ minWidth: 0 }}>
                  <div className="alerts-ui-title">Configuración de alertas</div>
                  <div className="alerts-ui-subtitle">
                    {sensorInfo?.name || selectedDevice}
                    {selectedDevice ? ` · ID ${selectedDevice}` : ""}
                  </div>
                </div>
              </div>

              <button type="button" className="btn-solid btn-ghost" onClick={closeAlerts}>
                Cerrar
              </button>
            </div>

            <div className="modal-body alerts-modal-body alerts-ui-body">
              <div className="modal-section alerts-ui-section">
                <div className="alerts-ui-section-head">
                  <div className="alerts-ui-section-title-wrap">
                    <div className="alerts-ui-section-icon" aria-hidden="true">✈️</div>
                    <div>
                      <div className="alerts-ui-section-title">Telegram</div>
                      <div className="alerts-ui-section-copy">
                        Vincula las cuentas que podrán recibir las alertas de este sensor.
                      </div>
                    </div>
                  </div>

                  <span className={`alerts-ui-badge ${tgStatus === "linked" ? "is-on" : "is-off"}`}>
                    <span className="alerts-ui-dot" />
                    {tgStatus === "linked" ? "Conectado" : "No conectado"}
                  </span>
                </div>

                {telegramAccounts.length > 0 ? (
                  <div className="alerts-ui-telegram-list">
                    {telegramAccounts.map((account, index) => (
                      <div key={account.id} className="alerts-ui-telegram-item">
                        <div style={{ minWidth: 0 }}>
                          <div style={{ color: "#0f172a", fontWeight: 900, fontSize: 12.5 }}>
                            Cuenta de Telegram {index + 1}
                          </div>
                          <div className="small" style={{ overflowWrap: "anywhere" }}>
                            Chat ID: {account.chat_id_masked}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn-solid btn-ghost"
                          onClick={() => removeTelegramAccount(account.id)}
                          disabled={tgLoading}
                        >
                          Eliminar
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="alerts-ui-empty">
                    Todavía no hay cuentas de Telegram vinculadas.
                  </div>
                )}

                <div className="alerts-ui-actions">
                  <button
                    type="button"
                    className="btn-solid btn-alerts"
                    onClick={generateTelegramCode}
                    disabled={
                      tgLoading ||
                      telegramAccounts.length >= maxNotificationRecipients
                    }
                  >
                    {tgLoading ? "..." : "Generar código"}
                  </button>

                  <button
                    type="button"
                    className="btn-solid btn-download"
                    onClick={confirmTelegram}
                    disabled={
                      tgLoading ||
                      !tgCode ||
                      telegramAccounts.length >= maxNotificationRecipients
                    }
                    title={!tgCode ? "Primero genera el código" : "Confirmar leyendo mensajes del bot"}
                  >
                    {tgLoading ? "..." : "Confirmar vínculo"}
                  </button>

                  {tgCode && (
                    <span className="alerts-ui-code">
                      Código <strong>{tgCode}</strong>
                    </span>
                  )}

                  <span className="alerts-ui-badge">
                    {telegramAccounts.length} de {maxNotificationRecipients} vinculadas
                  </span>
                </div>

                {telegramAccounts.length >= maxNotificationRecipients ? (
                  <div className="small" style={{ marginTop: 10 }}>
                    Alcanzaste el máximo de cuentas permitidas.
                  </div>
                ) : null}

                {tgMsg ? <div className="msg" style={{ marginTop: 10 }}>{tgMsg}</div> : null}
                <div className="alerts-ui-helper">
                  Genera un código, envíalo al bot de Telegram y luego selecciona “Confirmar vínculo”.
                </div>
              </div>

              <div className="modal-section alerts-ui-section">
                <div className="alerts-ui-section-head">
                  <div className="alerts-ui-section-title-wrap">
                    <div className="alerts-ui-section-icon" aria-hidden="true">📣</div>
                    <div>
                      <div className="alerts-ui-section-title">Canales de notificación</div>
                      <div className="alerts-ui-section-copy">
                        Selecciona por qué medios se enviarán las alertas y configura sus destinatarios.
                      </div>
                    </div>
                  </div>
                </div>

                {prefLoading ? (
                  <div className="msg">Cargando preferencias...</div>
                ) : (
                  <>
                    <div className="alerts-ui-channel-grid">
                      <label className="alerts-ui-channel">
                        <input
                          type="checkbox"
                          checked={telegramEnabled}
                          onChange={(e) => setTelegramEnabled(e.target.checked)}
                        />
                        <div className="alerts-ui-channel-main">
                          <div className="alerts-ui-channel-name">Telegram</div>
                          <div className="alerts-ui-channel-desc">Notificación instantánea al bot</div>
                        </div>
                      </label>

                      <label className="alerts-ui-channel">
                        <input
                          type="checkbox"
                          checked={emailEnabled}
                          onChange={(e) => setEmailEnabled(e.target.checked)}
                        />
                        <div className="alerts-ui-channel-main">
                          <div className="alerts-ui-channel-name">Correo electrónico</div>
                          <div className="alerts-ui-channel-desc">Aviso a las direcciones registradas</div>
                        </div>
                      </label>

                    </div>

                    <div className="alerts-ui-recipient-grid">
                      <div className="alerts-ui-recipient-card">
                        <div className="alerts-ui-recipient-head">
                          <div className="alerts-ui-recipient-title">Destinatarios por correo</div>
                          <div
                            className={`alerts-ui-counter ${
                              alertEmails.length >= maxEmailRecipients ? "is-full" : ""
                            }`}
                          >
                            {alertEmails.length} / {maxEmailRecipients}
                            {alertEmails.length >= maxEmailRecipients ? " · completo" : ""}
                          </div>
                        </div>

                        {alertEmails.length > 0 ? (
                          <div className="alerts-ui-list">
                            {alertEmails.map((email) => (
                              <div key={email} className="alerts-ui-list-item">
                                <div className="alerts-ui-list-label">{email}</div>
                                <button
                                  type="button"
                                  className="btn-solid btn-ghost"
                                  onClick={() => removeAlertEmail(email)}
                                  disabled={prefSaving}
                                >
                                  Eliminar
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="alerts-ui-empty">
                            No hay destinatarios de correo configurados.
                          </div>
                        )}

                        {alertEmails.length >= maxEmailRecipients ? (
                          <div className="alerts-ui-limit-reached">
                            <span className="alerts-ui-limit-reached-icon" aria-hidden="true">✓</span>
                            <div>
                              <div className="alerts-ui-limit-reached-title">Límite de correos alcanzado</div>
                              <div className="alerts-ui-limit-reached-copy">
                                Elimina un correo registrado si deseas agregar uno diferente.
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="alerts-ui-input-row">
                            <input
                              className="input email-input"
                              type="email"
                              value={newAlertEmail}
                              onChange={(e) => setNewAlertEmail(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  addAlertEmail();
                                }
                              }}
                              placeholder="correo@empresa.com"
                              disabled={prefSaving}
                            />

                            <button
                              type="button"
                              className="btn-solid btn-alerts"
                              onClick={addAlertEmail}
                              disabled={prefSaving || !newAlertEmail.trim()}
                            >
                              Agregar
                            </button>
                          </div>
                        )}
                      </div>

                    </div>

                    <div className="alerts-ui-footer-action">
                      <div className="alerts-ui-footer-copy">
                        Telegram permite hasta {maxNotificationRecipients} cuentas. Correo electrónico permite hasta {maxEmailRecipients} destinatarios {maxEmailRecipients === 7 ? "por sensor para Rocnarf" : "por usuario"}.
                      </div>

                      <button
                        type="button"
                        className="btn-solid btn-download"
                        onClick={saveNotificationPreferences}
                        disabled={prefSaving}
                      >
                        {prefSaving ? "Guardando..." : "Guardar preferencias"}
                      </button>
                    </div>

                    {prefMsg ? (
                      <div className="msg" style={{ marginTop: 10 }}>
                        {prefMsg}
                      </div>
                    ) : null}
                  </>
                )}
              </div>

              <div className="modal-section alerts-ui-section">
                <div className="alerts-ui-section-head">
                  <div className="alerts-ui-section-title-wrap">
                    <div className="alerts-ui-section-icon" aria-hidden="true">📈</div>
                    <div>
                      <div className="alerts-ui-section-title">Límites visuales de la gráfica</div>
                      <div className="alerts-ui-section-copy">
                        Define líneas de referencia para facilitar la lectura. Estos valores no generan alertas.
                      </div>
                    </div>
                  </div>
                </div>

                {chartLimitMessages.__global ? (
                  <div className="msg" style={{ marginBottom: 10 }}>
                    {chartLimitMessages.__global}
                  </div>
                ) : null}

                <div className="alerts-ui-variable-list">
                  {availableVars.map((v) => {
                    const vKey = canonVar(v);
                    const hasVisualLimit =
                      Object.prototype.hasOwnProperty.call(chartLimits, vKey) ||
                      Object.prototype.hasOwnProperty.call(chartMinLimits, vKey);

                    return (
                      <div key={`visual-limit-${v}`} className="alerts-ui-variable-row">
                        <div className="alerts-ui-variable-name" title={`Variable interna: ${v}`}>
                          {getVariableDisplayLabel(v, true, "", activeType)}
                        </div>

                        <label className="alerts-ui-field">
                          <span className="alerts-ui-field-label">Mínimo visual</span>
                          <input
                            className="input"
                            type="number"
                            step="any"
                            value={chartMinLimitInputs[vKey] ?? ""}
                            onChange={(event) =>
                              setChartMinLimitInputs((current) => ({
                                ...current,
                                [vKey]: event.target.value,
                              }))
                            }
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                event.preventDefault();
                                saveChartLimit(vKey);
                              }
                            }}
                            placeholder="Sin límite"
                            disabled={chartLimitSaving[vKey] === true}
                            aria-label={`Mínimo visual de ${getVariableDisplayName(vKey)}`}
                          />
                        </label>

                        <label className="alerts-ui-field">
                          <span className="alerts-ui-field-label">Máximo visual</span>
                          <input
                            className="input"
                            type="number"
                            step="any"
                            value={chartLimitInputs[vKey] ?? ""}
                            onChange={(event) =>
                              setChartLimitInputs((current) => ({
                                ...current,
                                [vKey]: event.target.value,
                              }))
                            }
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                event.preventDefault();
                                saveChartLimit(vKey);
                              }
                            }}
                            placeholder="Sin límite"
                            disabled={chartLimitSaving[vKey] === true}
                            aria-label={`Máximo visual de ${getVariableDisplayName(vKey)}`}
                          />
                        </label>

                        <div className="alerts-ui-variable-actions">
                          <button
                            type="button"
                            className="btn-solid btn-download"
                            onClick={() => saveChartLimit(vKey)}
                            disabled={
                              chartLimitSaving[vKey] === true ||
                              (
                                !String(chartMinLimitInputs[vKey] ?? "").trim() &&
                                !String(chartLimitInputs[vKey] ?? "").trim()
                              )
                            }
                          >
                            {chartLimitSaving[vKey] === true ? "..." : "Guardar"}
                          </button>

                          {hasVisualLimit ? (
                            <button
                              type="button"
                              className="btn-solid btn-ghost"
                              onClick={() => removeChartLimit(vKey)}
                              disabled={chartLimitSaving[vKey] === true}
                            >
                              Quitar
                            </button>
                          ) : null}
                        </div>

                        {chartLimitMessages[vKey] ? (
                          <div className="msg" style={{ gridColumn: "1 / -1", fontSize: 12 }}>
                            {chartLimitMessages[vKey]}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="modal-section alerts-ui-section">
                <div className="alerts-ui-section-head">
                  <div className="alerts-ui-section-title-wrap">
                    <div className="alerts-ui-section-icon" aria-hidden="true">🎚️</div>
                    <div>
                      <div className="alerts-ui-section-title">Umbrales de alerta</div>
                      <div className="alerts-ui-section-copy">
                        Configura los valores que realmente disparan las notificaciones. Puedes dejar un campo vacío para trabajar sin ese límite.
                      </div>
                    </div>
                  </div>
                </div>

                {thrLoading ? (
                  <div className="msg">Cargando umbrales...</div>
                ) : (
                  <div className="alerts-ui-variable-list">
                    {availableVars.map((v) => (
                      <div key={v} className="alerts-ui-variable-row">
                        <div
                          className="alerts-ui-variable-name"
                          title={`Variable interna: ${v}`}
                        >
                          {getVariableDisplayLabel(v, true, "", activeType)}
                        </div>

                        <label className="alerts-ui-field">
                          <span className="alerts-ui-field-label">Mínimo de alerta</span>
                          <input
                            className="input"
                            type="number"
                            step="any"
                            placeholder="Sin mínimo"
                            value={thrMap?.[v]?.min ?? ""}
                            onChange={(e) =>
                              setThrMap((m) => ({
                                ...m,
                                [v]: { ...(m[v] || {}), min: e.target.value },
                              }))
                            }
                          />
                        </label>

                        <label className="alerts-ui-field">
                          <span className="alerts-ui-field-label">Máximo de alerta</span>
                          <input
                            className="input"
                            type="number"
                            step="any"
                            placeholder="Sin máximo"
                            value={thrMap?.[v]?.max ?? ""}
                            onChange={(e) =>
                              setThrMap((m) => ({
                                ...m,
                                [v]: { ...(m[v] || {}), max: e.target.value },
                              }))
                            }
                          />
                        </label>

                        <div className="alerts-ui-variable-actions">
                          <button
                            type="button"
                            className="btn-solid btn-download"
                            onClick={() => saveThreshold(v)}
                            disabled={!!thrSaving[v]}
                          >
                            {thrSaving[v] ? "..." : "Guardar"}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="alerts-ui-test">
                  <div>
                    <div className="alerts-ui-test-title">Prueba de funcionamiento</div>
                    <div className="alerts-ui-test-copy">
                      Envía una notificación de prueba inmediata a los canales habilitados. No crea ni modifica alarmas reales.
                    </div>
                  </div>

                  <div className="alerts-ui-actions">
                    <button
                      type="button"
                      className="btn-solid btn-alerts"
                      onClick={testAlertNow}
                      disabled={testLoading || !selectedDevice}
                      title="Envía una prueba inmediata sin modificar las alarmas reales"
                    >
                      {testLoading ? "..." : "Probar alerta ahora"}
                    </button>

                    {testMsg ? <div className="msg">{testMsg}</div> : null}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL LOGO DE REPORTES ===== */}
{canManageUsers && logoModalOpen && (
  <div
    className="modal-overlay"
    role="dialog"
    aria-modal="true"
    aria-label="Logo para reportes PDF"
    onClick={handleCloseTeamLogo}
  >
    <div
      className="modal-card"
      style={{ maxWidth: 620 }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="modal-head">
        <div className="modal-title">Logo para reportes PDF</div>

        <button
          type="button"
          className="btn-solid btn-ghost"
          onClick={handleCloseTeamLogo}
          disabled={logoLoading}
        >
          Cerrar
        </button>
      </div>

      <div className="modal-body">
        <div className="modal-section">
          <div className="section-title">Empresa / equipo</div>

          <div className="small">
            {logoLoading
              ? "Cargando..."
              : teamLogoInfo?.team_name || teamLogoInfo?.teamName || "Equipo no disponible"}
          </div>
        </div>

        <div className="modal-section">
          <div className="section-title">Logo actual</div>

          <div className="logo-preview-box">
            {teamLogoSrc ? (
              <img
                src={teamLogoSrc}
                alt="Logo actual del equipo"
                style={{
                  maxWidth: "100%",
                  maxHeight: 150,
                  objectFit: "contain",
                }}
              />
            ) : (
              <span className="small">
                Este equipo aún no tiene logo configurado. El menú lateral mostrará la inicial del cliente.
              </span>
            )}
          </div>
        </div>

        <div className="modal-section">
          <div className="section-title">Nuevo logo</div>

          <div className="row">
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="logo-file-input"
              onChange={handleSelectLogoFile}
              disabled={logoLoading}
            />
          </div>

          {logoPreview && (
            <div className="logo-preview-box" style={{ marginTop: 12 }}>
              <img src={logoPreview} alt="Vista previa del logo" />
            </div>
          )}

          <div className="logo-actions" style={{ marginTop: 12 }}>
            <button
              type="button"
              className="btn-solid btn-download"
              onClick={handleUploadTeamLogo}
              disabled={logoLoading || !logoFile}
            >
              {logoLoading ? "Guardando..." : "Guardar logo"}
            </button>

            <button
              type="button"
              className="btn-solid btn-ghost"
              onClick={handleDeleteTeamLogo}
              disabled={logoLoading || !teamLogoValue}
            >
              Quitar logo
            </button>
          </div>

          {logoMsg ? (
            <div className="msg" style={{ marginTop: 12 }}>
              {logoMsg}
            </div>
          ) : null}

          <div className="small" style={{ marginTop: 10 }}>
            Formatos permitidos: JPG, PNG o WEBP. Tamaño máximo: 2 MB.
          </div>
        </div>
      </div>
    </div>
  </div>
)}



      <ObservationsModal
        open={
          observationsOpen &&
          isMolinosUser &&
          !!selectedDevice &&
          !isAllDevices
        }
        onClose={() => setObservationsOpen(false)}
        deviceId={selectedDevice}
        sensorName={sensorInfo?.name || selectedDevice}
        viewAs={viewAs}
        token={token}
        apiRoot={API_ROOT}
      />

         {/* ===== EDITOR LATERAL DEL SENSOR ===== */}
{sensorEditorOpen && (
  <div
    className="sensor-editor-backdrop"
    role="presentation"
    onMouseDown={(e) => {
      if (e.target === e.currentTarget) {
        closeSensorEditor();
      }
    }}
  >
    <aside
      className="sensor-editor-drawer"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sensor-editor-title"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="sensor-editor-head">
        <div>
          <div
            id="sensor-editor-title"
            className="sensor-editor-title"
          >
            Editar sensor
          </div>

          <div className="sensor-editor-subtitle">
            {selectedDevice}
          </div>
        </div>

        <button
          type="button"
          className="sensor-editor-close"
          onClick={closeSensorEditor}
          disabled={sensorEditorSaving}
          aria-label="Cerrar"
        >
          ✕
        </button>
      </div>

      <form
        className="sensor-editor-form"
        onSubmit={(e) => {
          e.preventDefault();
          saveSensorProfile();
        }}
      >
        {/* NOMBRE */}
        <div className="sensor-editor-section">
          <label
            className="sensor-editor-label"
            htmlFor="sensor-editor-name"
          >
            Nombre del sensor
          </label>

          <input
            id="sensor-editor-name"
            className="sensor-editor-input"
            type="text"
            maxLength={120}
            value={sensorEditorName}
            onChange={(e) =>
              setSensorEditorName(e.target.value)
            }
            placeholder="Ej. Cámara fría #1"
            disabled={sensorEditorSaving}
          />

          <div className="sensor-editor-help">
            Este será el nombre que aparecerá en el panel.
          </div>
        </div>

        {/* UBICACIÓN */}
        <div className="sensor-editor-section">
          <div className="sensor-editor-section-title">
            Ubicación
          </div>

          <div className="sensor-editor-help">
            Opcional. Puedes dejar el sensor sin una ubicación
            configurada.
          </div>

          <div className="sensor-location-options">
            <label
              className={`sensor-location-option ${
                sensorEditorLocationSource === "none"
                  ? "active"
                  : ""
              }`}
            >
              <input
                type="radio"
                name="sensor-location-source"
                value="none"
                checked={
                  sensorEditorLocationSource === "none"
                }
                onChange={() => {
                  setSensorEditorLocationSource("none");
                  setSensorEditorMsg("");
                }}
                disabled={sensorEditorSaving}
              />

              <span className="sensor-location-option-body">
                <strong>Sin ubicación</strong>
                <small>
                  El sensor funcionará normalmente sin mostrar
                  coordenadas.
                </small>
              </span>
            </label>

            <label
              className={`sensor-location-option ${
                sensorEditorLocationSource === "manual"
                  ? "active"
                  : ""
              }`}
            >
              <input
                type="radio"
                name="sensor-location-source"
                value="manual"
                checked={
                  sensorEditorLocationSource === "manual"
                }
                onChange={() => {
                  setSensorEditorLocationSource("manual");
                  setSensorEditorMsg("");
                }}
                disabled={sensorEditorSaving}
              />

              <span className="sensor-location-option-body">
                <strong>Configurar ubicación</strong>
                <small>
                  Ingresa las coordenadas del lugar donde se
                  encuentra instalado.
                </small>
              </span>
            </label>
          </div>

          {sensorEditorLocationSource === "manual" && (
            <div className="sensor-editor-coordinates">
              <div>
                <label
                  className="sensor-editor-label"
                  htmlFor="sensor-editor-lat"
                >
                  Latitud
                </label>

                <input
                  id="sensor-editor-lat"
                  className="sensor-editor-input"
                  type="number"
                  step="any"
                  inputMode="decimal"
                  value={sensorEditorLat}
                  onChange={(e) =>
                    setSensorEditorLat(e.target.value)
                  }
                  placeholder="-2.179740"
                  disabled={sensorEditorSaving}
                />
              </div>

              <div>
                <label
                  className="sensor-editor-label"
                  htmlFor="sensor-editor-lon"
                >
                  Longitud
                </label>

                <input
                  id="sensor-editor-lon"
                  className="sensor-editor-input"
                  type="number"
                  step="any"
                  inputMode="decimal"
                  value={sensorEditorLon}
                  onChange={(e) =>
                    setSensorEditorLon(e.target.value)
                  }
                  placeholder="-79.853900"
                  disabled={sensorEditorSaving}
                />
              </div>
            </div>
          )}
        </div>

        {/* FOTO DEL LUGAR DE INSTALACIÓN */}
        <div className="sensor-editor-section">
          <div className="sensor-editor-section-title">
            Foto del lugar de instalación
          </div>

          <div className="sensor-editor-help">
            Opcional. Puedes agregar una fotografía para identificar
            físicamente dónde quedó instalado el sensor.
          </div>

          <input
            ref={sensorEditorPhotoInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files?.[0];

              if (file) {
                handleSensorEditorPhotoFile(file);
              }
            }}
          />

          {sensorEditorPhotoLoading ? (
            <div className="sensor-editor-photo-loading">
              Consultando fotografía...
            </div>
          ) : (
            <>
              {!sensorEditorPhotoPreviewUrl && !sensorEditorPhotoUrl ? (
                <button
                  type="button"
                  className="sensor-editor-photo-drop"
                  onClick={() =>
                    sensorEditorPhotoInputRef.current?.click()
                  }
                  onDragOver={(e) => {
                    e.preventDefault();
                  }}
                  onDrop={(e) => {
                    e.preventDefault();

                    const file = e.dataTransfer.files?.[0];

                    if (file) {
                      handleSensorEditorPhotoFile(file);
                    }
                  }}
                  disabled={sensorEditorSaving}
                >
                  <span className="sensor-editor-photo-upload-icon">
                    ↑
                  </span>

                  <strong>Haz clic para subir una imagen</strong>

                  <small>o arrástrala aquí</small>

                  <span className="sensor-editor-photo-formats">
                    JPG, JPEG, PNG o WEBP · Máx. 5 MB
                  </span>
                </button>
              ) : (
                <div className="sensor-editor-photo-card">
                  <div className="sensor-editor-photo-image-wrap">
                    <img
                      src={getSensorInstallationPhotoSrc(
                        sensorEditorPhotoPreviewUrl ||
                          sensorEditorPhotoUrl
                      )}
                      alt="Lugar de instalación"
                      className="sensor-editor-photo-image"
                    />

                    {sensorEditorPhotoPreviewUrl && (
                      <div className="sensor-editor-photo-new-badge">
                        Nueva foto
                      </div>
                    )}
                  </div>

                  <div className="sensor-editor-photo-info">
                    <strong>
                      {sensorEditorPhotoPreviewUrl
                        ? sensorEditorPhotoFile?.name ||
                          "Nueva fotografía"
                        : "Foto de instalación actual"}
                    </strong>

                    {sensorEditorPhotoFile && (
                      <small>
                        {(
                          sensorEditorPhotoFile.size /
                          1024 /
                          1024
                        ).toFixed(2)}{" "}
                        MB
                      </small>
                    )}

                    {!sensorEditorPhotoPreviewUrl && (
                      <small>
                        Fotografía actualmente guardada
                      </small>
                    )}

                    <div className="sensor-editor-photo-buttons">
                      <button
                        type="button"
                        className="sensor-editor-photo-change"
                        onClick={() =>
                          sensorEditorPhotoInputRef.current?.click()
                        }
                        disabled={sensorEditorSaving}
                      >
                        Cambiar imagen
                      </button>

                      {sensorEditorPhotoPreviewUrl && (
                        <button
                          type="button"
                          className="sensor-editor-photo-cancel"
                          onClick={clearSensorEditorPhotoSelection}
                          disabled={sensorEditorSaving}
                        >
                          Descartar selección
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {sensorEditorMsg && (
          <div
            className={`sensor-editor-message ${
              sensorEditorMsg
                .toLowerCase()
                .includes("correctamente")
                ? "success"
                : "error"
            }`}
          >
            {sensorEditorMsg}
          </div>
        )}

        <div className="sensor-editor-actions">
          <button
            type="button"
            className="sensor-editor-btn secondary"
            onClick={closeSensorEditor}
            disabled={sensorEditorSaving}
          >
            Cancelar
          </button>

          <button
            type="submit"
            className="sensor-editor-btn primary"
            disabled={sensorEditorSaving}
          >
            {sensorEditorSaving
              ? "Guardando..."
              : "Guardar cambios"}
          </button>
        </div>
      </form>
    </aside>
  </div>
)}

         {canClaimSensors && claimOpen && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Agregar sensor"
          onClick={() => setClaimOpen(false)}
        >
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">Agregar sensor</div>
              <button
                type="button"
                className="btn-solid btn-ghost"
                onClick={() => setClaimOpen(false)}
              >
                Cerrar
              </button>
            </div>

            <div className="modal-body">
              <div className="modal-section">
                <div className="section-title">Vincular sensor a tu cuenta</div>

                <div className="row" style={{ marginBottom: 12 }}>
                  <input
                    className="input"
                    type="text"
                    placeholder="ID del sensor"
                    value={claimDeviceId}
                    onChange={(e) => setClaimDeviceId(e.target.value)}
                  />
                </div>

                <div className="row" style={{ marginBottom: 12 }}>
                  <input
                    className="input"
                    type="text"
                    placeholder="Código de vinculación"
                    value={claimPairingCode}
                    onChange={(e) => setClaimPairingCode(e.target.value)}
                  />
                </div>

                <div className="row">
                  <button
                    type="button"
                    className="btn-solid"
                    style={{ background: "#16a34a" }}
                    onClick={handleClaimSensor}
                    disabled={claimLoading}
                  >
                    {claimLoading ? "Vinculando..." : "Guardar sensor"}
                  </button>
                </div>

                {claimMsg ? (
                  <div className="msg" style={{ marginTop: 12 }}>
                    {claimMsg}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}

 {/* ===== MI CUENTA ===== */}
        {accountModalOpen && (
          <div
            className="modal-overlay account-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="account-modal-title"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) {
                closeAccountModal();
              }
            }}
          >
            <div
              className="account-modal-card"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="account-modal-head">
                <div>
                  <div className="account-modal-eyebrow">MI CUENTA</div>
                  <h2 id="account-modal-title">Perfil de usuario</h2>
                  <p>Información de la cuenta con la que estás trabajando.</p>
                </div>

                <button
                  type="button"
                  className="account-modal-close"
                  onClick={closeAccountModal}
                  aria-label="Cerrar"
                >
                  ✕
                </button>
              </div>

              <div className="account-modal-profile">
                <div className="account-modal-avatar" aria-hidden="true">
                  {String(username || "U").trim().charAt(0).toUpperCase() || "U"}
                </div>

                <div className="account-modal-profile-copy">
                  <strong>{username}</strong>
                  <span>{accountRoleLabel}</span>
                </div>
              </div>

              <div className="account-modal-details">
                <div className="account-modal-detail">
                  <span>Usuario</span>
                  <strong>{user?.username || user?.name || "No disponible"}</strong>
                </div>

                <div className="account-modal-detail">
                  <span>Rol</span>
                  <strong>{accountRoleLabel}</strong>
                </div>

                <div className="account-modal-detail">
                  <span>Equipo</span>
                  <strong>
                    {teamLogoInfo?.team_name ||
                      teamLogoInfo?.teamName ||
                      user?.team_name ||
                      user?.teamName ||
                      user?.team?.name ||
                      (user?.team_id || user?.teamId
                        ? `Equipo #${user?.team_id || user?.teamId}`
                        : "No disponible")}
                  </strong>
                </div>
              </div>

              <div className="account-modal-actions">
                <button
                  type="button"
                  className="account-modal-btn secondary"
                  onClick={closeAccountModal}
                >
                  Cerrar
                </button>

                <button
                  type="button"
                  className="account-modal-btn primary"
                  onClick={() => {
                    setAccountModalOpen(false);
                    handleOpenPasswordModal();
                  }}
                >
                  Cambiar contraseña
                </button>
              </div>
            </div>
          </div>
        )}

 {/* ===== MODAL CAMBIAR CONTRASEÑA ===== */}
        {passwordModalOpen && (
          <div
            className="modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="change-password-title"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) {
                handleClosePasswordModal();
              }
            }}
          >
            <div
              className="modal-card"
              style={{ width: "min(480px, 96vw)" }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="modal-head">
                <div
                  id="change-password-title"
                  className="modal-title"
                >
                  Cambiar contraseña
                </div>

                <button
                  type="button"
                  className="btn-solid btn-ghost"
                  onClick={handleClosePasswordModal}
                  disabled={passwordLoading}
                  style={{
                    padding: "7px 11px",
                    borderRadius: 10,
                  }}
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleChangePassword}>
                <div className="modal-body">
                  <div className="modal-section">
                    <div
                      className="small"
                      style={{ marginBottom: 14 }}
                    >
                      Ingresa tu contraseña actual y luego establece
                      una nueva contraseña.
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gap: 14,
                      }}
                    >
                      <label
                        style={{
                          display: "grid",
                          gap: 6,
                          fontWeight: 800,
                          color: "#0f172a",
                        }}
                      >
                        Contraseña actual

                        <input
                          type="password"
                          className="input"
                          value={currentPassword}
                          onChange={(e) =>
                            setCurrentPassword(e.target.value)
                          }
                          autoComplete="current-password"
                          disabled={passwordLoading}
                          required
                          autoFocus
                        />
                      </label>

                      <label
                        style={{
                          display: "grid",
                          gap: 6,
                          fontWeight: 800,
                          color: "#0f172a",
                        }}
                      >
                        Nueva contraseña

                        <input
                          type="password"
                          className="input"
                          value={newPassword}
                          onChange={(e) =>
                            setNewPassword(e.target.value)
                          }
                          autoComplete="new-password"
                          minLength={8}
                          disabled={passwordLoading}
                          required
                        />
                      </label>

                      <label
                        style={{
                          display: "grid",
                          gap: 6,
                          fontWeight: 800,
                          color: "#0f172a",
                        }}
                      >
                        Confirmar nueva contraseña

                        <input
                          type="password"
                          className="input"
                          value={confirmPassword}
                          onChange={(e) =>
                            setConfirmPassword(e.target.value)
                          }
                          autoComplete="new-password"
                          minLength={8}
                          disabled={passwordLoading}
                          required
                        />
                      </label>
                    </div>

                    <div
                      className="small"
                      style={{ marginTop: 12 }}
                    >
                      La contraseña debe contener al menos 8
                      caracteres.
                    </div>

                    {passwordMsg && (
                      <div
                        className="msg"
                        style={{
                          marginTop: 14,
                          padding: "10px 12px",
                          borderRadius: 10,
                          background: passwordMsg
                            .toLowerCase()
                            .includes("correctamente")
                            ? "#dcfce7"
                            : "#fee2e2",
                          color: passwordMsg
                            .toLowerCase()
                            .includes("correctamente")
                            ? "#166534"
                            : "#991b1b",
                        }}
                      >
                        {passwordMsg}
                      </div>
                    )}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 10,
                      flexWrap: "wrap",
                    }}
                  >
                    <button
                      type="button"
                      className="btn-solid btn-ghost"
                      onClick={handleClosePasswordModal}
                      disabled={passwordLoading}
                    >
                      Cancelar
                    </button>

                    <button
                      type="submit"
                      className="btn-solid btn-download"
                      disabled={passwordLoading}
                    >
                      {passwordLoading
                        ? "Guardando..."
                        : "Actualizar contraseña"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}
   
 </div>
  );
}











